import React from "react"
import {
  TComposedTableComposedValue,
  BreakdownRecord,
} from "../components/ui/charts/ComposedTable/types"
import { TMetric } from "./table-visualization-types"
import { CaptionPreview } from "~/ui-rtk/components/ui/common/CaptionPreview/CaptionPreview"
import { Image } from "../components/ui/common/Image"
import MarathonImage from "../components/atoms/MarathonImage/MarathonImage"
import { mapMediaChannelToSourceType } from "../components/atoms/MarathonImage/helpers"
import { formatNumber } from "../utils/format"
import { safeDivide } from "../utils/number-utils"
import { CONNECTORS_IDS, SOURCE_IDS } from "./sources"
import { highlightRoasCell } from "../utils/table"
import {
  CHANNEL_GROUP_COLOR,
  CHANNEL_GROUP_LABEL,
} from "./brand-value-overview"
import { getLightColor, stringToColor } from "../utils/chart-utils"
import { cn } from "../utils/tailwind-utils"
import { formatValue } from "../components/ui/charts/TableVisualization/utils"
import { DEP_VAR_SOURCES } from "./revenue"

export enum AggregationType {
  DAILY = "DAILY",
  WEEKLY = "WEEKLY",
  MONTHLY = "MONTHLY",
}

export const CHARTS_LOADING_DATA = [
  { date: 1672531200001, value: 2400 },
  { date: 1672531200002, value: 1398 },
  { date: 1672531200003, value: 9800 },
  { date: 1672531200004, value: 3908 },
  { date: 1672531200005, value: 4800 },
  { date: 1672531200006, value: 3800 },
  { date: 1672531200007, value: 4300 },
  { date: 1672531200008, value: 2400 },
  { date: 1672531200010, value: 5356 },
  { date: 1672531200009, value: 2398 },
]

export const AGG_TYPES = [
  { type: AggregationType.DAILY, short: "D", long: "Daily" },
  { type: AggregationType.WEEKLY, short: "W", long: "Weekly" },
  { type: AggregationType.MONTHLY, short: "M", long: "Monthly" },
]

export const TOTAL_CALCULATED_DATA_KEY = "__total__"
export const UNKNOWN_DATA_KEY = "__unknown__"
export const TOTAL_COLOR = "yellow"

export enum VISUALIZATION_TYPE {
  TableVisualization = "TableVisualization",
  AreaChart = "AreaChart",
  BarChart = "BarChart",
  ComposedChart = "ComposedChart",
  DropDownChart = "DropDownChart",
  CohortVisualization = "CohortVisualization",
  MultiChart = "MultiChart",
  ComposedTable = "ComposedTable",
  SortedItems = "SortedItems",
  SimpleTable = "SimpleTable",
}

export type TLabelConfig = {
  label?: string
  numberType?: "default" | "currency"
  unit?: string
  pluralizeUnit?: boolean
  dataKey: string
  isBrand?: boolean
}

// Revenue KPI Metrics Definitions
export interface MetricDefinition {
  key: string
  label: string
  type: "currency" | "number" | "decimal" | "percent"
  suffix?: string
}

// Generate revenue breakdown metrics based on dep_var
/**
 * @deprecated Duplicated in `libs/shared/kpi/dynamic-groups.ts`. The shared
 * lib version takes a `ResolvedKpiPrefs` object instead of loose args. Both
 * copies must stay in sync until the UI is refactored to import from
 * `~/shared/kpi` and this is deleted.
 */
export function getRevenueBreakdownMetrics(
  depVar: string,
  amazonEnabled: boolean,
): MetricDefinition[] {
  const metrics: MetricDefinition[] = []

  // Always add baseline revenue first
  if (depVar?.includes("ga_") || depVar?.includes("google")) {
    metrics.push({
      key: "metrics.ga_baseline_revenue",
      label: "Baseline Revenue",
      type: "currency",
    })

    metrics.push({
      key: "metrics.ga_revenue_baseline_plus_paid_brand_search",
      label: "Baseline + Paid Brand Search Revenue",
      type: "currency",
    })
    // Add Baseline Plus Paid Brand Search
    if (amazonEnabled) {
      metrics.push({
        key: "metrics.ga_amazon_baseline_revenue_plus_paid_brand_search",
        label: "Baseline + Paid Brand Search Revenue + Amazon Owned Revenue",
        type: "currency",
      })
    }
  } else if (depVar?.includes("shopify")) {
    metrics.push({
      key: "metrics.shopify_baseline_revenue",
      label: "Baseline Revenue",
      type: "currency",
    })

    // Add Baseline Plus Paid Brand Search
    metrics.push({
      key: "metrics.shopify_baseline_plus_paid_brand_search_revenue",
      label: "Baseline + Paid Brand Search Revenue",
      type: "currency",
    })

    // Add subscription variant if applicable
    if (depVar?.includes("subscription")) {
      metrics.push({
        key: "metrics.shopify_baseline_plus_subscription_plus_paid_brand_search_revenue",
        label: "Baseline + Subscription + Paid Brand Search Revenue",
        type: "currency",
      })
    }

    // Add Amazon revenue if enabled
    if (amazonEnabled) {
      if (depVar?.includes("subscription")) {
        metrics.push({
          key: "metrics.shopify_plus_subscription_amazon_baseline_revenue",
          label:
            "Baseline + Subscription + Paid Brand Search + Amazon Owned Revenue",
          type: "currency",
        })
      } else {
        metrics.push({
          key: "metrics.shopify_amazon_baseline_revenue",
          label: "Baseline + Paid Brand Search + Amazon Owned Revenue",
          type: "currency",
        })
      }
    }
  }

  return metrics
}

// The 3rd Party Retail rollup (`wholesale_total_revenue`) is a term of Total
// Business Revenue since AMA-138, so the table shows it as its own row for
// companies with a connected retailer feed (the same gate as the `wholesale`
// table).
const THIRD_PARTY_RETAIL_REVENUE: MetricDefinition = {
  key: "metrics.wholesale_total_revenue",
  label: "3rd Party Retail Revenue",
  type: "currency",
}

// Generate total business revenue metrics based on dep_var
// Note: Total Business Revenue always uses Shopify unless Shopify is not connected
/**
 * @deprecated Duplicated in `libs/shared/kpi/dynamic-groups.ts`. Keep both
 * copies aligned until the UI imports from `~/shared/kpi`.
 */
export function getTotalBusinessRevenueMetrics(
  amazonEnabled: boolean,
  isShopifyConnected = true,
  wholesaleEnabled = false,
): MetricDefinition[] {
  const metrics: MetricDefinition[] = []

  // Add Amazon total revenue only if enabled
  if (amazonEnabled) {
    metrics.push({
      key: "metrics.amazon_total_revenue",
      label: "Amazon Total Revenue",
      type: "currency",
    })
  }

  // Always prefer Shopify for Total Business Revenue if connected
  // Only fall back to GA if Shopify is not connected
  if (isShopifyConnected) {
    // Use Shopify data for Total Business Revenue
    metrics.push({
      key: "metrics.shopify_total_customer_revenue",
      label: "Shopify Customer Revenue Total",
      type: "currency",
    })
    if (wholesaleEnabled) {
      metrics.push(THIRD_PARTY_RETAIL_REVENUE)
    }
    metrics.push({
      key: "metrics.total_business_revenue_shopify",
      label: "Total Business Revenue",
      type: "currency",
    })
  } else {
    // Fallback to GA multi-property metrics if Shopify is not connected
    metrics.push({
      key: "metrics.ga_multi_property_revenue",
      label: "GA Total Revenue",
      type: "currency",
    })
    if (wholesaleEnabled) {
      metrics.push(THIRD_PARTY_RETAIL_REVENUE)
    }
    metrics.push({
      key: "metrics.total_business_revenue_ga",
      label: "Total Business Revenue",
      type: "currency",
    })
  }

  return metrics
}

// Generate GA revenue metrics by traffic source - always use multi-property metrics
/**
 * @deprecated Duplicated in `libs/shared/kpi/dynamic-groups.ts` as
 * `getGaRevenueMetrics`. Keep aligned until the UI imports from
 * `~/shared/kpi`.
 */
export function getGARevenueMetrics(): MetricDefinition[] {
  // Always use multi-property metrics
  return [
    {
      key: "metrics.ga_multi_property_baseline_revenue",
      label: "GA Baseline Revenue",
      type: "currency",
    },
    {
      key: "metrics.ga_multi_property_triggered_revenue",
      label: "GA Triggered Revenue",
      type: "currency",
    },
    {
      key: "metrics.ga_multi_property_paid_revenue",
      label: "GA Paid Revenue",
      type: "currency",
    },
    {
      key: "metrics.ga_multi_property_revenue",
      label: "Total",
      type: "currency",
    },
  ]
}

export interface VisualizationConfig {
  id: string
  name?: string
  description?: string | null
  type: VISUALIZATION_TYPE | string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  props?: Record<string, any>
  metadata?: Record<string, unknown> | null
  requiredSources?: string[]
  renderInfo?: () => React.ReactNode
}

// The AI mentions pipeline (Bowerman `mentions`, dbt ai__mentions) analyzes
// Instagram and TikTok mentions, so those are the sources these widgets need.
const AI_MENTIONS_SOURCES = [SOURCE_IDS.INSTAGRAM, SOURCE_IDS.TIKTOK]

export const VISUALIZATIONS_MAP: Record<string, VisualizationConfig> = {
  // In-use
  google_keywords_share_of_search_total_composed_chart: {
    id: "google_keywords_share_of_search_total_composed_chart",
    description:
      "This chart shows your brand's share of search across ALL of your category terms as compared to your selected competitors, which are editable in your settings page.",
    type: "ComposedChart",
    props: {
      dataAggTypeOnly: "MONTHLY",
      interactive: false,
      highlightMetric: "__CURRENT_COMPANY_NAME__",
      highlightMetricPrefix: "company_mind_share_metrics_monthly.brand",
      highlightMetricSuffix: "share_of_search",
      stacked: true,
      comparisonEnabled: true,
      graphsFromColumn: {
        type: "area",
        unit: "%",
        label: "{stackKey}",
        suffixKey: "share_of_search",
        column: "company_mind_share_metrics_monthly.brand",
        labelAggType: "percent-average",
        pluralizeUnit: false,
      },
      manualTotal: true,
      totalLabelProps: {
        label: "Total searches",
      },
      tooltipProps: {
        renderCell: (
          value: number,
          dataKey: string,
          payload: Record<string, number>,
          _labelConfig: TLabelConfig,
          _ignoreDecimals = false,
          prefix = "",
        ) => {
          const renderedValuePercentage = formatNumber(Number(value), {
            currency: false,
            decimals: 2,
          })
          const searchesKey = dataKey.replace(".share_of_search", ".searches")
          const searchesValue = formatNumber(
            Number(payload[`${prefix}${searchesKey}`]),
            {
              currency: false,
              compact: true,
            },
          )
          return (
            <>
              <div>{renderedValuePercentage}%&nbsp;</div>
              <div className="text-white/50">{searchesValue} searches</div>
            </>
          )
        },
      },
      blurTitle: true,
      emptyStateText: "Google returned no search volume for these queries.",
    },
    metadata: null,
    requiredSources: [SOURCE_IDS.GOOGLE_KEYWORDS],
  },
  google_keywords_share_of_search_brands_share_composed_chart: {
    id: "google_keywords_share_of_search_brands_share_composed_chart",
    description:
      "This chart shows the number of searches on your exact brand term (e.g. your company name) vs. those of your competitors, with no category modifiers appended.",
    type: "ComposedChart",
    props: {
      dataAggTypeOnly: "MONTHLY",
      interactive: false,
      highlightMetric: "__CURRENT_COMPANY_NAME__",
      highlightMetricPrefix: "company_mind_share_metrics_monthly.brand",
      highlightMetricSuffix: "share_of_search",
      stacked: true,
      comparisonEnabled: true,
      graphsFromColumn: {
        type: "area",
        unit: "%",
        label: "{stackKey}",
        suffixKey: "share_of_search",
        column: "company_mind_share_metrics_monthly.brand",
        labelAggType: "percent-average",
        pluralizeUnit: false,
      },
      manualTotal: true,
      totalLabelProps: {
        label: "Total searches",
      },
      tooltipProps: {
        renderCell: (
          value: number,
          dataKey: string,
          payload: Record<string, number>,
          _labelConfig: TLabelConfig,
          _ignoreDecimals = false,
          prefix = "",
        ) => {
          const renderedValuePercentage = formatNumber(Number(value), {
            currency: false,
            decimals: 2,
          })
          const searchesKey = dataKey.replace(".share_of_search", ".searches")
          const searchesValue = formatNumber(
            Number(payload[`${prefix}${searchesKey}`]),
            {
              currency: false,
              compact: true,
            },
          )
          return (
            <>
              <div>{renderedValuePercentage}%&nbsp;</div>
              <div className="text-white/50">{searchesValue} searches</div>
            </>
          )
        },
      },
      emptyStateText: "Google returned no search volume for these queries.",
      blurTitle: true,
    },
    metadata: null,
    requiredSources: [SOURCE_IDS.GOOGLE_KEYWORDS],
  },
  google_keywords_share_of_search_by_category_multi_chart: {
    id: "google_keywords_share_of_search_by_category_multi_chart",
    name: "Share Of Search By Category Multi Chart",
    description:
      "This chart shows your brand's share of search for the given category search term for any given time period. Note this data is only available on a monthly basis.",
    type: "MultiChart",
    props: {
      unit: "%",
      highlightMetric: "__CURRENT_COMPANY_NAME__",
      highlightMetricSuffix: "share_of_search",
      groupKey: "category",
      stacked: true,
      dataAggTypeOnly: "MONTHLY",
      chartColSpan: 4,
      chartRowSpan: 6,
      labelAggType: "percent-total",
      groupStackKey: "brand",
      pluralizeUnit: false,
      groupStackLabelFormat: "{stackKey}",
      manualTotal: true,
      comparisonEnabled: true,
      totalLabelProps: {
        label: "Total searches",
      },
      tooltipProps: {
        renderCell: (
          value: number,
          dataKey: string,
          payload: Record<string, number>,
          _labelConfig: TLabelConfig,
          _ignoreDecimals = false,
          prefix = "",
        ) => {
          const renderedValuePercentage = formatNumber(Number(value), {
            currency: false,
            decimals: 2,
          })
          const searchesKey = dataKey.replace(".share_of_search", ".searches")
          const searchesValue = formatNumber(
            Number(payload[`${prefix}${searchesKey}`]),
            {
              currency: false,
              compact: true,
            },
          )
          return (
            <>
              <div>{renderedValuePercentage}%&nbsp;</div>
              <div className="text-white/50">{searchesValue} searches</div>
            </>
          )
        },
      },
      emptyStateText: "Google returned no search volume for these queries.",
      blurTitle: true,
    },
    metadata: { autoSize: true, noResize: true, defaultColSpan: 12 },
    requiredSources: [SOURCE_IDS.GOOGLE_KEYWORDS],
  },
  instagram_share_of_comments_area_chart: {
    id: "instagram_share_of_comments_area_chart",
    name: "Instagram Share of Comments Chart",
    description:
      "This metric represent the total number of comments across all of your Instagram posts on any given day.",
    type: "AreaChart",
    props: {
      unit: "%",
      title: "IG New Comments",
      dataAggTypeOnly: "DAILY",
      labelAggType: "avg",
    },
    metadata: null,
    requiredSources: [SOURCE_IDS.INSTAGRAM],
  },
  instagram_share_of_likes_area_chart: {
    id: "instagram_share_of_likes_area_chart",
    name: "Instagram Share of Likes Chart",
    description:
      "This metric represent the total number of likes across all of your Instagram posts on any given day.",
    type: "AreaChart",
    props: {
      unit: "%",
      title: "IG New Likes",
      dataAggTypeOnly: "DAILY",
      labelAggType: "avg",
    },
    metadata: null,
    requiredSources: [SOURCE_IDS.INSTAGRAM],
  },
  instagram_share_of_followers_area_chart: {
    id: "instagram_share_of_followers_area_chart",
    name: "Instagram Share of Followers Chart",
    description:
      "This metric represent the total number of likes across all of your Instagram posts on any given day.",
    type: "AreaChart",
    props: {
      unit: "%",
      title: "IG New Followers",
      dataAggTypeOnly: "DAILY",
      labelAggType: "avg",
    },
    metadata: null,
    requiredSources: [SOURCE_IDS.INSTAGRAM],
  },
  total_followers_stacked_chart: {
    id: "total_followers_stacked_chart",
    name: "Total New Followers Chart",
    description:
      "The total number of new followers you added across all of your social channels on any given day. Note: the Tiktok follows metric shows net change (new follows - unfollows) as opposed to only new followers.",
    type: "ComposedChart",
    props: {
      title: "Total New Followers",
      totalLabelProps: {
        unit: "follower",
        label: "Total",
        pluralizeUnit: true,
      },
      comparisonEnabled: true,
      graphs: [
        {
          type: "area",
          unit: "follower",
          label: "TikTok",
          dataKey: "metrics.tiktok_profile_follows",
          requiredSources: [SOURCE_IDS.TIKTOK],
          pluralizeUnit: true,
        },
        {
          type: "area",
          unit: "follower",
          label: "TikTok Ads",
          dataKey: "metrics.tiktok_ads_follows",
          requiredSources: [SOURCE_IDS.TIKTOK_ADS],
          pluralizeUnit: true,
        },
        {
          type: "area",
          unit: "follower",
          label: "Facebook",
          dataKey: "metrics.facebook_pages_follows",
          requiredSources: [SOURCE_IDS.FACEBOOK],
          pluralizeUnit: true,
        },
        {
          type: "area",
          unit: "follower",
          label: "YouTube",
          dataKey: "metrics.youtube_channel_follows",
          requiredSources: [SOURCE_IDS.YOUTUBE],
          pluralizeUnit: true,
        },
        {
          type: "area",
          unit: "follower",
          label: "Facebook Ads",
          dataKey: "metrics.facebook_ads_follows",
          requiredSources: [SOURCE_IDS.FACEBOOK_ADS],
          pluralizeUnit: true,
        },
        {
          type: "area",
          unit: "follower",
          label: "Total",
          isTotal: true,
          color: TOTAL_COLOR,
          dataKey: TOTAL_CALCULATED_DATA_KEY,
          requiredSources: [
            SOURCE_IDS.INSTAGRAM,
            SOURCE_IDS.TIKTOK,
            SOURCE_IDS.TIKTOK_ADS,
            SOURCE_IDS.FACEBOOK,
            SOURCE_IDS.FACEBOOK_ADS,
            SOURCE_IDS.YOUTUBE,
          ],
          pluralizeUnit: true,
        },
      ],
      compareUnit: "follow",
    },
    metadata: null,
    requiredSources: [
      SOURCE_IDS.INSTAGRAM,
      SOURCE_IDS.INSTAGRAM_ADS,
      SOURCE_IDS.TIKTOK,
      SOURCE_IDS.TIKTOK_ADS,
      SOURCE_IDS.FACEBOOK,
      SOURCE_IDS.FACEBOOK_ADS,
      SOURCE_IDS.YOUTUBE,
    ],
  },
  total_new_subscribers_stacked_chart: {
    id: "total_new_subscribers_stacked_chart",
    name: "Total Subscribers Chart",
    description:
      "This metric represents the number of new subscribers you earned across email and SMS on any given date",
    type: "ComposedChart",
    props: {
      title: "Total New Subscribers",
      totalLabelProps: {
        unit: "subscriber",
        label: "Total",
        pluralizeUnit: true,
      },
      comparisonEnabled: true,
      graphs: [
        {
          type: "area",
          label: "Klaviyo email subscribers",
          dataKey: "metrics.klaviyo_email_subscribers",
          requiredSources: [SOURCE_IDS.KLAVIYO],
        },
        {
          type: "area",
          label: "Attentive SMS subscribers",
          dataKey: "metrics.attentive_sms_subscribers",
          requiredSources: [SOURCE_IDS.ATTENTIVE],
        },
        {
          type: "area",
          label: "Total New subscribers",
          isTotal: true,
          color: TOTAL_COLOR,
          dataKey: TOTAL_CALCULATED_DATA_KEY,
          requiredSources: [SOURCE_IDS.ATTENTIVE, SOURCE_IDS.KLAVIYO],
        },
      ],
      compareLabelAggType: "sum",
    },
    metadata: null,
    requiredSources: [SOURCE_IDS.KLAVIYO, SOURCE_IDS.ATTENTIVE],
  },
  total_shares_stacked_chart: {
    id: "total_shares_stacked_chart",
    name: "Total Shares Chart",
    description:
      "This metric represents the total number of shares on your content across all of your social channels for any given date.",
    type: "ComposedChart",
    props: {
      title: "Total Shares",
      totalLabelProps: {
        unit: "share",
        label: "Total",
        pluralizeUnit: true,
      },
      comparisonEnabled: true,
      graphs: [
        {
          type: "area",
          label: "Instagram",
          dataKey: "metrics.instagram_business_shares",
          requiredSources: [SOURCE_IDS.INSTAGRAM],
        },
        {
          type: "area",
          label: "TikTok",
          dataKey: "metrics.tiktok_videos_shares",
          requiredSources: [SOURCE_IDS.TIKTOK],
        },
        {
          type: "area",
          label: "TikTok Shares from Ads",
          dataKey: "metrics.tiktok_ads_shares",
          requiredSources: [SOURCE_IDS.TIKTOK_ADS],
        },
        {
          type: "area",
          label: "Facebook Ads",
          dataKey: "metrics.facebook_ads_shares",
          requiredSources: [SOURCE_IDS.FACEBOOK_ADS],
        },
        {
          type: "area",
          label: "Instagram Ads",
          dataKey: "metrics.instagram_ads_shares",
          requiredSources: [SOURCE_IDS.INSTAGRAM_ADS],
        },
        {
          type: "area",
          label: "Facebook",
          dataKey: "metrics.facebook_pages_shares",
          requiredSources: [SOURCE_IDS.FACEBOOK],
        },
        {
          type: "area",
          label: "YouTube",
          dataKey: "metrics.youtube_channel_shares",
          requiredSources: [SOURCE_IDS.YOUTUBE],
        },
        {
          type: "area",
          label: "Total",
          isTotal: true,
          color: TOTAL_COLOR,
          dataKey: TOTAL_CALCULATED_DATA_KEY,
          requiredSources: [
            SOURCE_IDS.INSTAGRAM,
            SOURCE_IDS.INSTAGRAM_ADS,
            SOURCE_IDS.FACEBOOK,
            SOURCE_IDS.FACEBOOK_ADS,
            SOURCE_IDS.TIKTOK,
            SOURCE_IDS.TIKTOK_ADS,
            SOURCE_IDS.YOUTUBE,
          ],
        },
      ],
      compareUnit: "share",
    },
    metadata: null,
    requiredSources: [
      SOURCE_IDS.INSTAGRAM,
      SOURCE_IDS.INSTAGRAM_ADS,
      SOURCE_IDS.FACEBOOK,
      SOURCE_IDS.FACEBOOK_ADS,
      SOURCE_IDS.TIKTOK,
      SOURCE_IDS.TIKTOK_ADS,
      SOURCE_IDS.YOUTUBE,
    ],
  },
  total_comments_stacked_chart: {
    id: "total_comments_stacked_chart",
    name: "Total Comments Chart",
    description:
      "The total number of comments on your brand's social content across all channels on any given day.",
    type: "ComposedChart",
    props: {
      title: "Total Comments",
      totalLabelProps: {
        unit: "comment",
        label: "Total",
        pluralizeUnit: true,
      },
      comparisonEnabled: true,
      graphs: [
        {
          type: "area",
          label: "Instagram",
          dataKey: "metrics.instagram_business_comments",
          requiredSources: [SOURCE_IDS.INSTAGRAM],
        },
        {
          type: "area",
          label: "Instagram Ads",
          dataKey: "metrics.instagram_ads_comments",
          requiredSources: [SOURCE_IDS.INSTAGRAM_ADS],
        },
        {
          type: "area",
          label: "TikTok",
          dataKey: "metrics.tiktok_videos_comments",
          requiredSources: [SOURCE_IDS.TIKTOK],
        },
        {
          type: "area",
          label: "TikTok Ads",
          dataKey: "metrics.tiktok_ads_comments",
          requiredSources: [SOURCE_IDS.TIKTOK_ADS],
        },
        {
          type: "area",
          label: "Facebook",
          dataKey: "metrics.facebook_pages_comments",
          requiredSources: [SOURCE_IDS.FACEBOOK],
        },
        {
          type: "area",
          label: "Facebook Ads",
          dataKey: "metrics.facebook_ads_comments",
          requiredSources: [SOURCE_IDS.FACEBOOK_ADS],
        },
        {
          type: "area",
          label: "YouTube",
          dataKey: "metrics.youtube_channel_comments",
          requiredSources: [SOURCE_IDS.YOUTUBE],
        },
        {
          type: "area",
          label: "Total",
          isTotal: true,
          color: TOTAL_COLOR,
          dataKey: TOTAL_CALCULATED_DATA_KEY,
          requiredSources: [
            SOURCE_IDS.FACEBOOK_ADS,
            SOURCE_IDS.INSTAGRAM,
            SOURCE_IDS.INSTAGRAM_ADS,
            SOURCE_IDS.FACEBOOK,
            SOURCE_IDS.TIKTOK,
            SOURCE_IDS.TIKTOK_ADS,
            SOURCE_IDS.YOUTUBE,
          ],
        },
      ],
      compareUnit: "comment",
    },
    metadata: null,
    requiredSources: [
      SOURCE_IDS.INSTAGRAM,
      SOURCE_IDS.FACEBOOK,
      SOURCE_IDS.FACEBOOK_ADS,
      SOURCE_IDS.TIKTOK,
      SOURCE_IDS.TIKTOK_ADS,
      SOURCE_IDS.YOUTUBE,
    ],
  },
  total_likes_stacked_chart: {
    id: "total_likes_stacked_chart",
    name: "Total Likes Chart",
    description:
      "The total number of likes on your brand's social content across all channels on any given day.",
    type: "ComposedChart",
    props: {
      title: "Total Likes",
      totalLabelProps: {
        unit: "like",
        label: "Total",
        pluralizeUnit: true,
      },
      comparisonEnabled: true,
      graphs: [
        {
          type: "area",
          label: "Instagram",
          dataKey: "metrics.instagram_business_likes",
          requiredSources: [SOURCE_IDS.INSTAGRAM],
        },
        {
          type: "area",
          label: "Instagram Ads",
          dataKey: "metrics.instagram_ads_likes",
          requiredSources: [SOURCE_IDS.INSTAGRAM_ADS],
        },
        {
          type: "area",
          label: "TikTok",
          dataKey: "metrics.tiktok_videos_likes",
          requiredSources: [SOURCE_IDS.TIKTOK],
        },
        {
          type: "area",
          label: "TikTok Ads",
          dataKey: "metrics.tiktok_ads_likes",
          requiredSources: [SOURCE_IDS.TIKTOK_ADS],
        },
        {
          type: "area",
          label: "Facebook",
          dataKey: "metrics.facebook_pages_likes",
          requiredSources: [SOURCE_IDS.FACEBOOK],
        },
        {
          type: "area",
          label: "Facebook Ads",
          dataKey: "metrics.facebook_ads_likes",
          requiredSources: [SOURCE_IDS.FACEBOOK_ADS],
        },
        {
          type: "area",
          label: "YouTube",
          dataKey: "metrics.youtube_channel_likes",
          requiredSources: [SOURCE_IDS.YOUTUBE],
        },
        {
          type: "area",
          label: "Total",
          isTotal: true,
          color: TOTAL_COLOR,
          dataKey: TOTAL_CALCULATED_DATA_KEY,
          requiredSources: [
            SOURCE_IDS.INSTAGRAM,
            SOURCE_IDS.INSTAGRAM_ADS,
            SOURCE_IDS.FACEBOOK,
            SOURCE_IDS.FACEBOOK_ADS,
            SOURCE_IDS.TIKTOK,
            SOURCE_IDS.TIKTOK_ADS,
            SOURCE_IDS.YOUTUBE,
          ],
        },
      ],
      compareUnit: "like",
    },
    metadata: null,
    requiredSources: [
      SOURCE_IDS.INSTAGRAM,
      SOURCE_IDS.FACEBOOK,
      SOURCE_IDS.FACEBOOK_ADS,
      SOURCE_IDS.TIKTOK,
      SOURCE_IDS.TIKTOK_ADS,
      SOURCE_IDS.YOUTUBE,
    ],
  },
  total_saves_stacked_chart: {
    id: "total_saves_stacked_chart",
    name: "Total Saves Chart",
    description: null,
    type: "ComposedChart",
    props: {
      title: "Total Saves",
      comparisonEnabled: true,
      graphs: [
        {
          type: "area",
          label: "Instagram Saves",
          dataKey: "metrics.instagram_business_saves",
          requiredSources: [SOURCE_IDS.INSTAGRAM],
        },
        {
          type: "area",
          label: "Instagram Saves from Ads",
          dataKey: "metrics.instagram_ads_saves",
          requiredSources: [SOURCE_IDS.INSTAGRAM_ADS],
        },
        {
          type: "area",
          label: "TikTok Saves from Ads",
          dataKey: "metrics.tiktok_ads_saves",
          requiredSources: [SOURCE_IDS.TIKTOK_ADS],
        },
        {
          type: "area",
          label: "Pinterest Saves from Ads",
          dataKey: "metrics.pinterest_ads_saves",
          requiredSources: [SOURCE_IDS.PINTEREST_ADS],
        },
        {
          type: "area",
          label: "Total Saves",
          isTotal: true,
          color: TOTAL_COLOR,
          dataKey: TOTAL_CALCULATED_DATA_KEY,
          requiredSources: [
            SOURCE_IDS.INSTAGRAM,
            SOURCE_IDS.INSTAGRAM_ADS,
            SOURCE_IDS.TIKTOK_ADS,
            SOURCE_IDS.PINTEREST_ADS,
          ],
        },
      ],
      compareUnit: "save",
    },
    metadata: null,
    requiredSources: [
      SOURCE_IDS.INSTAGRAM,
      SOURCE_IDS.TIKTOK_ADS,
      SOURCE_IDS.PINTEREST_ADS,
    ],
  },
  total_social_profile_views_stacked_chart: {
    id: "total_social_profile_views_stacked_chart",
    name: "Total Social Profile Views Chart",
    description:
      "The total number of visits to your brand's social profile pages across all social platforms on any given day.",
    type: "ComposedChart",
    props: {
      title: "Total Social Profile Views",
      totalLabelProps: {
        unit: "view",
        label: "Total",
        pluralizeUnit: true,
      },
      comparisonEnabled: true,
      graphs: [
        {
          type: "area",
          label: "Instagram",
          dataKey: "metrics.instagram_business_profile_views",
          requiredSources: [SOURCE_IDS.INSTAGRAM],
        },
        {
          type: "area",
          label: "TikTok",
          dataKey: "metrics.tiktok_profile_views",
          requiredSources: [SOURCE_IDS.TIKTOK],
        },
        {
          type: "area",
          label: "Facebook",
          dataKey: "metrics.facebook_pages_views",
          requiredSources: [SOURCE_IDS.FACEBOOK],
        },
        {
          type: "area",
          label: "Total",
          isTotal: true,
          color: TOTAL_COLOR,
          dataKey: TOTAL_CALCULATED_DATA_KEY,
          requiredSources: [
            SOURCE_IDS.INSTAGRAM,
            SOURCE_IDS.FACEBOOK,
            SOURCE_IDS.TIKTOK,
          ],
        },
      ],
      compareUnit: "view",
    },
    metadata: null,
    requiredSources: [
      SOURCE_IDS.INSTAGRAM,
      SOURCE_IDS.FACEBOOK,
      SOURCE_IDS.TIKTOK,
    ],
  },
  facebook_mentions_area_chart: {
    id: "facebook_mentions_area_chart",
    name: "Facebook Mentions Chart",
    description:
      "The total number of times your brand's profile was mentioned in another user's post or story on any given day.",
    type: "ComposedChart",
    props: {
      unit: "mention",
      title: "Facebook Mentions",
      comparisonEnabled: true,
      graphs: [
        {
          type: "area",
          label: "Facebook Mentions",
          dataKey: "metrics.facebook_pages_mentions",
          yAxisId: "mentions",
          requiredSources: [SOURCE_IDS.FACEBOOK],
        },
      ],
    },
    metadata: null,
    requiredSources: [SOURCE_IDS.FACEBOOK],
  },
  branded_search_totals_stacked_chart: {
    id: "branded_search_totals_stacked_chart",
    name: "Branded Search Total Chart",
    description:
      "This metric represents the number of organic search impressions and clicks on your brand that were generated on searches containing your branded keywords as compared to the number of paid search impressions on thos same search terms.",
    type: "ComposedChart",
    props: {
      title: "Branded Search Totals",
      totalLabelProps: {
        unit: "impression",
        label: "Total",
        pluralizeUnit: true,
      },
      tooltipProps: {
        numeralFormat: "0.00a",
      },
      comparisonEnabled: true,
      hideTotals: true,
      customTotals: [
        {
          label: "Total Impressions",
          dataKey: "total-impressions",
          dataKeysToSum: [
            "metrics.google_search_organic_branded_search_impressions",
            "metrics.google_ads_paid_branded_search_impressions",
          ],
        },
        {
          label: "Total Clicks",
          dataKey: "total-clicks",
          dataKeysToSum: [
            "metrics.google_search_organic_branded_search_clicks",
            "metrics.google_ads_paid_branded_search_clicks",
          ],
        },
      ],
      graphs: [
        {
          type: "area",
          label: "Organic searches",
          dataKey: "metrics.google_search_organic_branded_search_impressions",
          // yAxisId: "impressions",
          requiredSources: [SOURCE_IDS.GOOGLE_SEARCH],
        },
        {
          type: "area",
          label: "Paid impressions",
          dataKey: "metrics.google_ads_paid_branded_search_impressions",
          // yAxisId: "impressions",
          requiredSources: [SOURCE_IDS.GOOGLE_ADS],
        },
        {
          type: "area",
          label: "Organic clicks",
          dataKey: "metrics.google_search_organic_branded_search_clicks",
          // yAxisId: "clicks",
          requiredSources: [SOURCE_IDS.GOOGLE_SEARCH],
        },
        {
          type: "area",
          label: "Paid clicks",
          dataKey: "metrics.google_ads_paid_branded_search_clicks",
          // yAxisId: "clicks",
          requiredSources: [SOURCE_IDS.GOOGLE_ADS],
        },
        // {
        //   type: "dashed",
        //   unit: "dollar",
        //   label: "Spend",
        //   dataKey: "metrics.google_ads_paid_branded_search_total_spend",
        //   requiredSources: [SOURCE_IDS.GOOGLE_ADS],
        //   numberType: "currency",
        //   pluralizeUnit: true,
        // },
      ],
      interactive: false,
      compareLabelAggType: "sum",
      graphsManualSort: true,
    },
    metadata: null,
    requiredSources: [SOURCE_IDS.GOOGLE_ADS, SOURCE_IDS.GOOGLE_SEARCH],
  },
  ga_revenue_break_down_stacked_chart: {
    id: "ga_revenue_break_down_stacked_chart",
    name: "Revenue Breakdown",
    description:
      "This chart shows a comparison between your total Baseline revenue - resilient revenue from organic channels - vs. Paid revenue from advertising sources.",
    type: "ComposedChart",
    renderInfo: () => (
      <div className="flex flex-col p-system-xxs text-sm">
        <p>
          <span className="text-orange font-bold">Paid</span>: All revenue
          sources not specified in 'Triggered' or 'Baseline', including but not
          limited to Facebook Ads, Google Ads, Tiktok Ads, etc.
        </p>
        <p>
          <span className="text-yellow font-bold">Triggered</span>: Revenue
          attributed to email and SMS marketing campaigns.
        </p>
        <p>
          <span className="text-japanese-laurel font-bold">Baseline</span>:
          Revenue generated from organic search, direct traffic, and organic
          social referrals.
        </p>
      </div>
    ),
    props: {
      title: "Revenue Breakdown",
      comparisonEnabled: true,
      tooltipProps: {
        renderCell: (
          value: number,
          _dataKey: string,
          payload: Record<string, number>,
          _labelConfig: TLabelConfig,
          ignoreDecimals = false,
          prefix = "",
        ) => {
          const total = [
            `${prefix}metrics.ga_paid_revenue`,
            `${prefix}metrics.ga_triggered_revenue`,
            `${prefix}metrics.ga_baseline_revenue`,
          ].reduce((acc: number, key: string) => acc + payload[key], 0)
          const percentValue = 100 * (value / total)
          const renderedValue = formatNumber(Number(value), {
            currency: true,
            decimals: ignoreDecimals ? 0 : 2,
          })
          return (
            <>
              <div>{renderedValue}&nbsp;</div>
              <div className="text-white/50 text-sm">
                {percentValue.toFixed(2)}% of Total
              </div>
            </>
          )
        },
      },
      totalLabelProps: {
        label: "Total Revenue",
        numberType: "currency",
      },
      graphs: [
        {
          type: "area",
          label: "Paid Revenue",
          dataKey: "metrics.ga_paid_revenue",
          numberType: "currency",
          labelAggType: "sum",
          pluralizeUnit: false,
          stacked: true,
          color: "#EE8434",
        },
        {
          type: "area",
          label: "Triggered Revenue",
          dataKey: "metrics.ga_triggered_revenue",
          numberType: "currency",
          labelAggType: "sum",
          pluralizeUnit: false,
          stacked: true,
          color: "#F4D148",
        },
        {
          type: "area",
          label: "Baseline Revenue",
          dataKey: "metrics.ga_baseline_revenue",
          numberType: "currency",
          labelAggType: "sum",
          pluralizeUnit: false,
          stacked: true,
          color: "#318936",
        },
        {
          type: "line",
          label: "Spend",
          dataKey: "metrics.total_ad_spend",
          numberType: "currency",
          color: "#E2323E",
        },
      ],
      filterableDataKey: true,

      graphsManualSort: true,
      interactive: false,
      compareNumberType: "currency",
      compareLabelAggType: "sum",
      hideTotal: false,
    },
    metadata: null,
    requiredSources: [SOURCE_IDS.GOOGLE_ANALYTICS],
  },
  ga_mp_revenue_break_down_stacked_chart: {
    id: "ga_mp_revenue_break_down_stacked_chart",
    name: "Revenue Breakdown",
    description:
      "This chart shows a comparison between your total Baseline revenue - resilient revenue from organic channels - vs. Paid revenue from advertising sources.",
    type: "ComposedChart",
    renderInfo: () => (
      <div className="flex flex-col p-system-xxs text-sm">
        <p>
          <span className="text-orange font-bold">Paid</span>: All revenue
          sources not specified in 'Triggered' or 'Baseline', including but not
          limited to Facebook Ads, Google Ads, Tiktok Ads, etc.
        </p>
        <p>
          <span className="text-yellow font-bold">Triggered</span>: Revenue
          attributed to email and SMS marketing campaigns.
        </p>
        <p>
          <span className="text-japanese-laurel font-bold">Baseline</span>:
          Revenue generated from organic search, direct traffic, and organic
          social referrals.
        </p>
      </div>
    ),
    props: {
      title: "Revenue Breakdown",
      comparisonEnabled: true,
      tooltipProps: {
        renderCell: (
          value: number,
          _dataKey: string,
          payload: Record<string, number>,
          _labelConfig: TLabelConfig,
          ignoreDecimals = false,
          prefix = "",
        ) => {
          const total = [
            `${prefix}metrics.ga_multi_property_paid_revenue`,
            `${prefix}metrics.ga_multi_property_triggered_revenue`,
            `${prefix}metrics.ga_multi_property_baseline_revenue`,
          ].reduce((acc: number, key: string) => acc + payload[key], 0)
          const percentValue = 100 * (value / total)
          const renderedValue = formatNumber(Number(value), {
            currency: true,
            decimals: ignoreDecimals ? 0 : 2,
          })
          return (
            <>
              <div>{renderedValue}&nbsp;</div>
              <div className="text-white/50 text-sm">
                {percentValue.toFixed(2)}% of Total
              </div>
            </>
          )
        },
      },
      totalLabelProps: {
        label: "Total Revenue",
        numberType: "currency",
      },
      graphs: [
        {
          type: "area",
          label: "Paid Revenue",
          dataKey: "metrics.ga_multi_property_paid_revenue",
          numberType: "currency",
          labelAggType: "sum",
          pluralizeUnit: false,
          stacked: true,
          color: "#EE8434",
        },
        {
          type: "area",
          label: "Triggered Revenue",
          dataKey: "metrics.ga_multi_property_triggered_revenue",
          numberType: "currency",
          labelAggType: "sum",
          pluralizeUnit: false,
          stacked: true,
          color: "#F4D148",
        },
        {
          type: "area",
          label: "Baseline Revenue",
          dataKey: "metrics.ga_multi_property_baseline_revenue",
          numberType: "currency",
          labelAggType: "sum",
          pluralizeUnit: false,
          stacked: true,
          color: "#318936",
        },
        {
          type: "line",
          label: "Spend",
          dataKey: "metrics.total_ad_spend",
          numberType: "currency",
          color: "#E2323E",
        },
      ],
      graphsManualSort: true,
      interactive: false,
      compareNumberType: "currency",
      compareLabelAggType: "sum",
      hideTotal: false,
    },
    metadata: null,
    requiredSources: [SOURCE_IDS.GOOGLE_ANALYTICS],
  },
  shopify_revenue_break_down_stacked_chart: {
    id: "shopify_revenue_break_down_stacked_chart",
    name: "Web Revenue Breakdown",
    description:
      "This chart shows a comparison between your total Baseline revenue - resilient revenue from organic channels - vs. Paid revenue from advertising sources.",
    type: "ComposedChart",
    renderInfo: () => (
      <div className="flex flex-col p-system-xxs text-sm">
        <p>
          <span className="text-orange font-bold">Paid</span>: All revenue
          sources not specified in 'Triggered' or 'Baseline', including but not
          limited to Facebook Ads, Google Ads, Tiktok Ads, etc.
        </p>
        <p>
          <span className="text-yellow font-bold">Triggered</span>: Revenue
          attributed to email and SMS marketing campaigns.
        </p>
        <p>
          <span className="text-japanese-laurel font-bold">Baseline</span>:
          Revenue generated from organic search, direct traffic, and organic
          social referrals.
        </p>
      </div>
    ),
    props: {
      title: "Web Revenue Breakdown",
      comparisonEnabled: true,
      tooltipProps: {
        renderCell: (
          value: number,
          _dataKey: string,
          payload: Record<string, number>,
          _labelConfig: TLabelConfig,
          ignoreDecimals = false,
          prefix = "",
          excludeFromTotal: string[] = [],
        ) => {
          const total = [
            `${prefix}metrics.shopify_paid_revenue`,
            `${prefix}metrics.shopify_triggered_revenue`,
            `${prefix}metrics.shopify_baseline_revenue`,
            `${prefix}metrics.shopify_subscription_discount_revenue`,
            `${prefix}metrics.shopify_subscription_nondiscount_revenue`,
          ].reduce((acc: number, key: string) => acc + payload[key], 0)
          const percentValue = 100 * (value / total)
          const renderedValue = formatNumber(Number(value), {
            currency: true,
            decimals: ignoreDecimals ? 0 : 2,
          })
          const showPercentage = !excludeFromTotal.includes(_dataKey)
          return (
            <>
              <div>{renderedValue}&nbsp;</div>
              {showPercentage && (
                <div className="text-white/50 text-sm">
                  {percentValue.toFixed(2)}% of Total
                </div>
              )}
            </>
          )
        },
      },
      totalLabelProps: {
        label: "Total Web Revenue",
        numberType: "currency",
      },
      graphs: [
        {
          type: "area",
          label: "Paid Revenue",
          dataKey: "metrics.shopify_paid_revenue",
          numberType: "currency",
          labelAggType: "sum",
          pluralizeUnit: false,
          stacked: true,
          color: "#EE8434",
        },
        {
          type: "area",
          label: "Triggered Revenue",
          dataKey: "metrics.shopify_triggered_revenue",
          numberType: "currency",
          labelAggType: "sum",
          pluralizeUnit: false,
          stacked: true,
          color: "#F4D148",
        },
        {
          type: "area",
          label: "Baseline Revenue",
          dataKey: "metrics.shopify_baseline_revenue",
          numberType: "currency",
          labelAggType: "sum",
          pluralizeUnit: false,
          stacked: true,
          color: "#318936",
        },
        {
          type: "area",
          label: "Subscription Discount Revenue",
          dataKey: "metrics.shopify_subscription_discount_revenue",
          numberType: "currency",
          labelAggType: "sum",
          pluralizeUnit: false,
          stacked: true,
          color: "#3366DD",
        },
        {
          type: "area",
          label: "Subscription Non-Discount Revenue",
          dataKey: "metrics.shopify_subscription_nondiscount_revenue",
          numberType: "currency",
          labelAggType: "sum",
          pluralizeUnit: false,
          stacked: true,
          color: "#99CCFF",
        },
        {
          type: "line",
          label: "Spend",
          dataKey: "metrics.total_ad_spend",
          numberType: "currency",
          color: "#E2323E",
        },
      ],
      graphsManualSort: true,
      interactive: false,
      compareNumberType: "currency",
      compareLabelAggType: "sum",
      hideTotal: false,
      filterableDataKey: true,
    },
    metadata: null,
    requiredSources: [SOURCE_IDS.SHOPIFY],
  },
  shopify_revenue_with_discount_table: {
    id: "shopify_revenue_with_discount_table",
    name: "Shopify Baseline, Triggered and Paid revenue Breakdown Table",
    description: "To be added please",
    type: "SimpleTable",
    props: {
      comparisonEnabled: true,
      showTotalRow: true,
      columnConfig: [
        {
          title: "Revenue Type",
          id: "revenue_type",
          totalRender: () => "All Channels:",
          columns: [
            {
              dataKey: "revenue_type",
              render: (row: TMetric) => (
                <div className="flex justify-start gap-2 items-center">
                  <div
                    className="rounded-xl w-2 h-2"
                    style={{
                      backgroundColor: row.color as string,
                    }}
                  ></div>
                  {row.revenue_type as string}
                </div>
              ),
            },
          ],
        },
        {
          title: "Non-Discount Web Revenue",
          id: "non_discount_revenue",
          totalType: "currency",
          totalDataKey: "non_discount_revenue",
          columns: [
            {
              type: "currency",
              dataKey: "non_discount_revenue",
            },
            {
              type: "percent",
              dataKey: "non_discount_revenue_of_type",
              getSuffix: (row: TMetric) => ` of ${row.revenue_type as string}`,
            },
            {
              type: "percent",
              dataKey: "non_discount_revenue_of_total",
              getSuffix: () => ` of Total`,
            },
          ],
        },
        {
          title: "Discount Web Revenue",
          id: "discount_revenue",
          totalType: "currency",
          totalDataKey: "discount_revenue",
          columns: [
            {
              type: "currency",
              dataKey: "discount_revenue",
            },
            {
              type: "percent",
              dataKey: "discount_revenue_of_type",
              getSuffix: (row: TMetric) => ` of ${row.revenue_type as string}`,
            },
            {
              type: "percent",
              dataKey: "discount_revenue_of_total",
              getSuffix: () => ` of Total`,
            },
          ],
        },
        {
          title: "Total Web Revenue",
          id: "total_revenue",
          totalType: "currency",
          totalDataKey: "total_revenue",
          columns: [
            {
              type: "currency",
              dataKey: "total_revenue",
            },
            {
              type: "percent",
              dataKey: "total_revenue_of_total",
            },
          ],
        },
      ],
    },
    requiredSources: [SOURCE_IDS.SHOPIFY],
  },
  // TODO: EXPERIMENTARY
  ga_session_paid_triggered_and_baseline_traffic_data_from_daily_metrics_table:
    {
      id: "ga_session_paid_triggered_and_baseline_traffic_data_from_daily_metrics_table",
      name: "Baseline and Paid Session Traffic Table",
      description:
        "This table shows aggregate performance of your baseline traffic for the entire selected time period.",
      type: "ComposedTable",
      props: {
        isComposed: true,
        comparisonEnabled: true,
        calculateTotal: true,
        columnConfig: [
          {
            keys: ["paid", "triggered", "baseline"],
            type: "title",
            header: "Breakdown",
            isBreakdown: true,
            dataKey: "breakdown",
          },
          {
            computeConfig: {
              compute: (row: BreakdownRecord, keyPrefix: string) => {
                const total = row[
                  `${TOTAL_CALCULATED_DATA_KEY}.sessions`
                ] as number
                const value = row[`${keyPrefix}sessions`] as number
                if (!total || !value) {
                  return "-"
                }
                if (keyPrefix.startsWith(TOTAL_CALCULATED_DATA_KEY)) {
                  return value
                }
                const percentage = safeDivide(value, total)
                return {
                  value,
                  percentage,
                } as TComposedTableComposedValue
              },
              keySuffix: "sessions_percentage",
            },
            keys: [
              "company_revenue_metrics_daily.paid_sessions_percentage",
              "company_revenue_metrics_daily.baseline_sessions_percentage",
              "company_revenue_metrics_daily.triggered_sessions_percentage",
            ],
            required: {
              keys: [
                "company_revenue_metrics_daily.paid_sessions",
                "company_revenue_metrics_daily.baseline_sessions",
                "company_revenue_metrics_daily.triggered_sessions",
              ],
              dataKey: "sessions",
            },
            type: "number",
            dataKey: "sessions_percentage",
            header: "Sessions",
          },
          {
            computeConfig: {
              compute: (row: BreakdownRecord, keyPrefix: string) => {
                const total = row[
                  `${TOTAL_CALCULATED_DATA_KEY}.revenue`
                ] as number
                const value = row[`${keyPrefix}revenue`] as number
                if (!total || !value) {
                  return "-"
                }
                if (keyPrefix.startsWith(TOTAL_CALCULATED_DATA_KEY)) {
                  return value
                }
                const percentage = safeDivide(value, total)
                return {
                  value,
                  percentage,
                } as TComposedTableComposedValue
              },
              keySuffix: "revenue_percentage",
            },
            keys: [
              "company_revenue_metrics_daily.paid_revenue_percentage",
              "company_revenue_metrics_daily.baseline_revenue_percentage",
              "company_revenue_metrics_daily.triggered_revenue_percentage",
            ],
            required: {
              keys: [
                "company_revenue_metrics_daily.paid_revenue",
                "company_revenue_metrics_daily.baseline_revenue",
                "company_revenue_metrics_daily.triggered_revenue",
              ],
              dataKey: "revenue",
            },
            type: "currency",
            dataKey: "revenue_percentage",
            header: "Revenue",
          },
          {
            computeConfig: {
              compute: (row: BreakdownRecord, keyPrefix: string) => {
                if (
                  !row[`${keyPrefix}revenue`] ||
                  !row[`${keyPrefix}sessions`]
                ) {
                  return "-"
                }
                const value = safeDivide(
                  row[`${keyPrefix}revenue`] as number,
                  row[`${keyPrefix}sessions`] as number,
                )
                if (keyPrefix.startsWith(TOTAL_CALCULATED_DATA_KEY)) {
                  return value
                }
                // const total = safeDivide(
                //   row[`${TOTAL_CALCULATED_DATA_KEY}.revenue`],
                //   row[`${TOTAL_CALCULATED_DATA_KEY}.sessions`],
                // )
                // const percentage = safeDivide(value, total) - 1
                return {
                  value,
                  // percentage,
                } as TComposedTableComposedValue
              },
              keySuffix: "revenue_per_session",
            },
            keys: [
              "company_revenue_metrics_daily.paid_revenue_per_session",
              "company_revenue_metrics_daily.baseline_revenue_per_session",
              "company_revenue_metrics_daily.triggered_revenue_per_session",
            ],
            type: "currency",
            dataKey: "revenue_per_session",
            header: "Revenue/Session",
            percentageStyles: {
              negative: "text-error",
              positive: "text-success",
            },
          },
        ],
        compareUniqueKey: "sessionType",
        disableGranularity: false, // needs for data blending
      },
      metadata: { defaultColSpan: 12, defaultRowSpan: 12 },
      requiredSources: [SOURCE_IDS.GOOGLE_ANALYTICS],
    },
  ga_mp_session_paid_triggered_and_baseline_traffic_data_table: {
    id: "ga_mp_session_paid_triggered_and_baseline_traffic_data_table",
    name: "Baseline and Paid Session Traffic Table",
    description:
      "This table shows aggregate performance of your baseline traffic for the entire selected time period.",
    type: "ComposedTable",
    props: {
      isComposed: true,
      comparisonEnabled: true,
      calculateTotal: true,
      columnConfig: [
        {
          keys: ["paid", "triggered", "baseline"],
          type: "title",
          header: "Breakdown",
          isBreakdown: true,
          dataKey: "breakdown",
        },
        {
          computeConfig: {
            compute: (row: BreakdownRecord, keyPrefix: string) => {
              const total = row[
                `${TOTAL_CALCULATED_DATA_KEY}.sessions`
              ] as number
              const value = row[`${keyPrefix}sessions`] as number
              if (!total || !value) {
                return "-"
              }
              if (keyPrefix.startsWith(TOTAL_CALCULATED_DATA_KEY)) {
                return value
              }
              const percentage = safeDivide(value, total)
              return {
                value,
                percentage,
              } as TComposedTableComposedValue
            },
            keySuffix: "sessions_percentage",
          },
          keys: [
            "metrics.ga_multi_property_paid_sessions_percentage",
            "metrics.ga_multi_property_baseline_sessions_percentage",
            "metrics.ga_multi_property_triggered_sessions_percentage",
          ],
          required: {
            keys: [
              "metrics.ga_multi_property_paid_sessions",
              "metrics.ga_multi_property_baseline_sessions",
              "metrics.ga_multi_property_triggered_sessions",
            ],
            dataKey: "sessions",
          },
          type: "number",
          dataKey: "sessions_percentage",
          header: "Sessions",
        },
        {
          computeConfig: {
            compute: (row: BreakdownRecord, keyPrefix: string) => {
              const total = row[
                `${TOTAL_CALCULATED_DATA_KEY}.revenue`
              ] as number
              const value = row[`${keyPrefix}revenue`] as number
              if (!total || !value) {
                return "-"
              }
              if (keyPrefix.startsWith(TOTAL_CALCULATED_DATA_KEY)) {
                return value
              }
              const percentage = safeDivide(value, total)
              return {
                value,
                percentage,
              } as TComposedTableComposedValue
            },
            keySuffix: "revenue_percentage",
          },
          keys: [
            "metrics.ga_multi_property_paid_revenue_percentage",
            "metrics.ga_multi_property_baseline_revenue_percentage",
            "metrics.ga_multi_property_triggered_revenue_percentage",
          ],
          required: {
            keys: [
              "metrics.ga_multi_property_paid_revenue",
              "metrics.ga_multi_property_baseline_revenue",
              "metrics.ga_multi_property_triggered_revenue",
            ],
            dataKey: "revenue",
          },
          type: "currency",
          dataKey: "revenue_percentage",
          header: "Revenue",
        },
        {
          computeConfig: {
            compute: (row: BreakdownRecord, keyPrefix: string) => {
              if (!row[`${keyPrefix}revenue`] || !row[`${keyPrefix}sessions`]) {
                return "-"
              }
              const value = safeDivide(
                row[`${keyPrefix}revenue`] as number,
                row[`${keyPrefix}sessions`] as number,
              )
              if (keyPrefix.startsWith(TOTAL_CALCULATED_DATA_KEY)) {
                return value
              }
              // const total = safeDivide(
              //   row[`${TOTAL_CALCULATED_DATA_KEY}.revenue`],
              //   row[`${TOTAL_CALCULATED_DATA_KEY}.sessions`],
              // )
              // const percentage = safeDivide(value, total) - 1
              return {
                value,
                // percentage,
              } as TComposedTableComposedValue
            },
            keySuffix: "revenue_per_session",
          },
          keys: [
            "metrics.ga_multi_property_paid_revenue_per_session",
            "metrics.ga_multi_property_baseline_revenue_per_session",
            "metrics.ga_multi_property_triggered_revenue_per_session",
          ],
          type: "currency",
          dataKey: "revenue_per_session",
          header: "Revenue/Session",
          percentageStyles: {
            negative: "text-error",
            positive: "text-success",
          },
        },
      ],
      compareUniqueKey: "sessionType",
      disableGranularity: false, // needs for data blending
    },
    metadata: { defaultColSpan: 12, defaultRowSpan: 12 },
    requiredSources: [SOURCE_IDS.GOOGLE_ANALYTICS],
  },
  ga_baseline_traffic_vs_revenue_stacked_chart: {
    id: "ga_baseline_traffic_vs_revenue_stacked_chart",
    name: "Baseline Traffic & Revenue",
    description: null,
    type: "ComposedChart",
    props: {
      title: "Baseline Traffic & Revenue",
      comparisonEnabled: true,
      graphs: [
        {
          type: "area",
          unit: "session",
          label: "Baseline Traffic",
          dataKey: "metrics.ga_baseline_sessions",
          yAxisId: "sessions",
        },
        {
          type: "area",
          numberType: "currency",
          label: "Baseline Revenue",
          dataKey: "metrics.ga_baseline_revenue",
          yAxisId: "currency",
        },
      ],
      hideTotal: true,
      interactive: false,
    },
    metadata: null,
    requiredSources: [SOURCE_IDS.GOOGLE_ANALYTICS],
  },
  ga_mp_baseline_traffic_vs_revenue_stacked_chart: {
    id: "ga_mp_baseline_traffic_vs_revenue_stacked_chart",
    name: "Baseline Traffic & Revenue",
    description: null,
    type: "ComposedChart",
    props: {
      title: "Baseline Traffic & Revenue",
      comparisonEnabled: true,
      graphs: [
        {
          type: "area",
          unit: "session",
          label: "Baseline Traffic",
          dataKey: "metrics.ga_multi_property_baseline_sessions",
          yAxisId: "sessions",
        },
        {
          type: "area",
          numberType: "currency",
          label: "Baseline Revenue",
          dataKey: "metrics.ga_multi_property_baseline_revenue",
          yAxisId: "currency",
        },
      ],
      hideTotal: true,
      interactive: false,
    },
    metadata: null,
    requiredSources: [SOURCE_IDS.GOOGLE_ANALYTICS],
  },
  ga_sessions_from_organic_sessions: {
    id: "ga_sessions_from_organic_sessions",
    name: "Sessions vs. Revenue from Organic",
    description:
      "This metric represents the total number of sessions that came from Organic Search, Direct, and Organic Referral - meant to represent those users who are visiting you purely through organic, and highly reliabe/repeatable, mediums.",
    type: "ComposedChart",
    props: {
      title: "Sessions vs. Revenue from Organic",
      comparisonEnabled: true,
      graphs: [
        {
          type: "area",
          unit: "session",
          label: "sessions",
          yAxisId: "sessions",
          dataKey: "metrics.ga_sessions_from_organic_sessions",
        },
        {
          type: "line",
          label: "revenue",
          dataKey: "metrics.ga_revenue_from_organic_sessions",
          numberType: "currency",
          yAxisId: "currency",
        },
      ],
      hideTotal: true,
    },
    metadata: null,
    requiredSources: [SOURCE_IDS.GOOGLE_ANALYTICS],
  },
  ga_sessions_from_direct_sessions: {
    id: "ga_sessions_from_direct_sessions",
    name: "Sessions vs. Revenue from Direct",
    description:
      "Traffic that arrives to your site from direct entry of the brand url in browser.",
    type: "ComposedChart",
    props: {
      title: "Sessions vs. Revenue from Direct",
      comparisonEnabled: true,
      graphs: [
        {
          type: "area",
          unit: "session",
          label: "sessions",
          yAxisId: "sessions",
          dataKey: "metrics.ga_sessions_from_direct_sessions",
        },
        {
          type: "line",
          label: "revenue",
          dataKey: "metrics.ga_revenue_from_direct_sessions",
          numberType: "currency",
          yAxisId: "currency",
        },
      ],
      hideTotal: true,
    },
    metadata: null,
    requiredSources: [SOURCE_IDS.GOOGLE_ANALYTICS],
  },
  ga_sessions_from_organic_referal_sessions: {
    id: "ga_sessions_from_organic_referal_sessions",
    name: "Organic Social Referral",
    description:
      'Traffic that arrives to your site with medium "referral" and an organic social source utm.',
    type: "ComposedChart",
    props: {
      title: "Organic Social Referral",
      comparisonEnabled: true,
      graphs: [
        {
          type: "area",
          unit: "session",
          label: "sessions",
          yAxisId: "sessions",
          dataKey: "metrics.ga_sessions_from_organic_referal_sessions",
        },
        {
          type: "line",
          label: "revenue",
          dataKey: "metrics.ga_revenue_from_organic_referral_sessions",
          numberType: "currency",
          yAxisId: "currency",
        },
      ],
      hideTotal: true,
    },
    metadata: null,
    requiredSources: [SOURCE_IDS.GOOGLE_ANALYTICS],
  },
  ga_mp_sessions_from_organic_sessions: {
    id: "ga_mp_sessions_from_organic_sessions",
    name: "Sessions vs. Revenue from Organic",
    description:
      "This metric represents the total number of sessions that came from Organic Search, Direct, and Organic Referral - meant to represent those users who are visiting you purely through organic, and highly reliabe/repeatable, mediums.",
    type: "ComposedChart",
    props: {
      title: "Sessions vs. Revenue from Organic",
      comparisonEnabled: true,
      graphs: [
        {
          type: "area",
          unit: "session",
          label: "sessions",
          yAxisId: "sessions",
          dataKey: "metrics.ga_multi_property_sessions_from_organic_sessions",
        },
        {
          type: "line",
          label: "revenue",
          dataKey: "metrics.ga_multi_property_revenue_from_organic_sessions",
          numberType: "currency",
          yAxisId: "currency",
        },
      ],
      hideTotal: true,
    },
    metadata: null,
    requiredSources: [SOURCE_IDS.GOOGLE_ANALYTICS],
  },
  ga_mp_sessions_from_direct_sessions: {
    id: "ga_mp_sessions_from_direct_sessions",
    name: "Sessions vs. Revenue from Direct",
    description:
      "Traffic that arrives to your site from direct entry of the brand url in browser.",
    type: "ComposedChart",
    props: {
      title: "Sessions vs. Revenue from Direct",
      comparisonEnabled: true,
      graphs: [
        {
          type: "area",
          unit: "session",
          label: "sessions",
          yAxisId: "sessions",
          dataKey: "metrics.ga_multi_property_sessions_from_direct_sessions",
        },
        {
          type: "line",
          label: "revenue",
          dataKey: "metrics.ga_multi_property_revenue_from_direct_sessions",
          numberType: "currency",
          yAxisId: "currency",
        },
      ],
      hideTotal: true,
    },
    metadata: null,
    requiredSources: [SOURCE_IDS.GOOGLE_ANALYTICS],
  },
  ga_mp_sessions_from_organic_referal_sessions: {
    id: "ga_mp_sessions_from_organic_referal_sessions",
    name: "Organic Social Referral",
    description:
      'Traffic that arrives to your site with medium "referral" and an organic social source utm.',
    type: "ComposedChart",
    props: {
      title: "Organic Social Referral",
      comparisonEnabled: true,
      graphs: [
        {
          type: "area",
          unit: "session",
          label: "sessions",
          yAxisId: "sessions",
          dataKey:
            "metrics.ga_multi_property_sessions_from_organic_referal_sessions",
        },
        {
          type: "line",
          label: "revenue",
          dataKey:
            "metrics.ga_multi_property_revenue_from_organic_referral_sessions",
          numberType: "currency",
          yAxisId: "currency",
        },
      ],
      hideTotal: true,
    },
    metadata: null,
    requiredSources: [SOURCE_IDS.GOOGLE_ANALYTICS],
  },
  session_source_and_medium_traffic_data_table: {
    id: "session_source_and_medium_traffic_data_table",
    name: "Session Source And Medium Traffic Table",
    description:
      "This table shows a breakout of all of your session sources and mediums, and their relative performance, in aggregate over the entire time period selected.",
    type: "TableVisualization",
    props: {
      comparisonEnabled: true,
      columnConfig: [
        {
          key: "company_revenue_metrics_daily.sessionsource",
          type: "title",
          header: "Source",
          width: 300,
        },
        {
          key: "company_revenue_metrics_daily.sessionmedium",
          type: "title",
          header: "Medium",
        },
        {
          key: "company_revenue_metrics_daily.baseline_sessions",
          type: "number",
          header: "Sessions",
        },
        {
          key: "company_revenue_metrics_daily.baseline_engaged_sessions",
          type: "number",
          header: "Engaged Sessions",
        },
        {
          key: "company_revenue_metrics_daily.baseline_new_sessions",
          type: "number",
          header: "New Sessions",
        },
        {
          key: "company_revenue_metrics_daily.baseline_revenue",
          type: "currency",
          header: "Revenue",
        },
        {
          key: "company_revenue_metrics_daily.baseline_revenue_per_session",
          type: "currency",
          header: "Revenue Per Session",
        },
      ],
      compareUniqueKey: "company_revenue_metrics_daily.sessionsource",
      enablePagination: true,
      disableGranularity: true,
    },
    metadata: { defaultColSpan: 12, defaultRowSpan: 12 },
    requiredSources: [SOURCE_IDS.GOOGLE_ANALYTICS],
  },
  branded_search_clicks_stacked_chart: {
    id: "branded_search_clicks_stacked_chart",
    name: "Branded Search Clicks Chart",
    description:
      "This chart compares the number of clicks you are getting from branded search terms organically vs. in paid branded search through Google Ads.",
    type: "ComposedChart",
    props: {
      title: "Branded Search Clicks",
      totalLabelProps: {
        unit: "click",
        label: "Total",
        pluralizeUnit: true,
      },
      graphs: [
        {
          type: "area",
          label: "Paid clicks",
          dataKey: "metrics.google_ads_paid_branded_search_clicks",
          yAxisId: "clicks",
          requiredSources: [SOURCE_IDS.GOOGLE_ADS],
          labelAggType: "percent-total",
        },
        {
          type: "area",
          label: "Organic clicks",
          dataKey: "metrics.google_search_organic_branded_search_clicks",
          yAxisId: "clicks",
          requiredSources: [SOURCE_IDS.GOOGLE_SEARCH],
          labelAggType: "percent-total",
        },
        // {
        //   type: "dashed",
        //   unit: "dollar",
        //   label: "Spend",
        //   dataKey: "metrics.google_ads_paid_branded_search_total_spend",
        //   requiredSources: [SOURCE_IDS.GOOGLE_ADS],
        //   numberType: "currency",
        //   pluralizeUnit: true,
        // },
      ],
      compareUnit: "click",
      interactive: false,
      compareLabelAggType: "sum",
    },
    metadata: null,
    requiredSources: [SOURCE_IDS.GOOGLE_ADS, SOURCE_IDS.GOOGLE_SEARCH],
  },
  brand_media_optimization_campaign_table: {
    id: "brand_media_optimization_campaign_table",
    name: "Brand Media Optimization Campaign Table",
    description: "<TO BE ADDED>",
    type: "TableVisualization",
    props: {
      sortBy: `brand_value_agg`,
      queryPrefix: "company_media_metrics_campaigns",
      columnConfig: [
        {
          key: "media_channel",
          type: "image",
          header: "Media Channel",
          classes: {
            head: "text-center",
            cell: "text-center",
          },
          width: 100,
        },
        {
          key: "campaign_name",
          type: "title",
          render: (row: TMetric) => {
            const title = row.campaign_name
            if (typeof title !== "string") {
              return ""
            }

            return <CaptionPreview text={title} exact={true} />
          },
          header: "Name",
          width: 350,
        },
        {
          key: "campaign_status",
          type: "title",
          header: "Status",
          width: 100,
        },
        {
          key: "spend",
          type: "currency",
          header: "Spend",
          width: 100,
          metricKey: "total_ad_spend",
        },
        {
          key: "brand_value_agg",
          type: "currency",
          // isBrand: true,
          header: "180d Brand Value",
          width: 150,
          isBrand: true,
          metricKey: "d180_brand_value",
        },
        {
          key: "brand_value_agg_range",
          // type: "currency",
          header: "180d Brand Value Range",
          disableSorting: true,
          disableFilter: true,
          width: 200,
          isBrand: true,
          render: (row: TMetric) => (
            <div>
              {formatValue(
                (row?.lower_brand_value_agg as string | number) || "",
                "currency",
                undefined,
              )}
              {" - "}
              {formatValue(
                (row?.upper_brand_value_agg as string | number) || "",
                "currency",
                undefined,
              )}
            </div>
          ),
        },
        {
          key: "brand_value_over_spend",
          type: "coef",
          // isBrand: true,
          header: "180d Brand ROAS",
          width: 150,
          highlightFn: highlightRoasCell,
          isBrand: true,
        },
        {
          key: "purchases",
          type: "currency",
          header: "7d Click DR Revenue",
          width: 130,
        },
        {
          key: "short_term_revenue_roas",
          type: "coef",
          header: "7d Click DR ROAS",
          width: 130,
          highlightFn: highlightRoasCell,
        },
        {
          key: "modeled_revenue",
          type: "currency",
          isBrand: true,
          header: "Modeled Revenue",
          width: 150,
          disabled: true,
        },
        {
          key: "modeled_revenue_over_spend",
          type: "coef",
          isBrand: true,
          header: "Modeled ROAS",
          width: 150,
          highlightFn: highlightRoasCell,
          disabled: true,
        },
        {
          key: "estimated_d180_brand_value",
          type: "currency",
          isBrand: true,
          header: "Modeled Brand Value",
          width: 170,
          disabled: true,
        },
        {
          key: "modeled_brand_value_over_spend",
          type: "coef",
          isBrand: true,
          header: "Modeled bROAS",
          width: 150,
          highlightFn: highlightRoasCell,
          disabled: true,
        },
        {
          key: "revenue_from_search",
          type: "currency",
          header: "Revenue from Search",
          width: 130,
          isBrand: true,
        },
        {
          key: "revenue_from_search_roas",
          type: "coef",
          header: "Revenue from Search ROAS",
          width: 130,
          isBrand: true,
        },
        {
          key: "long_term_revenue",
          type: "currency",
          header: "Total Value",
          width: 130,
        },
        {
          key: "long_term_revenue_roas",
          type: "coef",
          header: "Total ROAS",
          width: 130,
          highlightFn: highlightRoasCell,
        },
        {
          key: "shares",
          header: "Shares",
          type: "number",
          width: 110,
        },
        {
          key: "likes",
          header: "Likes & Reactions",
          type: "number",
          width: 120,
        },
        {
          key: "follows",
          header: "Followers",
          type: "number",
          width: 120,
        },
        {
          key: "comments",
          header: "Comments",
          type: "number",
          width: 130,
        },
        {
          key: "saves",
          header: "Saves",
          type: "number",
          width: 110,
        },
        {
          key: "brand_value_over_spend_range",
          // type: "coef",
          header: "180d Brand ROAS Range",
          width: 200,
          disableSorting: true,
          disableFilter: true,
          isBrand: true,
          render: (row: TMetric) => (
            <div>
              {formatValue(
                safeDivide(
                  row.lower_brand_value_agg as number,
                  row.spend as number,
                ) || 0.01,
                "coef",
                undefined,
              )}
              {" - "}
              {formatValue(
                safeDivide(
                  row.upper_brand_value_agg as number,
                  row.spend as number,
                ) || 0.01,
                "coef",
                undefined,
              )}
            </div>
          ),
        },
        {
          key: "d30_brand_value",
          type: "currency",
          // isBrand: true,
          header: "30d Brand Value",
          width: 150,
          isBrand: true,
          metricKey: "d30_brand_value",
        },
        {
          key: "d60_brand_value",
          type: "currency",
          // isBrand: true,
          header: "60d Brand Value",
          width: 150,
          isBrand: true,
          metricKey: "d60_brand_value",
        },
        {
          key: "d90_brand_value",
          type: "currency",
          // isBrand: true,
          header: "90d Brand Value",
          width: 150,
          isBrand: true,
          metricKey: "d90_brand_value",
        },
        {
          key: "facebook_ads_brand_value",
          type: "currency",
          header: "Facebook ads Brand Value",
          width: 120,
          disabled: true,
        },
        {
          key: "facebook_ads_spend",
          type: "currency",
          header: "Facebook ads spend",
          width: 120,
          disabled: true,
          metricKey: "facebook_ads_spend",
        },
        {
          key: "facebook_ads_comments",
          type: "number",
          header: "Facebook ads comments",
          width: 120,
          disabled: true,
          metricKey: "facebook_ads_comments",
        },
        {
          key: "facebook_ads_shares",
          type: "number",
          header: "Facebook ads shares",
          width: 120,
          disabled: true,
        },
        {
          key: "facebook_ads_new_fans",
          type: "number",
          header: "Facebook ads new fans",
          width: 120,
          disabled: true,
        },
        {
          key: "facebook_ads_reactions",
          type: "number",
          header: "Facebook ads reactions",
          width: 120,
          disabled: true,
        },
        {
          key: "instagram_ads_brand_value",
          type: "currency",
          header: "Instagram ads Brand Value",
          width: 120,
          disabled: true,
        },
        {
          key: "instagram_ads_spend",
          type: "currency",
          header: "Instagram ads spend",
          width: 120,
          disabled: true,
        },
        {
          key: "instagram_ads_follows",
          type: "number",
          header: "Instagram ads follows",
          width: 120,
          disabled: true,
        },
        {
          key: "instagram_ads_comments",
          type: "number",
          header: "Instagram ads comments",
          width: 120,
          disabled: true,
        },
        {
          key: "instagram_ads_likes",
          type: "number",
          header: "Instagram ads likes",
          width: 120,
          disabled: true,
        },
        {
          key: "instagram_ads_shares",
          type: "number",
          header: "Instagram ads shares",
          width: 120,
          disabled: true,
        },
        {
          key: "tiktok_ads_spend",
          type: "currency",
          header: "TikTok ads spend",
          width: 120,
          disabled: true,
        },
        {
          key: "tiktok_ads_follows",
          type: "number",
          header: "TikTok ads follows",
          width: 120,
          disabled: true,
        },
        {
          key: "tiktok_ads_comments",
          type: "number",
          header: "TikTok ads comments",
          width: 120,
          disabled: true,
        },
        {
          key: "tiktok_ads_shares",
          type: "number",
          header: "TikTok ads shares",
          width: 120,
          disabled: true,
        },
        {
          key: "tiktok_ads_likes",
          type: "number",
          header: "TikTok ads likes",
          width: 120,
          disabled: true,
        },
        {
          key: "pinterest_ads_spend",
          type: "currency",
          header: "Pinterest ads spend",
          width: 120,
          disabled: true,
        },
        {
          key: "pinterest_ads_saves",
          type: "number",
          header: "Pinterest ads saves",
          width: 120,
          disabled: true,
        },
        {
          key: "budget_agg",
          type: "currency",
          header: "Budget",
          disabled: true,
          width: 85,
        },
        {
          key: "campaign_id",
          type: "title",
          header: "Campaign ID",
        },
      ],
      totalLabelColumn: "campaign_name",
      compareUniqueKey: "campaign_id",
      enablePagination: true,
      rowClickInDrawer: true,
      prefixEnabled: true,
      disableGranularity: true,
      highlightCells: true,
    },
    metadata: { defaultColSpan: 12, defaultRowSpan: 12 },
    requiredSources: [
      SOURCE_IDS.META,
      SOURCE_IDS.TIKTOK_ADS,
      SOURCE_IDS.PINTEREST_ADS,
    ],
  },
  brand_media_optimization_adsets_table: {
    id: "brand_media_optimization_adsets_table",
    name: "Brand Media Optimization Ad Sets Table",
    description: "<TO BE ADDED>",
    type: "TableVisualization",
    props: {
      sortBy: `brand_value_agg`,
      queryPrefix: "company_media_metrics_adsets",
      additionalFilters: [
        {
          value: "campaign_id",
          label: "Campaign Id",
        },
      ],
      columnConfig: [
        {
          key: "media_channel",
          type: "image",
          header: "Media Channel",
          classes: {
            head: "text-center",
            cell: "text-center",
          },
          width: 65,
        },
        {
          key: "adset_name",
          type: "title",
          header: "Name",
          render: (row: TMetric) => {
            const title = row["adset_name"]
            if (typeof title !== "string") {
              return ""
            }

            return <CaptionPreview text={title} exact={true} />
          },
          width: 350,
        },
        {
          key: "campaign_name",
          type: "title",
          header: "Campaign",
          render: (row: TMetric) => {
            const title = row.campaign_name
            if (typeof title !== "string") {
              return ""
            }

            return <CaptionPreview text={title} exact={true} />
          },
          width: 350,
        },
        {
          key: "campaign_status",
          type: "title",
          header: "Campaign Status",
          width: 100,
        },
        {
          key: "adset_status",
          type: "title",
          header: "Adset Status",
          width: 125,
        },
        {
          key: "initiative_type",
          type: "custom",
          header: "Initiative Type",
          width: 250,
          cellType: "custom-component",
          // Custom component is provided by BrandMediaOptimizationDashboard.
        },
        {
          key: "spend",
          type: "currency",
          header: "Spend",
          width: 100,
        },
        {
          key: "brand_value_agg",
          type: "currency",
          isBrand: true,
          header: "180d Brand Value",
          width: 150,
        },
        {
          key: "brand_value_agg_range",
          type: "currency",
          isBrand: true,
          header: "180d Brand Value Range",
          disableSorting: true,
          disableFilter: true,
          width: 200,
          render: (row: TMetric) => (
            <div>
              {formatValue(
                (row?.lower_brand_value_agg as string | number) || "",
                "currency",
                undefined,
              )}
              {" - "}
              {formatValue(
                (row?.upper_brand_value_agg as string | number) || "",
                "currency",
                undefined,
              )}
            </div>
          ),
        },
        {
          key: "brand_value_over_spend",
          type: "coef",
          isBrand: true,
          header: "180d Brand ROAS",
          width: 150,
          highlightFn: highlightRoasCell,
        },
        {
          key: "purchases",
          type: "currency",
          header: "7d Click DR Revenue",
          width: 110,
        },
        {
          key: "short_term_revenue_roas",
          type: "coef",
          header: "7d Click DR ROAS",
          width: 110,
          highlightFn: highlightRoasCell,
        },
        {
          key: "modeled_revenue",
          type: "currency",
          isBrand: true,
          header: "Modeled Revenue",
          width: 150,
          disabled: true,
        },
        {
          key: "modeled_revenue_over_spend",
          type: "coef",
          isBrand: true,
          header: "Modeled ROAS",
          width: 150,
          highlightFn: highlightRoasCell,
          disabled: true,
        },
        {
          key: "estimated_d180_brand_value",
          type: "currency",
          isBrand: true,
          header: "Modeled Brand Value",
          width: 170,
          disabled: true,
        },
        {
          key: "modeled_brand_value_over_spend",
          type: "coef",
          isBrand: true,
          header: "Modeled bROAS",
          width: 150,
          highlightFn: highlightRoasCell,
          disabled: true,
        },
        {
          key: "revenue_from_search",
          type: "currency",
          header: "Revenue from Search",
          width: 130,
          isBrand: true,
        },
        {
          key: "revenue_from_search_roas",
          type: "coef",
          header: "Revenue from Search ROAS",
          width: 130,
          isBrand: true,
        },
        {
          key: "long_term_revenue",
          type: "currency",
          header: "Total Value",
          width: 130,
        },
        {
          key: "long_term_revenue_roas",
          type: "coef",
          header: "Total ROAS",
          width: 130,
          highlightFn: highlightRoasCell,
        },
        {
          key: "shares",
          header: "Shares",
          type: "number",
          width: 110,
        },
        {
          key: "likes",
          header: "Likes & Reactions",
          type: "number",
          width: 120,
        },
        {
          key: "follows",
          header: "Followers",
          type: "number",
          width: 120,
        },
        {
          key: "comments",
          header: "Comments",
          type: "number",
          width: 130,
        },
        {
          key: "saves",
          header: "Saves",
          type: "number",
          width: 110,
        },
        {
          key: "brand_value_over_spend_range",
          type: "coef",
          header: "180d Brand ROAS Range",
          isBrand: true,
          width: 200,
          render: (row: TMetric) => (
            <div>
              {formatValue(
                safeDivide(
                  row.lower_brand_value_agg as number,
                  row.spend as number,
                ) || 0.01,
                "coef",
                undefined,
              )}
              {" - "}
              {formatValue(
                safeDivide(
                  row.upper_brand_value_agg as number,
                  row.spend as number,
                ) || 0.01,
                "coef",
                undefined,
              )}
            </div>
          ),
        },
        {
          key: "d30_brand_value",
          type: "currency",
          // isBrand: true,
          header: "30d Brand Value",
          width: 150,
          isBrand: true,
          metricKey: "d30_brand_value",
        },
        {
          key: "d60_brand_value",
          type: "currency",
          // isBrand: true,
          header: "60d Brand Value",
          width: 150,
          isBrand: true,
          metricKey: "d60_brand_value",
        },
        {
          key: "d90_brand_value",
          type: "currency",
          // isBrand: true,
          header: "90d Brand Value",
          width: 150,
          isBrand: true,
          metricKey: "d90_brand_value",
        },
        {
          key: "facebook_ads_brand_value",
          type: "currency",
          header: "Facebook ads Brand Value",
          width: 120,
          disabled: true,
        },
        {
          key: "facebook_ads_spend",
          type: "currency",
          header: "Facebook ads spend",
          width: 120,
          disabled: true,
          metricKey: "facebook_ads_spend",
        },
        {
          key: "facebook_ads_comments",
          type: "number",
          header: "Facebook ads comments",
          width: 120,
          disabled: true,
          metricKey: "facebook_ads_comments",
        },
        {
          key: "facebook_ads_shares",
          type: "number",
          header: "Facebook ads shares",
          width: 120,
          disabled: true,
        },
        {
          key: "facebook_ads_new_fans",
          type: "number",
          header: "Facebook ads new fans",
          width: 120,
          disabled: true,
        },
        {
          key: "facebook_ads_reactions",
          type: "number",
          header: "Facebook ads reactions",
          width: 120,
          disabled: true,
        },
        {
          key: "instagram_ads_brand_value",
          type: "currency",
          header: "Instagram ads Brand Value",
          width: 120,
          disabled: true,
        },
        {
          key: "instagram_ads_spend",
          type: "currency",
          header: "Instagram ads spend",
          width: 120,
          disabled: true,
        },
        {
          key: "instagram_ads_follows",
          type: "number",
          header: "Instagram ads follows",
          width: 120,
          disabled: true,
        },
        {
          key: "instagram_ads_comments",
          type: "number",
          header: "Instagram ads comments",
          width: 120,
          disabled: true,
        },
        {
          key: "instagram_ads_likes",
          type: "number",
          header: "Instagram ads likes",
          width: 120,
          disabled: true,
        },
        {
          key: "instagram_ads_shares",
          type: "number",
          header: "Instagram ads shares",
          width: 120,
          disabled: true,
        },
        {
          key: "tiktok_ads_spend",
          type: "currency",
          header: "TikTok ads spend",
          width: 120,
          disabled: true,
        },
        {
          key: "tiktok_ads_follows",
          type: "number",
          header: "TikTok ads follows",
          width: 120,
          disabled: true,
        },
        {
          key: "tiktok_ads_comments",
          type: "number",
          header: "TikTok ads comments",
          width: 120,
          disabled: true,
        },
        {
          key: "tiktok_ads_shares",
          type: "number",
          header: "TikTok ads shares",
          width: 120,
          disabled: true,
        },
        {
          key: "tiktok_ads_likes",
          type: "number",
          header: "TikTok ads likes",
          width: 120,
          disabled: true,
        },
        {
          key: "pinterest_ads_spend",
          type: "currency",
          header: "Pinterest ads spend",
          width: 120,
          disabled: true,
        },
        {
          key: "pinterest_ads_saves",
          type: "number",
          header: "Pinterest ads saves",
          width: 120,
          disabled: true,
        },
        {
          key: "optimization_goal",
          type: "title",
          header: "Optimization Goal",
          width: 95,
          disabled: true,
        },
        {
          key: "budget_agg",
          type: "currency",
          header: "Budget",
          width: 85,
          disabled: true,
        },
        {
          key: "adset_id",
          type: "title",
          header: "Adset ID",
        },
      ],
      totalLabelColumn: "adset_name",
      compareUniqueKey: "adset_id",
      enablePagination: true,
      rowClickInDrawer: true,
      prefixEnabled: true,
      disableGranularity: true,
      highlightCells: true,
    },
    metadata: { defaultColSpan: 12, defaultRowSpan: 12 },
    requiredSources: [
      SOURCE_IDS.META,
      SOURCE_IDS.TIKTOK_ADS,
      SOURCE_IDS.PINTEREST_ADS,
    ],
  },
  custom_attribution_brand_media_optimization_adsets_table: {
    id: "custom_attribution_brand_media_optimization_adsets_table",
    name: "Brand Media Optimization Ad Sets Table",
    description: "<TO BE ADDED>",
    type: "TableVisualization",
    props: {
      sortBy: `brand_value_agg`,
      queryPrefix: "company_media_metrics_adsets",
      additionalFilters: [
        {
          value: "campaign_id",
          label: "Campaign Id",
        },
      ],
      columnConfig: [
        // {
        //   key: "select",
        //   type: "custom",
        //   header: "",
        //   width: 55,
        //   disableSorting: true,
        //   disableFiltering: true,
        // },
        {
          key: "media_channel",
          type: "image",
          header: "Media Channel",
          classes: {
            head: "text-center",
            cell: "text-center",
          },
          width: 65,
        },
        {
          key: "adset_name",
          type: "title",
          header: "Name",
          render: (row: TMetric) => {
            const title = row["adset_name"]
            if (typeof title !== "string") {
              return ""
            }

            return <CaptionPreview text={title} exact={true} />
          },
          width: 350,
        },

        {
          key: "campaign_name",
          filterKey: "campaign_id",
          type: "select",
          header: "Campaign",
          render: (row: TMetric) => {
            const title = row.campaign_name
            if (typeof title !== "string") {
              return ""
            }

            return <CaptionPreview text={title} exact={true} />
          },
          width: 350,
        },
        {
          key: "campaign_status",
          type: "title",
          header: "Campaign Status",
          width: 100,
        },
        {
          key: "initiative_type",
          type: "custom",
          header: "Initiative Type",
          width: 250,
          cellType: "custom-component",
          // Custom component will be provided by the table implementation
        },
        {
          key: "adset_status",
          type: "title",
          header: "Adset Status",
          width: 125,
        },

        {
          key: "spend",
          type: "currency",
          header: "Spend",
          width: 100,
        },
        {
          key: "modeled_revenue",
          type: "currency",
          isBrand: true,
          header: "Modeled Revenue",
          width: 150,
          disabled: true,
        },
        {
          key: "estimated_d180_brand_value",
          type: "currency",
          isBrand: true,
          header: "Modeled Brand Value",
          width: 170,
          disabled: true,
        },
        {
          key: "brand_value_agg",
          type: "currency",
          isBrand: true,
          header: "180d Brand Value",
          width: 120,
        },
        {
          key: "brand_value_agg_range",
          type: "currency",
          isBrand: true,
          header: "180d Brand Value Range",
          disableSorting: true,
          disableFilter: true,
          width: 200,
          render: (row: TMetric) => (
            <div>
              {formatValue(
                (row?.lower_brand_value_agg as string | number) || "",
                "currency",
                undefined,
              )}
              {" - "}
              {formatValue(
                (row?.upper_brand_value_agg as string | number) || "",
                "currency",
                undefined,
              )}
            </div>
          ),
        },
        {
          key: "brand_value_over_spend_range",
          type: "coef",
          header: "180d Brand ROAS Range",
          isBrand: true,
          width: 200,
          disableSorting: true,
          disableFilter: true,
          render: (row: TMetric) => (
            <div>
              {formatValue(
                safeDivide(
                  row.lower_brand_value_agg as number,
                  row.spend as number,
                ) || 0.01,
                "coef",
                undefined,
              )}
              {" - "}
              {formatValue(
                safeDivide(
                  row.upper_brand_value_agg as number,
                  row.spend as number,
                ) || 0.01,
                "coef",
                undefined,
              )}
            </div>
          ),
        },
        {
          key: "brand_value_over_spend",
          type: "coef",
          isBrand: true,
          header: "180d Brand ROAS",
          width: 120,
          highlightFn: highlightRoasCell,
        },
        {
          key: "d30_brand_value",
          type: "currency",
          // isBrand: true,
          header: "30d Brand Value",
          width: 150,
          isBrand: true,
          metricKey: "d30_brand_value",
        },
        {
          key: "d60_brand_value",
          type: "currency",
          // isBrand: true,
          header: "60d Brand Value",
          width: 150,
          isBrand: true,
          metricKey: "d60_brand_value",
        },
        {
          key: "d90_brand_value",
          type: "currency",
          // isBrand: true,
          header: "90d Brand Value",
          width: 150,
          isBrand: true,
          metricKey: "d90_brand_value",
        },
        {
          key: "meta_purchase_value_7d_click_1d_view",
          type: "currency",
          header: "7d Click, 1d View Revenue",
          width: 130,
        },
        {
          key: "purchase_value_1d_view",
          type: "number",
          header: "7d Click, 1d View Count",
          width: 130,
        },
        {
          key: "purchase_value_1d_engaged_view",
          type: "number",
          header: "7d Click, 1d Engaged View Count",
          width: 130,
        },
        {
          key: "ga4_total_revenue",
          type: "number",
          header: "7d Click DR Count",
          width: 130,
        },

        {
          key: "purchases",
          type: "currency",
          header: "7d Click DR Revenue",
          width: 110,
        },
        {
          key: "short_term_revenue_roas",
          type: "coef",
          header: "7d Click DR ROAS",
          width: 110,
          highlightFn: highlightRoasCell,
        },
        {
          key: "revenue_from_search",
          type: "currency",
          header: "Revenue from Search",
          width: 130,
        },
        {
          key: "revenue_from_search_roas",
          type: "coef",
          header: "Revenue from Search ROAS",
          width: 130,
        },
        {
          key: "long_term_revenue",
          type: "currency",
          header: "Total Value",
          width: 130,
        },
        {
          key: "long_term_revenue_roas",
          type: "coef",
          header: "Total ROAS",
          width: 130,
          highlightFn: highlightRoasCell,
        },
        {
          key: "shares",
          header: "Shares",
          type: "number",
          width: 110,
          disabled: true,
        },
        {
          key: "likes",
          header: "Likes & Reactions",
          type: "number",
          width: 120,
          disabled: true,
        },
        {
          key: "follows",
          header: "Followers",
          type: "number",
          width: 120,
          disabled: true,
        },
        {
          key: "comments",
          header: "Comments",
          type: "number",
          width: 130,
          disabled: true,
        },
        {
          key: "saves",
          header: "Saves",
          type: "number",
          width: 110,
          disabled: true,
        },
        {
          key: "facebook_ads_brand_value",
          type: "currency",
          header: "Facebook ads Brand Value",
          width: 120,
          disabled: true,
        },
        {
          key: "facebook_ads_spend",
          type: "currency",
          header: "Facebook ads spend",
          width: 120,
          disabled: true,
          metricKey: "facebook_ads_spend",
        },
        {
          key: "facebook_ads_comments",
          type: "number",
          header: "Facebook ads comments",
          width: 120,
          disabled: true,
          metricKey: "facebook_ads_comments",
        },
        {
          key: "facebook_ads_shares",
          type: "number",
          header: "Facebook ads shares",
          width: 120,
          disabled: true,
        },
        {
          key: "facebook_ads_new_fans",
          type: "number",
          header: "Facebook ads new fans",
          width: 120,
          disabled: true,
        },
        {
          key: "facebook_ads_reactions",
          type: "number",
          header: "Facebook ads reactions",
          width: 120,
          disabled: true,
        },
        {
          key: "instagram_ads_brand_value",
          type: "currency",
          header: "Instagram ads Brand Value",
          width: 120,
          disabled: true,
        },
        {
          key: "instagram_ads_spend",
          type: "currency",
          header: "Instagram ads spend",
          width: 120,
          disabled: true,
        },
        {
          key: "instagram_ads_follows",
          type: "number",
          header: "Instagram ads follows",
          width: 120,
          disabled: true,
        },
        {
          key: "instagram_ads_comments",
          type: "number",
          header: "Instagram ads comments",
          width: 120,
          disabled: true,
        },
        {
          key: "instagram_ads_likes",
          type: "number",
          header: "Instagram ads likes",
          width: 120,
          disabled: true,
        },
        {
          key: "instagram_ads_shares",
          type: "number",
          header: "Instagram ads shares",
          width: 120,
          disabled: true,
        },
        {
          key: "tiktok_ads_spend",
          type: "currency",
          header: "TikTok ads spend",
          width: 120,
          disabled: true,
        },
        {
          key: "tiktok_ads_follows",
          type: "number",
          header: "TikTok ads follows",
          width: 120,
          disabled: true,
        },
        {
          key: "tiktok_ads_comments",
          type: "number",
          header: "TikTok ads comments",
          width: 120,
          disabled: true,
        },
        {
          key: "tiktok_ads_shares",
          type: "number",
          header: "TikTok ads shares",
          width: 120,
          disabled: true,
        },
        {
          key: "tiktok_ads_likes",
          type: "number",
          header: "TikTok ads likes",
          width: 120,
          disabled: true,
        },
        {
          key: "pinterest_ads_spend",
          type: "currency",
          header: "Pinterest ads spend",
          width: 120,
          disabled: true,
        },
        {
          key: "pinterest_ads_saves",
          type: "number",
          header: "Pinterest ads saves",
          width: 120,
          disabled: true,
        },
        {
          key: "objective",
          type: "title",
          header: "Objective",
          width: 100,
          disabled: true,
        },
        {
          key: "optimization_goal",
          type: "title",
          header: "Optimization Goal",
          width: 100,
          disabled: true,
        },
        {
          key: "budget_agg",
          type: "currency",
          header: "Budget",
          width: 85,
          disabled: true,
        },
      ],
      totalLabelColumn: "adset_name",
      compareUniqueKey: "adset_id",
      enablePagination: true,
      rowClickInDrawer: true,
      prefixEnabled: true,
      disableGranularity: true,
      highlightCells: true,
    },
    metadata: { defaultColSpan: 12, defaultRowSpan: 12 },
    requiredSources: [
      SOURCE_IDS.META,
      SOURCE_IDS.TIKTOK_ADS,
      SOURCE_IDS.PINTEREST_ADS,
    ],
  },
  brand_media_optimization_ads_table: {
    id: "brand_media_optimization_ads_table",
    name: "Brand Media Optimization Ads Table",
    description: "<TO BE ADDED>",
    type: "TableVisualization",
    props: {
      queryPrefix: "company_media_metrics_ads",
      sortBy: `brand_value_agg`,
      additionalFilters: [
        {
          value: "all_product_handles_str",
          label: "Product",
          type: "searchable-select",
        },
        {
          value: "campaign_id",
          label: "Campaign Id",
        },
        {
          value: "adset_id",
          label: "AdSet Id",
        },
      ],
      columnConfig: [
        // unused key "company_media_metrics_daily.campaignid",
        {
          key: "tags",
          type: "tags",
          header: "Tags",
          width: 160,
          taggableId: "ad_id",
          disableSorting: true,
          disableFiltering: true,
        },
        {
          key: "media_channel",
          type: "image",
          header: "Media Channel",
          classes: {
            head: "text-center",
            cell: "text-center",
          },
          width: 65,
        },
        {
          key: "ad_name",
          type: "title",
          header: "Name",
          render: (row: TMetric) => {
            const title = row["ad_name"]
            if (typeof title !== "string") {
              return ""
            }

            return <CaptionPreview text={title} exact={true} />
          },
          width: 350,
        },
        {
          key: "adset_name",
          type: "title",
          header: "Ad Set",
          render: (row: TMetric) => {
            const title = row["adset_name"]
            if (typeof title !== "string") {
              return ""
            }

            return <CaptionPreview text={title} exact={true} />
          },
          width: 350,
        },
        {
          key: "campaign_name",
          type: "title",
          header: "Campaign",
          render: (row: TMetric) => {
            const title = row["campaign_name"]
            if (typeof title !== "string") {
              return ""
            }

            return <CaptionPreview text={title} exact={true} />
          },
          width: 350,
        },
        {
          key: "campaign_status",
          type: "title",
          header: "Campaign Status",
          width: 100,
        },
        {
          key: "adset_status",
          type: "title",
          header: "Adset Status",
          width: 125,
        },
        {
          key: "ad_status",
          type: "title",
          header: "Ad Status",
          width: 125,
        },
        {
          key: "initiative_type",
          type: "title",
          header: "Initiative Type",
          width: 150,
        },
        {
          key: "spend",
          type: "currency",
          header: "Spend",
          width: 100,
        },
        {
          key: "brand_value_agg",
          type: "currency",
          isBrand: true,
          header: "180d Brand Value",
          width: 150,
        },
        {
          key: "brand_value_agg_range",
          type: "currency",
          isBrand: true,
          header: "180d Brand Value Range",
          disableSorting: true,
          disableFilter: true,
          width: 200,
          render: (row: TMetric) => (
            <div>
              {formatValue(
                (row?.lower_brand_value_agg as string | number) || "",
                "currency",
                undefined,
              )}
              {" - "}
              {formatValue(
                (row?.upper_brand_value_agg as string | number) || "",
                "currency",
                undefined,
              )}
            </div>
          ),
        },

        {
          key: "brand_value_over_spend",
          type: "coef",
          isBrand: true,
          header: "180d Brand ROAS",
          width: 150,
          highlightFn: highlightRoasCell,
        },
        {
          key: "purchases",
          type: "currency",
          header: "7d Click DR Revenue",
          width: 110,
        },
        {
          key: "short_term_revenue_roas",
          type: "coef",
          header: "7d Click DR ROAS",
          width: 110,
          highlightFn: highlightRoasCell,
        },
        {
          key: "modeled_revenue",
          type: "currency",
          isBrand: true,
          header: "Modeled Revenue",
          width: 150,
          disabled: true,
        },
        {
          key: "modeled_revenue_over_spend",
          type: "coef",
          isBrand: true,
          header: "Modeled ROAS",
          width: 150,
          highlightFn: highlightRoasCell,
          disabled: true,
        },
        {
          key: "estimated_d180_brand_value",
          type: "currency",
          isBrand: true,
          header: "Modeled Brand Value",
          width: 170,
          disabled: true,
        },
        {
          key: "modeled_brand_value_over_spend",
          type: "coef",
          isBrand: true,
          header: "Modeled bROAS",
          width: 150,
          highlightFn: highlightRoasCell,
          disabled: true,
        },
        {
          key: "revenue_from_search",
          type: "currency",
          header: "Revenue from Search",
          width: 130,
        },
        {
          key: "revenue_from_search_roas",
          type: "coef",
          header: "Revenue from Search ROAS",
          width: 130,
        },
        {
          key: "long_term_revenue",
          type: "currency",
          header: "Total Value",
          width: 130,
        },
        {
          key: "long_term_revenue_roas",
          type: "coef",
          header: "Total ROAS",
          width: 130,
          highlightFn: highlightRoasCell,
        },
        {
          key: "shares",
          header: "Shares",
          type: "number",
          width: 120,
        },
        {
          key: "likes",
          header: "Likes & Reactions",
          type: "number",
          width: 120,
        },
        {
          key: "follows",
          header: "Followers",
          type: "number",
          width: 120,
        },
        {
          key: "comments",
          header: "Comments",
          type: "number",
          width: 120,
        },
        {
          key: "saves",
          header: "Saves",
          type: "number",
          width: 120,
        },
        {
          key: "brand_value_over_spend_range",
          type: "coef",
          isBrand: true,
          header: "180d Brand ROAS Range",
          width: 200,
          disableSorting: true,
          disableFilter: true,
          render: (row: TMetric) => (
            <div>
              {formatValue(
                safeDivide(
                  row.lower_brand_value_agg as number,
                  row.spend as number,
                ) || 0.01,
                "coef",
                undefined,
              )}
              {" - "}
              {formatValue(
                safeDivide(
                  row.upper_brand_value_agg as number,
                  row.spend as number,
                ) || 0.01,
                "coef",
                undefined,
              )}
            </div>
          ),
        },
        {
          key: "d30_brand_value",
          type: "currency",
          // isBrand: true,
          header: "30d Brand Value",
          width: 150,
          isBrand: true,
          metricKey: "d30_brand_value",
        },
        {
          key: "d60_brand_value",
          type: "currency",
          // isBrand: true,
          header: "60d Brand Value",
          width: 150,
          isBrand: true,
          metricKey: "d60_brand_value",
        },
        {
          key: "d90_brand_value",
          type: "currency",
          // isBrand: true,
          header: "90d Brand Value",
          width: 150,
          isBrand: true,
          metricKey: "d90_brand_value",
        },
        {
          key: "facebook_ads_brand_value",
          type: "currency",
          header: "Facebook ads Brand Value",
          width: 120,
          disabled: true,
        },
        {
          key: "facebook_ads_spend",
          type: "currency",
          header: "Facebook ads spend",
          disabled: true,
          width: 120,
        },
        {
          key: "facebook_ads_comments",
          type: "number",
          header: "Facebook ads comments",
          disabled: true,
          width: 120,
        },
        {
          key: "facebook_ads_shares",
          type: "number",
          header: "Facebook ads shares",
          disabled: true,
          width: 120,
        },
        {
          key: "facebook_ads_new_fans",
          type: "number",
          header: "Facebook ads new fans",
          disabled: true,
          width: 120,
        },
        {
          key: "facebook_ads_reactions",
          type: "number",
          header: "Facebook ads reactions",
          disabled: true,
          width: 120,
        },
        {
          key: "instagram_ads_brand_value",
          type: "currency",
          header: "Instagram ads Brand Value",
          width: 120,
          disabled: true,
        },
        {
          key: "instagram_ads_spend",
          type: "currency",
          header: "Instagram ads spend",
          disabled: true,
          width: 120,
        },
        {
          key: "instagram_ads_follows",
          type: "number",
          header: "Instagram ads follows",
          disabled: true,
          width: 120,
        },
        {
          key: "instagram_ads_comments",
          type: "number",
          header: "Instagram ads comments",
          disabled: true,
          width: 120,
        },
        {
          key: "instagram_ads_likes",
          type: "number",
          header: "Instagram ads likes",
          disabled: true,
          width: 120,
        },
        {
          key: "instagram_ads_shares",
          type: "number",
          header: "Instagram ads shares",
          disabled: true,
          width: 120,
        },
        {
          key: "tiktok_ads_spend",
          type: "currency",
          header: "TikTok ads spend",
          disabled: true,
          width: 120,
        },
        {
          key: "tiktok_ads_follows",
          type: "number",
          header: "TikTok ads follows",
          disabled: true,
          width: 120,
        },
        {
          key: "tiktok_ads_comments",
          type: "number",
          header: "TikTok ads comments",
          disabled: true,
          width: 120,
        },
        {
          key: "tiktok_ads_shares",
          type: "number",
          header: "TikTok ads shares",
          disabled: true,
          width: 120,
        },
        {
          key: "tiktok_ads_likes",
          type: "number",
          header: "TikTok ads likes",
          disabled: true,
          width: 120,
        },
        {
          key: "pinterest_ads_spend",
          type: "currency",
          header: "Pinterest ads spend",
          width: 120,
          disabled: true,
        },
        {
          key: "pinterest_ads_saves",
          type: "number",
          header: "Pinterest ads saves",
          width: 120,
          disabled: true,
        },
        {
          key: "optimization_goal",
          type: "title",
          header: "Optimization Goal",
          disabled: true,
          width: 95,
        },
        {
          key: "budget_agg",
          type: "currency",
          header: "Budget",
          disabled: true,
          width: 85,
        },
        {
          key: "ad_id",
          type: "title",
          header: "Ad ID",
        },
      ],
      totalLabelColumn: "ad_name",
      compareUniqueKey: "ad_id",
      enablePagination: true,
      rowClickInDrawer: true,
      prefixEnabled: true,
      disableGranularity: true,
      highlightCells: true,
    },
    metadata: { defaultColSpan: 12, defaultRowSpan: 12 },
    requiredSources: [
      SOURCE_IDS.META,
      SOURCE_IDS.TIKTOK_ADS,
      SOURCE_IDS.PINTEREST_ADS,
    ],
  },
  brand_media_organic_social_posts_table: {
    id: "brand_media_organic_social_posts_table",
    name: "Brand Media Organic Social Posts Table",
    description: "<TO BE ADDED>",
    type: "TableVisualization",
    props: {
      queryPrefix: "company_media_metrics_organic_posts",
      sortBy: `company_media_metrics_organic_posts.brand_value_agg`,
      columnConfig: [
        // {
        //   key: "company_media_metrics_organic_posts.md_date",
        //   type: "date",
        //   header: "Date",
        //   width: 120,
        // },
        {
          key: "tags",
          type: "tags",
          header: "Tags",
          width: 160,
          taggableId: "company_media_metrics_organic_posts.post_id",
          disableSorting: true,
          disableFiltering: true,
        },
        {
          key: "company_media_metrics_organic_posts.media_channel",
          type: "image",
          header: "Media Channel",
          classes: {
            head: "text-center",
            cell: "flex items-center justify-center",
          },
          width: 85,
        },
        {
          key: "company_media_metrics_organic_posts.post_date",
          type: "date",
          header: "Post Date",
          width: 120,
        },
        {
          key: "company_media_metrics_organic_posts.post_preview",
          type: "image",
          header: "Post Preview",
          classes: {
            head: "text-center",
            cell: "flex items-center justify-center",
          },
          render: (row: TMetric) => {
            const previewLink = row[
              "company_media_metrics_organic_posts.post_preview"
            ] as string
            const postCaption = row[
              "company_media_metrics_organic_posts.post_caption"
            ] as string
            const postId = row[
              "company_media_metrics_organic_posts.post_id"
            ] as string
            const mediaChannel = row[
              "company_media_metrics_organic_posts.media_channel"
            ] as string

            // The chart's postProcess pre-populated the asset-cache slice for
            // every visible row — MarathonImage finds a cached URL on mount
            // and won't fire its own resolve.
            if (postId && mediaChannel) {
              return (
                <MarathonImage
                  sourceType={mapMediaChannelToSourceType(mediaChannel, true)}
                  externalRefId={postId}
                  url={previewLink}
                  useThumbnail
                  alt={postCaption}
                  size={160}
                  className="aspect-square"
                />
              )
            }

            return (
              <Image
                classes={{
                  image: "w-[160px] h-[160px]",
                  wrapper: "aspect-square overflow-hidden",
                }}
                src={previewLink}
                fallback={previewLink}
                alt={postCaption}
              />
            )
          },
        },
        {
          key: "company_media_metrics_organic_posts.post_caption",
          type: "text",
          render: (row: TMetric) => {
            const caption =
              row["company_media_metrics_organic_posts.post_caption"]
            const postLink = row[
              "company_media_metrics_organic_posts.post_link"
            ] as string | undefined
            if (typeof caption !== "string") {
              return ""
            }

            return (
              <CaptionPreview
                text={caption}
                link={postLink}
                alwaysShow={true}
                exact={true}
                fullCaptionClass="max-w-200 word-break break-words"
                align="left"
              />
            )
          },
          header: "Post Caption",
          width: 300,
        },
        {
          key: "company_media_metrics_organic_posts.brand_value_agg",
          type: "currency",
          isBrand: true,
          header: "180d Brand Value",
          width: 150,
        },
        {
          key: "brand_value_agg_range",
          type: "currency",
          isBrand: true,
          header: "180d Brand Value Range",
          disableSorting: true,
          disableFilter: true,
          width: 200,
          render: (row: TMetric) => (
            <div>
              {formatValue(
                (row?.[
                  "company_media_metrics_organic_posts.lower_brand_value_agg"
                ] as string | number) || "",
                "currency",
                undefined,
              )}
              {" - "}
              {formatValue(
                (row?.[
                  "company_media_metrics_organic_posts.upper_brand_value_agg"
                ] as string | number) || "",
                "currency",
                undefined,
              )}
            </div>
          ),
        },
        {
          key: "company_media_metrics_organic_posts.reach",
          type: "number",
          header: "Reach",
          width: 100,
        },
        {
          key: "company_media_metrics_organic_posts.brand_value_reach",
          isBrand: true,
          type: "currency",
          header: "Brand Value / 1000 Reaches",
          width: 180,
        },
        {
          key: "company_media_metrics_organic_posts.likes",
          header: "Likes & Reactions",
          type: "number",
          width: 180,
        },
        {
          key: "company_media_metrics_organic_posts.likes_reach",
          header: "Likes / 1000 Reaches",
          type: "coef",
          width: 180,
        },
        {
          key: "company_media_metrics_organic_posts.comments",
          header: "Comments",
          type: "number",
          width: 130,
        },
        {
          key: "company_media_metrics_organic_posts.comments_reach",
          header: "Comments / 1000 Reaches",
          type: "coef",
          width: 180,
        },
        {
          key: "company_media_metrics_organic_posts.shares",
          header: "Shares",
          type: "number",
          width: 100,
        },
        {
          key: "company_media_metrics_organic_posts.shares_reach",
          header: "Shares / 1000 Reaches",
          type: "coef",
          width: 110,
        },
        {
          key: "company_media_metrics_organic_posts.saves",
          header: "Saves",
          type: "number",
          width: 100,
        },
        {
          key: "company_media_metrics_organic_posts.saves_reach",
          header: "Saves /1000 Reaches",
          type: "coef",
          width: 100,
        },
        {
          key: "company_media_metrics_organic_posts.follows",
          header: "Follows",
          type: "number",
          width: 110,
        },
      ],
      totalLabelColumn: "company_media_metrics_organic_posts.post_caption",
      enablePagination: true,
      compareUniqueKey: "company_media_metrics_organic_posts.post_id",
      disableGranularity: true,
      rowClickInDrawer: true,
      highlightCells: true,
    },
    metadata: { defaultColSpan: 12, defaultRowSpan: 12 },
    requiredSources: [
      SOURCE_IDS.FACEBOOK,
      SOURCE_IDS.INSTAGRAM,
      SOURCE_IDS.TIKTOK,
    ],
  },
  brand_media_media_mentions_table: {
    id: "brand_media_media_mentions_posts_table",
    name: "Brand Media Mentions Table",
    description: "<TO BE ADDED>",
    type: "TableVisualization",
    props: {
      queryPrefix: "company_media_metrics_mentions",
      sortBy: `company_media_metrics_mentions.brand_value_agg`,
      columnConfig: [
        {
          key: "company_media_metrics_mentions.md_date",
          extraSorting: {
            id: "company_media_metrics_mentions.post_id",
            desc: true,
          },
          type: "date",
          header: "Date",
          minSize: 100,
          percentSize: "10%",
        },
        {
          key: "company_media_metrics_mentions.media_channel",
          type: "image",
          header: "Media Channel",
          classes: {
            head: "text-center",
            cell: "flex items-center justify-center",
          },
          minSize: 85,
          percentSize: "7.5%",
        },
        {
          key: "company_media_metrics_mentions.post_caption",
          type: "text",
          render: (row: TMetric) => {
            const caption = row["company_media_metrics_mentions.post_caption"]
            const postLink = row["company_media_metrics_mentions.post_link"] as
              | string
              | undefined
            if (typeof caption !== "string") {
              return ""
            }

            return (
              <CaptionPreview
                text={caption}
                link={postLink}
                alwaysShow={true}
                exact={true}
                fullCaptionClass="max-w-200 word-break break-words"
                align="left"
              />
            )
          },
          header: "Post Caption",
          minSize: 200,
          percentSize: "20%",
        },
        {
          key: "company_media_metrics_mentions.display_url",
          type: "image",
          header: "Post Preview",
          render: (row: TMetric) => {
            const imageUrl = row[
              "company_media_metrics_mentions.display_url"
            ] as string
            const creatorName = row[
              "company_media_metrics_mentions.creator_name"
            ] as string
            const postId = row[
              "company_media_metrics_mentions.post_id"
            ] as string
            const mediaChannel = row[
              "company_media_metrics_mentions.media_channel"
            ] as string

            const imageClasses = {
              image: "w-full h-full max-w-60 max-h-60",
              wrapper: "aspect-square overflow-hidden rounded-xl",
            }

            // The chart's postProcess pre-populated the asset-cache slice for
            // every visible row — MarathonImage finds a cached URL on mount
            // and won't fire its own resolve.
            if (postId && mediaChannel) {
              return (
                <MarathonImage
                  sourceType={mapMediaChannelToSourceType(mediaChannel, true)}
                  externalRefId={postId}
                  url={imageUrl}
                  useThumbnail
                  alt={creatorName}
                  className="aspect-square rounded-xl max-w-60 max-h-60"
                />
              )
            }

            return (
              <Image classes={imageClasses} src={imageUrl} alt={creatorName} />
            )
          },
          minSize: 200,
          percentSize: "12.5%",
        },
        {
          key: "company_media_metrics_mentions.creator_name",
          type: "title",
          header: "Creator",
          minSize: 150,
          percentSize: "10%",
        },
        {
          key: "company_media_metrics_mentions.brand_value_agg",
          type: "currency",
          isBrand: true,
          header: "180d Brand Value",
          minSize: 120,
          percentSize: "10%",
        },
        {
          key: "company_media_metrics_mentions.reach",
          type: "number",
          header: "Reach",
          disabled: true,
          width: 100,
        },
        {
          key: "company_media_metrics_mentions.likes",
          header: "Likes & Reactions",
          type: "number",
          minSize: 100,
          percentSize: "10%",
        },
        {
          key: "company_media_metrics_mentions.comments",
          header: "Comments",
          type: "number",
          minSize: 130,
          percentSize: "10%",
        },
      ],
      totalLabelColumn: "company_media_metrics_mentions.post_caption",
      enablePagination: true,
      compareUniqueKey: "company_media_metrics_mentions.post_id",
      disableGranularity: true,
      rowClickInDrawer: true,
      highlightCells: true,
    },
    metadata: { defaultColSpan: 12, defaultRowSpan: 12 },
  },
  brand_value_cohort_table: {
    id: "brand_value_cohort_table",
    name: "Brand Value Cohort Table",
    description: "___MISSING___",
    type: "CohortVisualization",
    props: {
      dataId: "query_cohort_analysis",
      filterId: "cohort-metric",
      dataAggTypeOnly: "MONTHLY",
      columnConfig: [
        {
          key: "company_modeled_metrics_monthly.engagement_date.month",
          type: "date",
          header: "Cohort Date",
        },
        {
          key: "company_modeled_metrics_monthly.engagements",
          type: "number",
          header: "Cohort Size",
        },
        {
          key: "company_modeled_metrics_monthly.conversionMonth1",
          type: "currency",
          header: "Month 1",
        },
        {
          key: "company_modeled_metrics_monthly.conversionMonth2",
          type: "currency",
          header: "Month 2",
        },
        {
          key: "company_modeled_metrics_monthly.conversionMonth3",
          type: "currency",
          header: "Month 3",
        },
        {
          key: "company_modeled_metrics_monthly.conversionMonth4",
          type: "currency",
          header: "Month 4",
        },
        {
          key: "company_modeled_metrics_monthly.conversionMonth5",
          type: "currency",
          header: "Month 5",
        },
        {
          key: "company_modeled_metrics_monthly.conversionMonth6",
          type: "currency",
          header: "Month 6",
        },
        // {
        //   key: "company_modeled_metrics_monthly.conversionMonth7",
        //   type: "currency",
        //   header: "Month 7",
        // },
        // {
        //   key: "company_modeled_metrics_monthly.conversionMonth8",
        //   type: "currency",
        //   header: "Month 8",
        // },
        // {
        //   key: "company_modeled_metrics_monthly.conversionMonth9",
        //   type: "currency",
        //   header: "Month 9",
        // },
        // {
        //   key: "company_modeled_metrics_monthly.conversionMonth10",
        //   type: "currency",
        //   header: "Month 10",
        // },
        // {
        //   key: "company_modeled_metrics_monthly.conversionMonth11",
        //   type: "currency",
        //   header: "Month 11",
        // },
        // {
        //   key: "company_modeled_metrics_monthly.conversionMonth12",
        //   type: "currency",
        //   header: "Month 12",
        // },
      ],
      compareUniqueKey: "id",
      forecastCellColor: "#D6D6D6",
      forecastLabelText: "Forecasted Incremental Owned & Organic Revenue",
      realizedCellColor: "#BBDDFF",
      realizedLabelText: "Realized Incremental Owned & Organic Revenue",
      disableGranularity: false,
    },
  },
  brand_value_by_channel_group_chart: {
    id: "brand_value_by_channel_group_chart",
    name: "Brand Value by Channel Group Chart",
    description: "Brand Value by Channel Group.",
    type: "BarChart",
    props: {
      comparisonEnabled: true,
      bottomLegend: true,
      config: [
        {
          label: "Org Social",
          dataKey:
            "company_platform_brand_value.organic_channel_group_brand_value",
          color: "#66DECD",
          type: "currency",
          formatTooltip: (metrics: Record<string, number>) => (
            <div className="flex gap-system-xxs items-center">
              <span className="font-bold leading-none">
                {formatValue(
                  metrics[
                    "company_platform_brand_value.organic_channel_group_brand_value_coef"
                  ],
                  "percent",
                )}
              </span>{" "}
              <span className="text-sm leading-none">
                (
                {formatValue(
                  metrics[
                    "company_platform_brand_value.organic_channel_group_brand_value"
                  ],
                  "currency",
                )}
                )
              </span>
            </div>
          ),
        },
        {
          label: "Paid Social",
          dataKey: "company_platform_brand_value.ad_channel_group_brand_value",
          color: "#EE8434",
          type: "currency",
          formatTooltip: (metrics: Record<string, number>) => (
            <div className="flex gap-system-xxs items-center">
              <span className="font-bold leading-none">
                {formatValue(
                  metrics[
                    "company_platform_brand_value.ad_channel_group_brand_value_coef"
                  ],
                  "percent",
                )}
              </span>{" "}
              <span className="text-sm leading-none">
                (
                {formatValue(
                  metrics[
                    "company_platform_brand_value.ad_channel_group_brand_value"
                  ],
                  "currency",
                )}
                )
              </span>
            </div>
          ),
        },
        // {
        //   label: "Search",
        //   dataKey:
        //     "company_platform_brand_value.search_channel_group_brand_value",
        //   color: "#764CEF",
        //   type: "currency",
        //   formatTooltip: (metrics: Record<string, number>) => (
        //     <div className="flex gap-system-xxs items-center">
        //       <span className="font-bold leading-none">
        //         {formatValue(
        //           metrics[
        //             "company_platform_brand_value.search_channel_group_brand_value_coef"
        //           ],
        //           "percent",
        //         )}
        //       </span>{" "}
        //       <span className="text-sm leading-none">
        //         (
        //         {formatValue(
        //           metrics[
        //             "company_platform_brand_value.search_channel_group_brand_value"
        //           ],
        //           "currency",
        //         )}
        //         )
        //       </span>
        //     </div>
        //   ),
        // },
        {
          label: "Earned Media",
          dataKey:
            "company_platform_brand_value.mentions_channel_group_brand_value",
          color: "#F4D148",
          type: "currency",
          formatTooltip: (metrics: Record<string, number>) => (
            <div className="flex gap-system-xxs items-center">
              <span className="font-bold leading-none">
                {formatValue(
                  metrics[
                    "company_platform_brand_value.mentions_channel_group_brand_value_coef"
                  ],
                  "percent",
                )}
              </span>{" "}
              <span className="text-sm leading-none">
                (
                {formatValue(
                  metrics[
                    "company_platform_brand_value.mentions_channel_group_brand_value"
                  ],
                  "currency",
                )}
                )
              </span>
            </div>
          ),
        },
        {
          label: "Subs",
          dataKey:
            "company_platform_brand_value.subscription_channel_group_brand_value",
          color: "#218A00",
          type: "currency",
          formatTooltip: (metrics: Record<string, number>) => (
            <div className="flex gap-system-xxs items-center">
              <span className="font-bold leading-none">
                {formatValue(
                  metrics[
                    "company_platform_brand_value.subscription_channel_group_brand_value_coef"
                  ],
                  "percent",
                )}
              </span>{" "}
              <span className="text-sm leading-none">
                (
                {formatValue(
                  metrics[
                    "company_platform_brand_value.subscription_channel_group_brand_value"
                  ],
                  "currency",
                )}
                )
              </span>
            </div>
          ),
        },
        {
          label: "Unattributed",
          dataKey:
            "company_platform_brand_value.unattributed_channel_group_brand_value",
          color: "#0866FF",
          type: "currency",
          formatTooltip: (metrics: Record<string, number>) => (
            <div className="flex gap-system-xxs items-center">
              <span className="font-bold leading-none">
                {formatValue(
                  metrics[
                    "company_platform_brand_value.unattributed_channel_group_brand_value_coef"
                  ],
                  "percent",
                )}
              </span>{" "}
              <span className="text-sm leading-none">
                (
                {formatValue(
                  metrics[
                    "company_platform_brand_value.unattributed_channel_group_brand_value"
                  ],
                  "currency",
                )}
                )
              </span>
            </div>
          ),
        },
      ],
    },
  },
  brand_value_by_payout_curve_chart: {
    id: "brand_value_by_payout_curve_chart",
    name: "Brand Value Payout Curve",
    description: "Brand Value Payout Curve.",
    type: "BarChart",
    props: {
      comparisonEnabled: true,
      bottomLegend: true,
      config: [
        {
          label: "30d",
          dataKey: "metrics.d30_brand_value",
          color: "#002871",
          type: "currency",
          formatTooltip: (metrics: Record<string, number>) => (
            <div className="flex gap-system-xxs items-center">
              <span className="font-bold leading-none">
                {formatValue(
                  metrics["metrics.d30_brand_value_coef"],
                  "percent",
                )}
              </span>{" "}
              <span className="text-sm leading-none">
                ({formatValue(metrics["metrics.d30_brand_value"], "currency")})
              </span>
            </div>
          ),
        },
        {
          label: "60d",
          dataKey: "metrics.d60_brand_value",
          color: "#0F3882",
          type: "currency",
          formatTooltip: (metrics: Record<string, number>) => (
            <div className="flex gap-system-xxs items-center">
              <span className="font-bold leading-none">
                {formatValue(
                  metrics["metrics.d60_brand_value_coef"],
                  "percent",
                )}
              </span>{" "}
              <span className="text-sm leading-none">
                ({formatValue(metrics["metrics.d60_brand_value"], "currency")})
              </span>
            </div>
          ),
        },
        {
          label: "90d",
          dataKey: "metrics.d90_brand_value",
          color: "#214993",
          type: "currency",
          formatTooltip: (metrics: Record<string, number>) => (
            <div className="flex gap-system-xxs items-center">
              <span className="font-bold leading-none">
                {formatValue(
                  metrics["metrics.d90_brand_value_coef"],
                  "percent",
                )}
              </span>{" "}
              <span className="text-sm leading-none">
                ({formatValue(metrics["metrics.d90_brand_value"], "currency")})
              </span>
            </div>
          ),
        },
        {
          label: "120d",
          dataKey: "metrics.d120_brand_value",
          color: "#4067B1",
          type: "currency",
          formatTooltip: (metrics: Record<string, number>) => (
            <div className="flex gap-system-xxs items-center">
              <span className="font-bold leading-none">
                {formatValue(
                  metrics["metrics.d120_brand_value_coef"],
                  "percent",
                )}
              </span>{" "}
              <span className="text-sm leading-none">
                ({formatValue(metrics["metrics.d120_brand_value"], "currency")})
              </span>
            </div>
          ),
        },
        {
          label: "150d",
          dataKey: "metrics.d150_brand_value",
          color: "#4067B1",
          type: "currency",
          formatTooltip: (metrics: Record<string, number>) => (
            <div className="flex gap-system-xxs items-center">
              <span className="font-bold leading-none">
                {formatValue(
                  metrics["metrics.d150_brand_value_coef"],
                  "percent",
                )}
              </span>{" "}
              <span className="text-sm leading-none">
                ({formatValue(metrics["metrics.d150_brand_value"], "currency")})
              </span>
            </div>
          ),
        },
        {
          label: "180d",
          dataKey: "metrics.d180_brand_value",
          color: "#6387C8",
          type: "currency",
          formatTooltip: (metrics: Record<string, number>) => (
            <div className="flex gap-system-xxs items-center">
              <span className="font-bold leading-none">
                {formatValue(
                  metrics["metrics.d180_brand_value_coef"],
                  "percent",
                )}
              </span>{" "}
              <span className="text-sm leading-none">
                ({formatValue(metrics["metrics.d180_brand_value"], "currency")})
              </span>
            </div>
          ),
        },
      ],
    },
  },
  brand_value_stacked_chart: {
    id: "brand_value_stacked_chart",
    name: "Brand Value Chart",
    description:
      "Brand Value represents the next-180-day baseline revenue of your brand engagements. Think of this as the next 180 day cohort value of the new engagements you created in any given time period as measured in growth in baseline revenue.",
    type: "ComposedChart",
    props: {
      // title: "180d Brand Value from Direct Engagements",
      hideTotal: true,
      comparisonEnabled: true,
      isInverted: true,
      hideTooltipTitle: true,
      bottomLegend: true,
      groups: [
        {
          label: "Brand Value",
          value: "brand-value",
        },
        {
          label: "Revenue",
          value: "revenue",
        },
        // {
        //   label: "Channel Group Brand Value",
        //   value: "channel-group-brand-value",
        // },
        // {
        //   label: "Platform Brand Value",
        //   value: "platform-brand-value",
        // },
        {
          label: "Platform Engagements",
          value: "platform-engagements",
        },
        {
          label: "Traffic",
          value: "traffic",
        },
      ],
      graphs: [
        // Brand Value
        {
          type: "area",
          label: "30d Brand Value",
          numberType: "currency",
          dataKey: "metrics.d30_brand_value",
          group: "brand-value",
          isBrand: true,
          yAxisId: "cur",
        },
        {
          type: "area",
          label: "60d Brand Value",
          numberType: "currency",
          dataKey: "metrics.d60_brand_value",
          group: "brand-value",
          isBrand: true,
          yAxisId: "cur",
        },
        {
          type: "area",
          label: "90d Brand Value",
          numberType: "currency",
          dataKey: "metrics.d90_brand_value",
          group: "brand-value",
          isBrand: true,
          yAxisId: "cur",
        },
        {
          type: "area",
          label: "120d Brand Value",
          numberType: "currency",
          dataKey: "metrics.d120_brand_value",
          group: "brand-value",
          isBrand: true,
          yAxisId: "cur",
        },
        {
          type: "area",
          label: "150d Brand Value",
          numberType: "currency",
          dataKey: "metrics.d150_brand_value",
          group: "brand-value",
          isBrand: true,
          yAxisId: "cur",
        },
        {
          type: "area",
          label: "180d Brand Value",
          numberType: "currency",
          dataKey: "metrics.d180_brand_value",
          group: "brand-value",
          isBrand: true,
          yAxisId: "cur",
        },
        {
          type: "area",
          label: "Brand Factor",
          dataKey: "metrics.brand_factor",
          group: "brand-value",
          isBrand: true,
          yAxisId: "eng",
        },
        {
          type: "area",
          label: "Monetization Factor",
          dataKey: "metrics.monetization_factor",
          group: "brand-value",
          isBrand: true,
          yAxisId: "mon-factor",
        },
        {
          type: "area",
          label: "Unattributed Branded Searches",
          dataKey:
            "metrics.google_search_organic_branded_search_impressions_unattributed",
          group: "revenue",
          isBrand: true,
          yAxisId: "mon-factor",
        },

        // Revenue - GA Metrics (conditional based on multi-property setting)
        {
          type: "area",
          label: "GA Baseline Revenue",
          dataKey: "metrics.ga_baseline_revenue",
          numberType: "currency",
          group: "revenue",
          isBrand: true,
          yAxisId: "cur",
          isEnabled: ({
            isGaMultiPropertyEnabled,
          }: {
            isGaMultiPropertyEnabled?: boolean
          }) => !isGaMultiPropertyEnabled,
        },
        {
          type: "area",
          label: "GA Baseline Revenue",
          dataKey: "metrics.ga_multi_property_baseline_revenue",
          numberType: "currency",
          group: "revenue",
          isBrand: true,
          yAxisId: "cur",
          isEnabled: ({
            isGaMultiPropertyEnabled,
          }: {
            isGaMultiPropertyEnabled?: boolean
          }) => isGaMultiPropertyEnabled === true,
        },
        {
          type: "area",
          label: "GA Paid Revenue",
          dataKey: "metrics.ga_paid_revenue",
          numberType: "currency",
          group: "revenue",
          isBrand: true,
          yAxisId: "cur",
          isEnabled: ({
            isGaMultiPropertyEnabled,
          }: {
            isGaMultiPropertyEnabled?: boolean
          }) => !isGaMultiPropertyEnabled,
        },
        {
          type: "area",
          label: "GA Paid Revenue",
          dataKey: "metrics.ga_multi_property_paid_revenue",
          numberType: "currency",
          group: "revenue",
          isBrand: true,
          yAxisId: "cur",
          isEnabled: ({
            isGaMultiPropertyEnabled,
          }: {
            isGaMultiPropertyEnabled?: boolean
          }) => isGaMultiPropertyEnabled === true,
        },
        {
          type: "area",
          label: "GA Triggered Revenue",
          dataKey: "metrics.ga_triggered_revenue",
          numberType: "currency",
          group: "revenue",
          isBrand: true,
          yAxisId: "cur",
          isEnabled: ({
            isGaMultiPropertyEnabled,
          }: {
            isGaMultiPropertyEnabled?: boolean
          }) => !isGaMultiPropertyEnabled,
        },
        {
          type: "area",
          label: "GA Triggered Revenue",
          dataKey: "metrics.ga_multi_property_triggered_revenue",
          numberType: "currency",
          group: "revenue",
          isBrand: true,
          yAxisId: "cur",
          isEnabled: ({
            isGaMultiPropertyEnabled,
          }: {
            isGaMultiPropertyEnabled?: boolean
          }) => isGaMultiPropertyEnabled === true,
        },
        {
          type: "area",
          label: "GA Baseline + Paid Brand Search Revenue",
          dataKey: "metrics.ga_revenue_baseline_plus_paid_brand_search",
          numberType: "currency",
          group: "revenue",
          isBrand: true,
          yAxisId: "cur",
          isEnabled: ({
            depVar,
            isGaMultiPropertyEnabled,
          }: {
            depVar: string
            isGaMultiPropertyEnabled?: boolean
          }) =>
            [
              DEP_VAR_SOURCES[CONNECTORS_IDS.GOOGLE_ANALYTICS_4].ALT,
              DEP_VAR_SOURCES[CONNECTORS_IDS.GOOGLE_ANALYTICS_4].MAIN,
            ].includes(depVar) && !isGaMultiPropertyEnabled,
        },
        {
          type: "area",
          label: "GA Multi-Property Baseline + Paid Brand Search Revenue",
          dataKey:
            "metrics.ga_multi_property_revenue_baseline_plus_paid_brand_search",
          numberType: "currency",
          group: "revenue",
          isBrand: true,
          yAxisId: "cur",
          isEnabled: ({
            depVar,
            amazonEnabled,
            isGaMultiPropertyEnabled,
          }: {
            depVar: string
            amazonEnabled: boolean
            isGaMultiPropertyEnabled?: boolean
          }) =>
            [
              DEP_VAR_SOURCES[CONNECTORS_IDS.GOOGLE_ANALYTICS_4].ALT,
              DEP_VAR_SOURCES[CONNECTORS_IDS.GOOGLE_ANALYTICS_4].MAIN,
            ].includes(depVar) &&
            !amazonEnabled &&
            isGaMultiPropertyEnabled === true,
        },
        {
          type: "area",
          label: "GA Baseline + Amazon Owned + Paid Brand Search Revenue",
          dataKey: "metrics.ga_amazon_baseline_revenue_plus_paid_brand_search",
          numberType: "currency",
          group: "revenue",
          isBrand: true,
          yAxisId: "cur",
          isEnabled: ({
            depVar,
            amazonEnabled,
            isGaMultiPropertyEnabled,
          }: {
            depVar: string
            amazonEnabled: boolean
            isGaMultiPropertyEnabled?: boolean
          }) =>
            [
              DEP_VAR_SOURCES[CONNECTORS_IDS.GOOGLE_ANALYTICS_4].ALT,
              DEP_VAR_SOURCES[CONNECTORS_IDS.GOOGLE_ANALYTICS_4].MAIN,
            ].includes(depVar) &&
            amazonEnabled &&
            !isGaMultiPropertyEnabled,
        },
        {
          type: "area",
          label:
            "GA Multi-Property Baseline + Paid Brand Search Revenue + Amazon Owned",
          dataKey:
            "metrics.ga_multi_property_amazon_baseline_revenue_plus_paid_brand_search",
          numberType: "currency",
          group: "revenue",
          isBrand: true,
          yAxisId: "cur",
          isEnabled: ({
            depVar,
            amazonEnabled,
            isGaMultiPropertyEnabled,
          }: {
            depVar: string
            amazonEnabled: boolean
            isGaMultiPropertyEnabled?: boolean
          }) =>
            [
              DEP_VAR_SOURCES[CONNECTORS_IDS.GOOGLE_ANALYTICS_4].ALT,
              DEP_VAR_SOURCES[CONNECTORS_IDS.GOOGLE_ANALYTICS_4].MAIN,
            ].includes(depVar) &&
            amazonEnabled &&
            isGaMultiPropertyEnabled === true,
        },
        {
          type: "area",
          label: "GA Total Revenue",
          dataKey: "metrics.ga_total_revenue_from_all_sessions",
          numberType: "currency",
          group: "revenue",
          isBrand: true,
          yAxisId: "cur",
          isEnabled: ({
            isGaMultiPropertyEnabled,
          }: {
            isGaMultiPropertyEnabled?: boolean
          }) => !isGaMultiPropertyEnabled,
        },
        {
          type: "area",
          label: "GA Total Revenue",
          dataKey: "metrics.ga_multi_property_total_revenue_from_all_sessions",
          numberType: "currency",
          group: "revenue",
          isBrand: true,
          yAxisId: "cur",
          isEnabled: ({
            isGaMultiPropertyEnabled,
          }: {
            isGaMultiPropertyEnabled?: boolean
          }) => isGaMultiPropertyEnabled === true,
        },
        {
          type: "area",
          label: "Shopify Baseline Revenue",
          dataKey: "metrics.shopify_baseline_revenue",
          numberType: "currency",
          group: "revenue",
          isBrand: true,
          yAxisId: "cur",
        },
        {
          type: "area",
          label: "Shopify Paid Revenue",
          dataKey: "metrics.shopify_paid_revenue",
          numberType: "currency",
          group: "revenue",
          isBrand: true,
          yAxisId: "cur",
        },
        {
          type: "area",
          label: "Shopify Triggered Revenue",
          dataKey: "metrics.shopify_triggered_revenue",
          numberType: "currency",
          group: "revenue",
          isBrand: true,
          yAxisId: "cur",
        },
        {
          type: "area",
          label: "Shopify Subscription Revenue",
          dataKey: "metrics.shopify_subscription_revenue",
          numberType: "currency",
          group: "revenue",
          isBrand: true,
          yAxisId: "cur",
        },
        {
          type: "area",
          label: "Shopify Baseline + Paid Brand Search Revenue",
          dataKey: "metrics.shopify_baseline_plus_paid_brand_search_revenue",
          numberType: "currency",
          group: "revenue",
          isBrand: true,
          yAxisId: "cur",
          isEnabled: ({ depVar }: { depVar: string }) =>
            [DEP_VAR_SOURCES[CONNECTORS_IDS.SHOPIFY].MAIN].includes(depVar),
        },
        {
          type: "area",
          label: "Shopify Baseline + Subscription + Paid Brand Search Revenue",
          dataKey:
            "metrics.shopify_baseline_plus_subscription_plus_paid_brand_search_revenue",
          numberType: "currency",
          group: "revenue",
          isBrand: true,
          yAxisId: "cur",
          isEnabled: ({ depVar }: { depVar: string }) =>
            [DEP_VAR_SOURCES[CONNECTORS_IDS.SHOPIFY].ALT].includes(depVar),
        },
        {
          type: "area",
          label: "Shopify Baseline + Paid Brand Search + Amazon Owned Revenue",
          dataKey: "metrics.shopify_amazon_baseline_revenue",
          numberType: "currency",
          group: "revenue",
          isBrand: true,
          yAxisId: "cur",
          isEnabled: ({
            depVar,
            amazonEnabled,
          }: {
            depVar: string
            amazonEnabled: boolean
          }) =>
            [DEP_VAR_SOURCES[CONNECTORS_IDS.SHOPIFY].MAIN].includes(depVar) &&
            amazonEnabled,
        },
        {
          type: "area",
          label:
            "Shopify & Subscription Baseline + Paid Brand Search + Amazon Owned Revenue",
          dataKey: "metrics.shopify_plus_subscription_amazon_baseline_revenue",
          numberType: "currency",
          group: "revenue",
          isBrand: true,
          yAxisId: "cur",
          isEnabled: ({
            depVar,
            amazonEnabled,
          }: {
            depVar: string
            amazonEnabled: boolean
          }) =>
            [
              DEP_VAR_SOURCES[CONNECTORS_IDS.SHOPIFY].ALT,
              // DEP_VAR_SOURCES[CONNECTORS_IDS.SHOPIFY].MAIN,
            ].includes(depVar) && amazonEnabled,
        },
        {
          type: "area",
          label: "Shopify Total Revenue",
          dataKey: "metrics.shopify_total_customer_revenue",
          numberType: "currency",
          group: "revenue",
          isBrand: true,
          yAxisId: "cur",
        },
        {
          type: "area",
          label: "Shopify Total Web Revenue",
          dataKey: "metrics.shopify_total_traffic_source_revenue",
          numberType: "currency",
          group: "revenue",
          isBrand: true,
          yAxisId: "cur",
        },
        {
          type: "area",
          label: "Shopify Total New Customer Revenue",
          dataKey: "metrics.shopify_total_new_customer_revenue",
          numberType: "currency",
          group: "revenue",
          isBrand: true,
          yAxisId: "cur",
        },
        {
          type: "area",
          label: "Shopify Total Repeat Customer Revenue",
          dataKey: "metrics.shopify_total_repeat_customer_revenue",
          numberType: "currency",
          group: "revenue",
          isBrand: true,
          yAxisId: "cur",
        },
        {
          type: "area",
          label: "Shopify DTC Ecomm New Customer Revenue",
          dataKey: "metrics.shopify_ecomm_new_customer_revenue",
          numberType: "currency",
          group: "revenue",
          isBrand: true,
          yAxisId: "cur",
        },
        {
          type: "area",
          label: "Shopify DTC Ecomm Repeat Customer Revenue",
          dataKey: "metrics.shopify_ecomm_repeat_customer_revenue",
          numberType: "currency",
          group: "revenue",
          isBrand: true,
          yAxisId: "cur",
        },
        {
          type: "area",
          label: "Shopify DTC Ecomm Total Customer Revenue",
          dataKey: "metrics.shopify_ecomm_total_customer_revenue",
          numberType: "currency",
          group: "revenue",
          isBrand: true,
          yAxisId: "cur",
        },
        {
          type: "area",
          label: "Shopify DTC Retail New Customer Revenue",
          dataKey: "metrics.shopify_dtc_retail_new_customer_revenue",
          numberType: "currency",
          group: "revenue",
          isBrand: true,
          yAxisId: "cur",
        },
        {
          type: "area",
          label: "Shopify DTC Retail Repeat Customer Revenue",
          dataKey: "metrics.shopify_dtc_retail_repeat_customer_revenue",
          numberType: "currency",
          group: "revenue",
          isBrand: true,
          yAxisId: "cur",
        },
        {
          type: "area",
          label: "Shopify DTC Retail Total Customer Revenue",
          dataKey: "metrics.shopify_dtc_retail_total_customer_revenue",
          numberType: "currency",
          group: "revenue",
          isBrand: true,
          yAxisId: "cur",
        },
        {
          type: "area",
          label: "Shopify Third Party Ecomm New Customer Revenue",
          dataKey: "metrics.shopify_third_party_ecomm_new_customer_revenue",
          numberType: "currency",
          group: "revenue",
          isBrand: true,
          yAxisId: "cur",
        },
        {
          type: "area",
          label: "Shopify Third Party Ecomm Repeat Customer Revenue",
          dataKey: "metrics.shopify_third_party_ecomm_repeat_customer_revenue",
          numberType: "currency",
          group: "revenue",
          isBrand: true,
          yAxisId: "cur",
        },
        {
          type: "area",
          label: "Shopify Third Party Ecomm Total Customer Revenue",
          dataKey: "metrics.shopify_third_party_ecomm_total_customer_revenue",
          numberType: "currency",
          group: "revenue",
          isBrand: true,
          yAxisId: "cur",
        },
        {
          type: "area",
          label: "Shopify Subscription New Customer Revenue",
          dataKey: "metrics.shopify_subscription_new_customer_revenue",
          numberType: "currency",
          group: "revenue",
          isBrand: true,
          yAxisId: "cur",
        },
        {
          type: "area",
          label: "Shopify Subscription Repeat Customer Revenue",
          dataKey: "metrics.shopify_subscription_repeat_customer_revenue",
          numberType: "currency",
          group: "revenue",
          isBrand: true,
          yAxisId: "cur",
        },
        {
          type: "area",
          label: "Shopify Subscription Total Customer Revenue",
          dataKey: "metrics.shopify_subscription_total_customer_revenue",
          numberType: "currency",
          group: "revenue",
          isBrand: true,
          yAxisId: "cur",
        },
        {
          type: "area",
          label: "Amazon Owned Revenue",
          dataKey: "metrics.amazon_owned_revenue",
          numberType: "currency",
          group: "revenue",
          isBrand: true,
          yAxisId: "cur",
        },
        {
          type: "area",
          label: "Amazon Ads Spend",
          dataKey: "metrics.amazon_ads_total_spend",
          numberType: "currency",
          group: "revenue",
          isBrand: true,
          yAxisId: "cur",
        },
        {
          type: "area",
          label: "Amazon Total Revenue",
          dataKey: "metrics.amazon_total_revenue",
          numberType: "currency",
          group: "revenue",
          isBrand: true,
          yAxisId: "cur",
        },
        {
          type: "area",
          label: "Paid Social",
          dataKey: "metrics.total_social_ad_spend",
          numberType: "currency",
          group: "revenue",
          isBrand: true,
          yAxisId: "cur",
        },
        {
          type: "area",
          label: "Total Ad Spend",
          dataKey: "metrics.total_ad_spend",
          numberType: "currency",
          group: "revenue",
          isBrand: true,
          yAxisId: "cur",
        },
        {
          type: "area",
          label: "Total Brand ROAS",
          dataKey: "metrics.total_brand_roas",
          numberType: "coef",
          group: "revenue",
          isBrand: true,
          yAxisId: "coef",
        },

        // Channel Group Brand Value
        // {
        //   type: "area",
        //   label: "Organic Social Brand Value",
        //   numberType: "currency",
        //   dataKey:
        //     "company_platform_brand_value.organic_channel_group_brand_value",
        //   group: "channel-group-brand-value",
        //   isBrand: true,
        //   yAxisId: "cur",
        // },
        // {
        //   type: "area",
        //   label: "Paid Social Brand Value",
        //   numberType: "currency",
        //   dataKey: "company_platform_brand_value.ad_channel_group_brand_value",
        //   group: "channel-group-brand-value",
        //   isBrand: true,
        //   yAxisId: "cur",
        // },
        // {
        //   type: "area",
        //   label: "Search Brand Value",
        //   numberType: "currency",
        //   dataKey:
        //     "company_platform_brand_value.search_channel_group_brand_value",
        //   group: "channel-group-brand-value",
        //   isBrand: true,
        //   yAxisId: "cur",
        // },
        // {
        //   type: "area",
        //   label: "Earned Media Brand Value",
        //   numberType: "currency",
        //   dataKey:
        //     "company_platform_brand_value.mentions_channel_group_brand_value",
        //   group: "channel-group-brand-value",
        //   isBrand: true,
        //   yAxisId: "cur",
        // },
        // {
        //   type: "area",
        //   label: "Subscription Brand Value",
        //   numberType: "currency",
        //   dataKey:
        //     "company_platform_brand_value.subscription_channel_group_brand_value",
        //   group: "channel-group-brand-value",
        //   isBrand: true,
        //   yAxisId: "cur",
        // },

        // // Platform Brand Value:
        // {
        //   type: "area",
        //   label: "Facebook Brand Value",
        //   numberType: "currency",
        //   dataKey: "company_platform_brand_value.facebook_platform_brand_value",
        //   group: "platform-brand-value",
        //   isBrand: true,
        //   yAxisId: "cur",
        // },
        // {
        //   type: "area",
        //   label: "Instagram Brand Value",
        //   numberType: "currency",
        //   dataKey:
        //     "company_platform_brand_value.instagram_platform_brand_value",
        //   group: "platform-brand-value",
        //   isBrand: true,
        //   yAxisId: "cur",
        // },
        // {
        //   type: "area",
        //   label: "Klaviyo Brand Value",
        //   numberType: "currency",
        //   dataKey: "company_platform_brand_value.klaviyo_platform_brand_value",
        //   group: "platform-brand-value",
        //   isBrand: true,
        //   yAxisId: "cur",
        // },
        // {
        //   type: "area",
        //   label: "Youtube Brand Value",
        //   numberType: "currency",
        //   dataKey: "company_platform_brand_value.youtube_platform_brand_value",
        //   group: "platform-brand-value",
        //   isBrand: true,
        //   yAxisId: "cur",
        // },
        // {
        //   type: "area",
        //   label: "TikTok Brand Value",
        //   numberType: "currency",
        //   dataKey: "company_platform_brand_value.tiktok_platform_brand_value",
        //   group: "platform-brand-value",
        //   isBrand: true,
        //   yAxisId: "cur",
        // },

        // Platform Engagements:

        // Platform Engagements - Facebook
        {
          type: "area",
          label: "Facebook Ads Comments",
          dataKey: "metrics.facebook_ads_comments",
          group: "platform-engagements",
          yAxisId: "eng",
        },
        {
          type: "area",
          label: "Facebook Ads Follows",
          dataKey: "metrics.facebook_ads_follows",
          group: "platform-engagements",
          yAxisId: "eng",
        },
        {
          type: "area",
          label: "Facebook Ads Likes",
          dataKey: "metrics.facebook_ads_likes",
          group: "platform-engagements",
          yAxisId: "eng",
        },
        {
          type: "area",
          label: "Facebook Ads Shares",
          dataKey: "metrics.facebook_ads_shares",
          group: "platform-engagements",
          yAxisId: "eng",
        },
        {
          type: "area",
          label: "Facebook Pages Comments",
          dataKey: "metrics.facebook_pages_comments",
          group: "platform-engagements",
          yAxisId: "eng",
        },
        {
          type: "area",
          label: "Facebook Pages Follows",
          dataKey: "metrics.facebook_pages_follows",
          group: "platform-engagements",
          yAxisId: "eng",
        },
        {
          type: "area",
          label: "Facebook Pages Likes",
          dataKey: "metrics.facebook_pages_likes",
          group: "platform-engagements",
          yAxisId: "eng",
        },
        {
          type: "area",
          label: "Facebook Pages Shares",
          dataKey: "metrics.facebook_pages_shares",
          group: "platform-engagements",
          yAxisId: "eng",
        },
        {
          type: "area",
          label: "Facebook Total Comments",
          dataKey: "metrics.facebook_total_comments",
          group: "platform-engagements",
          yAxisId: "eng",
        },
        {
          type: "area",
          label: "Facebook Total Follows",
          dataKey: "metrics.facebook_total_follows",
          group: "platform-engagements",
          yAxisId: "eng",
        },
        {
          type: "area",
          label: "Facebook Total Likes",
          dataKey: "metrics.facebook_total_likes",
          group: "platform-engagements",
          yAxisId: "eng",
        },
        {
          type: "area",
          label: "Facebook Total Saves",
          dataKey: "metrics.facebook_total_saves",
          group: "platform-engagements",
          yAxisId: "eng",
        },
        {
          type: "area",
          label: "Facebook Total Shares",
          dataKey: "metrics.facebook_total_shares",
          group: "platform-engagements",
          yAxisId: "eng",
        },

        // Platform Engagements - Instagram
        {
          type: "area",
          label: "Instagram Ads Comments",
          dataKey: "metrics.instagram_ads_comments",
          group: "platform-engagements",
          yAxisId: "eng",
        },
        {
          type: "area",
          label: "Instagram Ads Likes",
          dataKey: "metrics.instagram_ads_likes",
          group: "platform-engagements",
          yAxisId: "eng",
        },
        {
          type: "area",
          label: "Instagram Ads Saves",
          dataKey: "metrics.instagram_ads_saves",
          group: "platform-engagements",
          yAxisId: "eng",
        },
        {
          type: "area",
          label: "Instagram Ads Shares",
          dataKey: "metrics.instagram_ads_shares",
          group: "platform-engagements",
          yAxisId: "eng",
        },
        {
          type: "area",
          label: "Instagram Post Comments",
          dataKey: "metrics.instagram_business_comments",
          group: "platform-engagements",
          yAxisId: "eng",
        },
        {
          type: "area",
          label: "Instagram Post Likes",
          dataKey: "metrics.instagram_business_likes",
          group: "platform-engagements",
          yAxisId: "eng",
        },
        {
          type: "area",
          label: "Instagram Post Saves",
          dataKey: "metrics.instagram_business_saves",
          group: "platform-engagements",
          yAxisId: "eng",
        },
        {
          type: "area",
          label: "Instagram Post Shares",
          dataKey: "metrics.instagram_business_shares",
          group: "platform-engagements",
          yAxisId: "eng",
        },
        {
          type: "area",
          label: "Instagram Total Comments",
          dataKey: "metrics.instagram_total_comments",
          group: "platform-engagements",
          yAxisId: "eng",
        },
        {
          type: "area",
          label: "Instagram Total Follows",
          dataKey: "metrics.instagram_total_follows",
          group: "platform-engagements",
          yAxisId: "eng",
        },
        {
          type: "area",
          label: "Instagram Total Likes",
          dataKey: "metrics.instagram_total_likes",
          group: "platform-engagements",
          yAxisId: "eng",
        },
        {
          type: "area",
          label: "Instagram Total Saves",
          dataKey: "metrics.instagram_total_saves",
          group: "platform-engagements",
          yAxisId: "eng",
        },
        {
          type: "area",
          label: "Instagram Total Shares",
          dataKey: "metrics.instagram_total_shares",
          group: "platform-engagements",
          yAxisId: "eng",
        },

        // Platform Engagements - Google
        {
          type: "area",
          dataKey: "metrics.google_search_organic_branded_search_impressions",
          label: "Google Branded Searches",
          group: "platform-engagements",
          yAxisId: "eng",
        },
        {
          type: "area",
          dataKey: "metrics.google_search_organic_branded_search_clicks",
          label: "Google Organic Branded Search Clicks",
          group: "platform-engagements",
          yAxisId: "eng",
        },
        {
          type: "area",
          dataKey: "metrics.google_ads_paid_branded_search_clicks",
          label: "Google Paid Branded Search Clicks",
          group: "platform-engagements",
          yAxisId: "eng",
        },
        {
          type: "area",
          dataKey: "metrics.google_ads_paid_branded_search_impressions",
          label: "Google Paid Branded Search Impressions",
          group: "platform-engagements",
          yAxisId: "eng",
        },
        {
          type: "area",
          dataKey: "metrics.total_google_search_clicks",
          label: "Google Total Search Clicks",
          group: "platform-engagements",
          yAxisId: "eng",
        },

        // Platform Engagements - Klaviyo
        {
          type: "area",
          label: "Klaviyo Email Subscribers",
          dataKey: "metrics.klaviyo_email_subscribers",
          group: "platform-engagements",
          yAxisId: "eng",
        },

        // Platform Engagements - TikTok
        {
          type: "area",
          label: "TikTok Ads Comments",
          dataKey: "metrics.tiktok_ads_comments",
          group: "platform-engagements",
          yAxisId: "eng",
        },
        {
          type: "area",
          label: "TikTok Ads Follows",
          dataKey: "metrics.tiktok_ads_follows",
          group: "platform-engagements",
          yAxisId: "eng",
        },
        {
          type: "area",
          label: "TikTok Ads Likes",
          dataKey: "metrics.tiktok_ads_likes",
          group: "platform-engagements",
          yAxisId: "eng",
        },
        {
          type: "area",
          label: "TikTok Ads Saves",
          dataKey: "metrics.tiktok_ads_saves",
          group: "platform-engagements",
          yAxisId: "eng",
        },
        {
          type: "area",
          label: "TikTok Ads Shares",
          dataKey: "metrics.tiktok_ads_shares",
          group: "platform-engagements",
          yAxisId: "eng",
        },
        {
          type: "area",
          label: "TikTok Organic Comments",
          dataKey: "metrics.tiktok_videos_comments",
          group: "platform-engagements",
          yAxisId: "eng",
        },
        {
          type: "area",
          label: "TikTok Organic Likes",
          dataKey: "metrics.tiktok_videos_likes",
          group: "platform-engagements",
          yAxisId: "eng",
        },
        {
          type: "area",
          label: "TikTok Organic Shares",
          dataKey: "metrics.tiktok_videos_shares",
          group: "platform-engagements",
          yAxisId: "eng",
        },
        {
          type: "area",
          label: "TikTok Total Comments",
          dataKey: "metrics.tiktok_total_comments",
          group: "platform-engagements",
          yAxisId: "eng",
        },
        {
          type: "area",
          label: "TikTok Total Follows",
          dataKey: "metrics.tiktok_total_follows",
          group: "platform-engagements",
          yAxisId: "eng",
        },
        {
          type: "area",
          label: "TikTok Total Likes",
          dataKey: "metrics.tiktok_total_likes",
          group: "platform-engagements",
          yAxisId: "eng",
        },
        {
          type: "area",
          label: "TikTok Total Shares",
          dataKey: "metrics.tiktok_total_shares",
          group: "platform-engagements",
          yAxisId: "eng",
        },

        // Platform Engagements - YouTube
        {
          type: "area",
          label: "YouTube Total Comments",
          dataKey: "metrics.youtube_channel_comments",
          group: "platform-engagements",
          yAxisId: "eng",
        },
        {
          type: "area",
          label: "YouTube Total Follows",
          dataKey: "metrics.youtube_channel_follows",
          group: "platform-engagements",
          yAxisId: "eng",
        },
        {
          type: "area",
          label: "YouTube Total Likes",
          dataKey: "metrics.youtube_channel_likes",
          group: "platform-engagements",
          yAxisId: "eng",
        },
        {
          type: "area",
          label: "YouTube Total Saves",
          dataKey: "metrics.youtube_channel_saves",
          group: "platform-engagements",
          yAxisId: "eng",
        },
        {
          type: "area",
          label: "YouTube Total Shares",
          dataKey: "metrics.youtube_channel_shares",
          group: "platform-engagements",
          yAxisId: "eng",
        },

        // Platform Engagements - Aggregates
        {
          type: "area",
          label: "Total Comments",
          dataKey: "metrics.total_comments",
          group: "platform-engagements",
          yAxisId: "eng",
        },
        {
          type: "area",
          label: "Total Likes",
          dataKey: "metrics.total_likes",
          group: "platform-engagements",
          yAxisId: "eng",
        },
        {
          type: "area",
          label: "Total New Followers",
          dataKey: "metrics.total_new_followers",
          group: "platform-engagements",
          yAxisId: "eng",
        },
        {
          type: "area",
          label: "Total New Subscribers",
          dataKey: "metrics.total_new_subscribers",
          group: "platform-engagements",
          yAxisId: "eng",
        },
        {
          type: "area",
          label: "Total Saves",
          dataKey: "metrics.total_saves",
          group: "platform-engagements",
          yAxisId: "eng",
        },
        {
          type: "area",
          label: "Total Shares",
          dataKey: "metrics.total_shares",
          group: "platform-engagements",
          yAxisId: "eng",
        },
        {
          type: "area",
          label: "Total Platform Engagements",
          dataKey: "metrics.total_platform_engagements",
          group: "platform-engagements",
          yAxisId: "eng",
        },
        {
          type: "area",
          label: "Total New Followers + Subscribers",
          dataKey: "metrics.total_followers_plus_subscribers",
          group: "platform-engagements",
          yAxisId: "eng",
        },
        {
          type: "area",
          label: "Total Digital Behavior",
          dataKey: "metrics.total_digital_behavior",
          group: "platform-engagements",
          yAxisId: "eng",
        },

        // Traffic
        {
          type: "area",
          label: "Baseline Sessions",
          dataKey: "metrics.ga_baseline_sessions",
          group: "traffic",
          yAxisId: "eng",
        },
        {
          type: "area",
          label: "Triggered Sessions",
          dataKey: "metrics.ga_triggered_sessions",
          group: "traffic",
          yAxisId: "eng",
        },
        {
          type: "area",
          label: "Paid Sessions",
          dataKey: "metrics.ga_paid_sessions",
          group: "traffic",
          yAxisId: "eng",
        },
      ],
    },
    metadata: null,
  },
  brand_value_conversions_by_platform_table: {
    id: "brand_value_conversions_by_platform_table",
    name: "Brand Value Conversions By Platform Table",
    description:
      "Brand Value represents the next-180-day baseline revenue of your brand engagements. Think of this as the next 180 day cohort value of the new engagements you created in any given time period as measured in growth in baseline revenue.",
    type: "TableVisualization",
    props: {
      comparisonEnabled: true,
      summaryWidget: "brand_value_conversions_by_platform_table_summary",
      totalLabelColumn: "company_platform_brand_value.platform",
      columnConfig: [
        {
          key: "company_platform_brand_value.platform",
          type: "title",
          width: 300,
          header: "Platform",
        },
        {
          key: "company_platform_brand_value.engagements",
          type: "number",
          header: "Engagements",
        },
        {
          key: "company_platform_brand_value.revenue30Days",
          type: "currency",
          // isBrand: true,
          header: "30d Brand Value",
        },
        {
          key: "company_platform_brand_value.revenue180Days",
          type: "currency",
          // isBrand: true,
          header: "180d Brand Value",
        },
        // {
        //   key: "company_platform_brand_value.revenuePerEngagement30Days",
        //   type: "currency",
        //   // isBrand: true,
        //   header: "30d Brand Value per Action",
        //   disableSorting: true,
        // },
        // {
        //   key: "company_platform_brand_value.revenuePerEngagement180Days",
        //   type: "currency",
        //   // isBrand: true,
        //   header: "180d Brand Value per Action",
        //   disableSorting: true,
        // },
      ],
      compareUniqueKey: "company_platform_brand_value.platform",
      enablePagination: true,
      disableGranularity: true,
      highlightCells: true,
    },
    metadata: { defaultColSpan: 12, defaultRowSpan: 12 },
    requiredSources: [
      SOURCE_IDS.GOOGLE_ADS,
      SOURCE_IDS.FACEBOOK_ADS,
      SOURCE_IDS.TIKTOK_ADS,
    ],
  },
  brand_value_conversions_by_channel_table: {
    id: "brand_value_conversions_by_channel_table",
    name: "Brand Value Conversions By Channel Table",
    description:
      "Brand Value represents the next-180-day baseline revenue of your brand engagements. Think of this as the next 180 day cohort value of the new engagements you created in any given time period as measured in growth in baseline revenue.",
    type: "TableVisualization",
    props: {
      comparisonEnabled: true,
      summaryWidget: "brand_value_conversions_by_channel_table_summary",
      totalLabelColumn: "company_platform_brand_value.channel",
      columnConfig: [
        {
          key: "company_platform_brand_value.channel",
          type: "title",
          width: 300,
          header: "Channel",
        },
        {
          key: "company_platform_brand_value.engagements",
          type: "number",
          header: "Engagements",
        },
        {
          key: "company_platform_brand_value.revenue30Days",
          type: "currency",
          // isBrand: true,
          header: "30d Brand Value",
        },
        {
          key: "company_platform_brand_value.revenue180Days",
          type: "currency",
          // isBrand: true,
          header: "180d Brand Value",
        },
        // {
        //   key: "company_platform_brand_value.revenuePerEngagement30Days",
        //   type: "currency",
        //   // isBrand: true,
        //   header: "30d Brand Value per Action",
        //   disableSorting: true,
        // },
        // {
        //   key: "company_platform_brand_value.revenuePerEngagement180Days",
        //   type: "currency",
        //   // isBrand: true,
        //   header: "180d Brand Value per Action",
        //   disableSorting: true,
        // },
      ],
      compareUniqueKey: "company_platform_brand_value.channel",
      enablePagination: true,
      disableGranularity: true,
      highlightCells: true,
    },
    metadata: { defaultColSpan: 12, defaultRowSpan: 12 },
    requiredSources: [
      SOURCE_IDS.GOOGLE_ADS,
      SOURCE_IDS.FACEBOOK_ADS,
      SOURCE_IDS.TIKTOK_ADS,
    ],
  },
  brand_value_conversions_by_channel_group_table: {
    id: "brand_value_conversions_by_channel_group_table",
    name: "Brand Value Conversions By Channel Group Table",
    description:
      "Brand Value represents the next-180-day baseline revenue of your brand engagements. Think of this as the next 180 day cohort value of the new engagements you created in any given time period as measured in growth in baseline revenue.",
    type: "TableVisualization",
    props: {
      comparisonEnabled: true,
      summaryWidget: "brand_value_conversions_by_channel_group_table_summary",
      totalLabelColumn: "company_platform_brand_value.channel_group",
      columnConfig: [
        {
          key: "company_platform_brand_value.channel_group",
          type: "title",
          width: 300,
          header: "Channel Group",
          render: (row: Record<string, string>) => {
            const channelGroup =
              row["company_platform_brand_value.channel_group"]
            const label = CHANNEL_GROUP_LABEL[channelGroup] ?? channelGroup
            const color =
              CHANNEL_GROUP_COLOR[channelGroup] ??
              getLightColor(stringToColor(channelGroup))
            return (
              <div className="flex items-center justify-start gap-system-sm capitalize">
                <div
                  className={cn("w-system-xs h-system-xs rounded-lg", color)}
                ></div>
                <div>{label}</div>
              </div>
            )
          },
        },
        {
          key: "company_platform_brand_value.engagements",
          type: "number",
          header: "Engagements",
        },
        {
          key: "company_platform_brand_value.revenue30Days",
          type: "currency",
          // isBrand: true,
          header: "30d Brand Value",
        },
        {
          key: "company_platform_brand_value.revenue180Days",
          type: "currency",
          // isBrand: true,
          header: "180d Brand Value",
        },
        // {
        //   key: "company_platform_brand_value.revenuePerEngagement30Days",
        //   type: "currency",
        //   // isBrand: true,
        //   header: "30d Brand Value per Action",
        //   disableSorting: true,
        // },
        // {
        //   key: "company_platform_brand_value.revenuePerEngagement180Days",
        //   type: "currency",
        //   // isBrand: true,
        //   header: "180d Brand Value per Action",
        //   disableSorting: true,
        // },
      ],
      compareUniqueKey: "company_platform_brand_value.channel_group",
      enablePagination: true,
      disableGranularity: true,
      highlightCells: true,
    },
    metadata: { defaultColSpan: 12, defaultRowSpan: 12 },
    requiredSources: [
      SOURCE_IDS.GOOGLE_ADS,
      SOURCE_IDS.FACEBOOK_ADS,
      SOURCE_IDS.TIKTOK_ADS,
    ],
  },
  revenue_kpi_dashboard_table: {
    id: "revenue_kpi_dashboard_table",
    name: "Revenue KPI Dashboard",
    description:
      "Comprehensive weekly breakdown of key revenue and performance metrics including ad spend, revenue by traffic source, customer type, and contribution margins.",
    type: "TableVisualization",
    props: {
      comparisonEnabled: false,
      disableGranularity: false,
      enablePagination: false,
      columnConfig: [
        {
          key: "metric",
          type: "title",
          width: 240,
          header: "Metric",
        },
        // Week columns will be dynamically generated based on data
      ],
      // NOTE: This inline metricGroups registry is duplicated in
      // `libs/shared/kpi/metric-groups.ts` as `KPI_METRIC_GROUPS`. Any change
      // to the metric keys/labels/types here must also be made there until
      // this is replaced with `metricGroups: KPI_METRIC_GROUPS` (pending UI
      // refactor to use the new prefs-object API shape).
      metricGroups: {
        adSpend: {
          title: "Ad Spend",
          metrics: [
            {
              key: "metrics.instagram_ads_spend",
              label: "Instagram Ads Spend",
              type: "currency",
            },
            {
              key: "metrics.facebook_ads_spend",
              label: "Facebook Ads Spend",
              type: "currency",
            },
            {
              key: "metrics.tiktok_ads_spend",
              label: "TikTok Ads Spend",
              type: "currency",
            },
            {
              key: "metrics.pinterest_ads_spend",
              label: "Pinterest Ads Spend",
              type: "currency",
            },
            {
              key: "metrics.google_ads_total_spend",
              label: "Google Ads Spend",
              type: "currency",
            },
            {
              key: "metrics.amazon_ads_total_spend",
              label: "Amazon Ads Spend",
              type: "currency",
            },
            {
              key: "metrics.total_ad_spend",
              label: "Total Ad Spend",
              type: "currency",
            },
            {
              key: "metrics.spend_percentage_of_new_customer_revenue",
              label: "Spend % of New Customer Revenue",
              type: "percent",
            },
            {
              key: "metrics.amer",
              label: "aMER",
              type: "decimal",
              suffix: "x",
            },
          ],
          showTotals: true,
          defaultExpanded: true,
        },
        mer: {
          title: "Marketing Efficiency Ratio (MER)",
          metrics: [
            {
              key: "metrics.dtc_ecomm_mer",
              label: "DTC Ecomm MER",
              type: "decimal",
              suffix: "x",
            },
            {
              key: "metrics.dtc_ecomm_amer",
              label: "DTC Ecomm aMER",
              type: "decimal",
              suffix: "x",
            },
            {
              key: "metrics.total_dtc_mer",
              label: "Total DTC MER",
              type: "decimal",
              suffix: "x",
            },
            {
              key: "metrics.total_dtc_amer",
              label: "Total DTC aMER",
              type: "decimal",
              suffix: "x",
            },
            {
              key: "metrics.total_business_mer",
              label: "Total Business MER",
              type: "decimal",
              suffix: "x",
            },
            {
              key: "metrics.dtc_ecomm_plus_amazon_mer",
              label: "DTC Ecomm + Amazon MER",
              type: "decimal",
              suffix: "x",
            },
          ],
          showTotals: false,
          defaultExpanded: true,
        },
        croWebMerch: {
          title: "CRO/Web Merch KPIs",
          metrics: [
            {
              key: "metrics.baseline_per_session_value",
              label: "Baseline Per Session Value",
              type: "currency",
            },
            {
              key: "metrics.triggered_per_session_value",
              label: "Triggered Per Session Value",
              type: "currency",
            },
            {
              key: "metrics.paid_per_session_value",
              label: "Paid Per Session Value",
              type: "currency",
            },
            {
              key: "metrics.total_per_session_value",
              label: "Total Per Session Value",
              type: "currency",
            },
          ],
          showTotals: true,
          defaultExpanded: false,
        },
        searchKPIs: {
          title: "Search KPIs",
          metrics: [
            {
              key: "metrics.ga_sessions_from_organic_sessions",
              label: "Sessions from Organic Search",
              type: "number",
            },
            {
              key: "metrics.google_ads_paid_branded_search_clicks",
              label: "Sessions from Paid Branded Search",
              type: "number",
            },
            {
              key: "metrics.ga_sessions_from_organic_plus_paid_branded_search_sessions",
              label: "Total Sessions from Organic + Paid Branded Search",
              type: "number",
            },
            {
              key: "metrics.ga_revenue_from_organic_sessions",
              label: "Revenue from Organic Search",
              type: "currency",
            },
            {
              key: "metrics.google_ads_paid_branded_search_revenue",
              label: "Revenue from Paid Branded Search",
              type: "currency",
            },
            {
              key: "metrics.ga_sessions_from_organic_plus_paid_branded_search_revenue",
              label: "Total Revenue from Organic + Paid Branded Search",
              type: "currency",
            },
            {
              key: "metrics.organic_per_session_value",
              label: "Revenue per Session from Organic Search",
              type: "currency",
            },
            {
              key: "metrics.google_ads_paid_branded_search_revenue_per_click",
              label: "Revenue per Session from Paid Branded Search",
              type: "currency",
            },
            {
              key: "metrics.ga_sessions_from_organic_plus_paid_branded_search_revenue_per_session",
              label: "Total Rev per Session from Organic + Paid Branded Search",
              type: "currency",
            },
          ],
          showTotals: false,
          defaultExpanded: false,
        },
        brand: {
          title: "Brand Search KPIs",
          metrics: [
            {
              key: "metrics.google_search_organic_branded_search_impressions",
              label: "Branded Searches",
              type: "number",
            },
            {
              key: "metrics.total_google_search_clicks",
              label: "Total Branded Search Clicks",
              type: "number",
            },
            {
              key: "metrics.branded_search_conversion_rate",
              label: "Search Conversion Rate",
              type: "percent",
            },
          ],
          showTotals: false,
          defaultExpanded: false,
        },
        metaAdsReachImpressions: {
          title: "Meta Ads Reach & Impressions",
          metrics: [
            {
              key: "metrics.facebook_ads_reach",
              label: "Meta Ads Unique Reach",
              type: "number",
            },
            {
              key: "metrics.facebook_ads_impressions",
              label: "Meta Ads Total Impressions",
              type: "number",
            },
          ],
          showTotals: false,
          defaultExpanded: false,
        },
        metaAdsAudienceSegments: {
          title: "Meta Ads Audience Segments",
          metrics: [
            {
              key: "metrics.facebook_ads_reach_prospecting",
              label: "Reach — Prospecting",
              type: "number",
            },
            {
              key: "metrics.facebook_ads_reach_engaged",
              label: "Reach — Engaged",
              type: "number",
            },
            {
              key: "metrics.facebook_ads_reach_existing",
              label: "Reach — Existing",
              type: "number",
            },
            {
              key: "metrics.facebook_ads_reach_unknown",
              label: "Reach — Unknown",
              type: "number",
            },
            {
              key: "metrics.facebook_ads_reach_uncategorized",
              label: "Reach — Uncategorized",
              type: "number",
            },
            {
              key: "metrics.facebook_ads_impressions_prospecting",
              label: "Impressions — Prospecting",
              type: "number",
            },
            {
              key: "metrics.facebook_ads_impressions_engaged",
              label: "Impressions — Engaged",
              type: "number",
            },
            {
              key: "metrics.facebook_ads_impressions_existing",
              label: "Impressions — Existing",
              type: "number",
            },
            {
              key: "metrics.facebook_ads_impressions_unknown",
              label: "Impressions — Unknown",
              type: "number",
            },
            {
              key: "metrics.facebook_ads_impressions_uncategorized",
              label: "Impressions — Uncategorized",
              type: "number",
            },
            {
              key: "metrics.facebook_ads_spend_prospecting",
              label: "Spend — Prospecting",
              type: "currency",
            },
            {
              key: "metrics.facebook_ads_spend_engaged",
              label: "Spend — Engaged",
              type: "currency",
            },
            {
              key: "metrics.facebook_ads_spend_existing",
              label: "Spend — Existing",
              type: "currency",
            },
            {
              key: "metrics.facebook_ads_spend_unknown",
              label: "Spend — Unknown",
              type: "currency",
            },
            {
              key: "metrics.facebook_ads_spend_uncategorized",
              label: "Spend — Uncategorized",
              type: "currency",
            },
            {
              key: "metrics.facebook_ads_link_clicks_prospecting",
              label: "Link Clicks — Prospecting",
              type: "number",
            },
            {
              key: "metrics.facebook_ads_link_clicks_engaged",
              label: "Link Clicks — Engaged",
              type: "number",
            },
            {
              key: "metrics.facebook_ads_link_clicks_existing",
              label: "Link Clicks — Existing",
              type: "number",
            },
            {
              key: "metrics.facebook_ads_link_clicks_unknown",
              label: "Link Clicks — Unknown",
              type: "number",
            },
            {
              key: "metrics.facebook_ads_link_clicks_uncategorized",
              label: "Link Clicks — Uncategorized",
              type: "number",
            },
          ],
          showTotals: false,
          defaultExpanded: false,
        },
        traffic: {
          title: "Traffic",
          metrics: [
            {
              key: "metrics.ga_paid_sessions",
              label: "Paid Sessions",
              type: "number",
            },
            {
              key: "metrics.ga_triggered_sessions",
              label: "Triggered Sessions",
              type: "number",
            },
            {
              key: "metrics.ga_baseline_sessions",
              label: "Baseline Sessions",
              type: "number",
            },
            {
              key: "metrics.ga_sessions",
              label: "Total Sessions",
              type: "number",
            },
          ],
          showTotals: true,
          defaultExpanded: false,
        },
        // Catalog-only group (intentionally NOT in DEFAULT_TABLE_SETTINGS.order):
        // GA4 web-funnel viewer counts (active users per funnel step), broken
        // out by traffic attribution. Mirrors `webFunnelViewers` in
        // libs/shared/kpi/metric-groups.ts.
        webFunnelViewers: {
          title: "Web Funnel Viewers",
          metrics: [
            {
              key: "metrics.ga_total_homepage_viewers",
              label: "Homepage Viewers — Total",
              type: "number",
            },
            {
              key: "metrics.ga_paid_homepage_viewers",
              label: "Homepage Viewers — Paid",
              type: "number",
            },
            {
              key: "metrics.ga_organic_homepage_viewers",
              label: "Homepage Viewers — Organic",
              type: "number",
            },
            {
              key: "metrics.ga_triggered_homepage_viewers",
              label: "Homepage Viewers — Triggered",
              type: "number",
            },
            {
              key: "metrics.ga_total_collection_viewers",
              label: "Collection Viewers — Total",
              type: "number",
            },
            {
              key: "metrics.ga_paid_collection_viewers",
              label: "Collection Viewers — Paid",
              type: "number",
            },
            {
              key: "metrics.ga_organic_collection_viewers",
              label: "Collection Viewers — Organic",
              type: "number",
            },
            {
              key: "metrics.ga_triggered_collection_viewers",
              label: "Collection Viewers — Triggered",
              type: "number",
            },
            {
              key: "metrics.ga_total_product_page_viewers",
              label: "Product Page Viewers — Total",
              type: "number",
            },
            {
              key: "metrics.ga_paid_product_page_viewers",
              label: "Product Page Viewers — Paid",
              type: "number",
            },
            {
              key: "metrics.ga_organic_product_page_viewers",
              label: "Product Page Viewers — Organic",
              type: "number",
            },
            {
              key: "metrics.ga_triggered_product_page_viewers",
              label: "Product Page Viewers — Triggered",
              type: "number",
            },
          ],
          showTotals: false,
          defaultExpanded: false,
        },
        averageOrderValues: {
          title: "Average Order Values",
          metrics: [
            {
              key: "metrics.shopify_baseline_aov",
              label: "Baseline AOV",
              type: "currency",
            },
            {
              key: "metrics.shopify_triggered_aov",
              label: "Triggered AOV",
              type: "currency",
            },
            {
              key: "metrics.shopify_paid_aov",
              label: "Paid AOV",
              type: "currency",
            },
            {
              key: "metrics.shopify_total_aov",
              label: "Total AOV",
              type: "currency",
            },
          ],
          showTotals: false,
          defaultExpanded: false,
        },
        totalBusinessRevenue: {
          title: "Total Business Revenue",
          metrics: [],
          showTotals: true,
          defaultExpanded: false,
          isDynamic: true,
          getMetrics: (
            amazonEnabled: boolean,
            isShopifyConnected = true,
            wholesaleEnabled = false,
          ) =>
            getTotalBusinessRevenueMetrics(
              amazonEnabled,
              isShopifyConnected,
              wholesaleEnabled,
            ),
        },
        gaRevenue: {
          title: "GA Revenue by Traffic Source",
          metrics: [],
          showTotals: true,
          defaultExpanded: true,
          isDynamic: true,
          getMetrics: () => getGARevenueMetrics(),
        },
        shopifyRevenue: {
          title: "Shopify Web Revenue by Traffic Source",
          metrics: [
            {
              key: "metrics.shopify_baseline_revenue",
              label: "Shopify Baseline Revenue",
              type: "currency",
            },
            {
              key: "metrics.shopify_triggered_revenue",
              label: "Shopify Triggered Revenue",
              type: "currency",
            },
            {
              key: "metrics.shopify_paid_revenue",
              label: "Shopify Paid Revenue",
              type: "currency",
            },
            {
              key: "metrics.shopify_subscription_revenue",
              label: "Shopify Subscription Revenue",
              type: "currency",
            },
            {
              key: "metrics.shopify_total_traffic_source_revenue",
              label: "Total Web Revenue",
              type: "currency",
            },
          ],
          showTotals: true,
          defaultExpanded: true,
        },
        shopifyCustomer: {
          title: "Shopify - Total Revenue",
          metrics: [
            {
              key: "metrics.shopify_total_new_customer_revenue",
              label: "Shopify New Customer Revenue",
              type: "currency",
            },
            {
              key: "metrics.shopify_total_repeat_customer_revenue",
              label: "Shopify Repeat Customer Revenue",
              type: "currency",
            },
            {
              key: "metrics.shopify_total_customer_revenue",
              label: "Total",
              type: "currency",
            },
          ],
          showTotals: true,
          defaultExpanded: false,
        },
        shopifyEcommRevenue: {
          title: "Shopify - DTC Ecomm Revenue",
          metrics: [
            {
              key: "metrics.shopify_ecomm_new_customer_revenue",
              label: "Ecomm - New Customer Revenue",
              type: "currency",
            },
            {
              key: "metrics.shopify_ecomm_repeat_customer_revenue",
              label: "Ecomm - Repeat Customer Revenue",
              type: "currency",
            },
            {
              key: "metrics.shopify_ecomm_total_customer_revenue",
              label: "Ecomm - Total Revenue",
              type: "currency",
            },
          ],
          showTotals: true,
          defaultExpanded: false,
        },
        shopifyRetailRevenue: {
          title: "Shopify - DTC Retail Revenue",
          metrics: [
            {
              key: "metrics.shopify_dtc_retail_new_customer_revenue",
              label: "DTC Retail - New Customer Revenue",
              type: "currency",
            },
            {
              key: "metrics.shopify_dtc_retail_repeat_customer_revenue",
              label: "DTC Retail - Repeat Customer Revenue",
              type: "currency",
            },
            {
              key: "metrics.shopify_dtc_retail_total_customer_revenue",
              label: "DTC Retail - Total Revenue",
              type: "currency",
            },
          ],
          showTotals: true,
          defaultExpanded: false,
        },
        // Mirrors KPI_METRIC_GROUPS.wholesale in libs/shared/kpi/metric-groups.ts.
        // 3rd Party Retail: retailer POS sell-through at consumer prices (not
        // sell-in, and not the Shopify "Third Party Retail" sales channel).
        // The slug and metric keys keep the `wholesale` name; only the labels
        // changed. REI is week-ending dated and SPINS quad-end dated, so
        // daily/weekly buckets are lumpy by design. showTotals stays false:
        // the 3rd Party Retail Revenue row is the rollup.
        wholesale: {
          title: "3rd Party Retail Revenue",
          metrics: [
            {
              key: "metrics.wholesale_rei_regular_revenue",
              label: "REI Regular Revenue",
              type: "currency",
            },
            {
              key: "metrics.wholesale_sams_club_regular_revenue",
              label: "Sam's Club Revenue",
              type: "currency",
            },
            {
              key: "metrics.wholesale_whole_foods_regular_revenue",
              label: "Whole Foods Revenue",
              type: "currency",
            },
            {
              key: "metrics.wholesale_walmart_regular_revenue",
              label: "Walmart Revenue",
              type: "currency",
            },
            {
              key: "metrics.wholesale_target_regular_revenue",
              label: "Target Revenue",
              type: "currency",
            },
            {
              key: "metrics.wholesale_spins_regular_revenue",
              label: "SPINS Retail Scan Revenue",
              type: "currency",
            },
            {
              key: "metrics.wholesale_kroger_regular_revenue",
              label: "Kroger Revenue",
              type: "currency",
            },
            {
              key: "metrics.wholesale_total_revenue",
              label: "3rd Party Retail Revenue",
              type: "currency",
            },
          ],
          showTotals: false,
          defaultExpanded: false,
        },
        shopifyThirdPartyEcommRevenue: {
          title: "Shopify - Third Party Ecomm Revenue",
          metrics: [
            {
              key: "metrics.shopify_third_party_ecomm_new_customer_revenue",
              label: "Third Party Ecomm - New Customer Revenue",
              type: "currency",
            },
            {
              key: "metrics.shopify_third_party_ecomm_repeat_customer_revenue",
              label: "Third Party Ecomm - Repeat Customer Revenue",
              type: "currency",
            },
            {
              key: "metrics.shopify_third_party_ecomm_total_customer_revenue",
              label: "Third Party Ecomm - Total Revenue",
              type: "currency",
            },
          ],
          showTotals: true,
          defaultExpanded: false,
        },
        shopifySubscriptionRevenue: {
          title: "Shopify - Subscription Revenue",
          metrics: [
            {
              key: "metrics.shopify_subscription_new_customer_revenue",
              label: "Subscription - New Customer Revenue",
              type: "currency",
            },
            {
              key: "metrics.shopify_subscription_repeat_customer_revenue",
              label: "Subscription - Repeat Customer Revenue",
              type: "currency",
            },
            {
              key: "metrics.shopify_subscription_total_customer_revenue",
              label: "Subscription - Total Revenue",
              type: "currency",
            },
          ],
          showTotals: true,
          defaultExpanded: false,
        },
        amazonRevenue: {
          title: "Amazon Revenue",
          metrics: [
            {
              key: "metrics.amazon_paid_revenue",
              label: "Paid",
              type: "currency",
            },
            {
              key: "metrics.amazon_owned_revenue",
              label: "Owned",
              type: "currency",
            },
            {
              key: "metrics.amazon_total_revenue",
              label: "Total",
              type: "currency",
            },
          ],
          showTotals: false,
          defaultExpanded: false,
        },
        shopifyContribution: {
          title: "Profitability",
          metrics: [
            {
              key: "metrics.shopify_ecomm_gross_profit",
              label: "DTC Ecomm Gross Profit",
              type: "currency",
            },
            {
              key: "metrics.shopify_subscription_gross_profit",
              label: "Subscription Gross Profit",
              type: "currency",
            },
            {
              key: "metrics.shopify_dtc_retail_gross_profit",
              label: "DTC Retail Gross Profit",
              type: "currency",
            },
            {
              key: "metrics.shopify_total_gross_profit",
              label: "Shopify Total Gross Profit",
              type: "currency",
            },
            {
              key: "metrics.amazon_gross_profit",
              label: "Amazon Gross Profit",
              type: "currency",
            },
            {
              key: "metrics.total_contribution_margin",
              label: "Ecomm Contribution Margin",
              type: "currency",
            },
            {
              key: "metrics.subscription_contribution_margin",
              label: "Subscription Contribution Margin",
              type: "currency",
            },
            {
              key: "metrics.dtc_retail_contribution_margin",
              label: "DTC Retail Contribution Margin",
              type: "currency",
            },
            {
              key: "metrics.shopify_total_contribution_margin",
              label: "Shopify Total Contribution Margin",
              type: "currency",
            },
            {
              key: "metrics.amazon_contribution_margin",
              label: "Amazon Contribution Margin",
              type: "currency",
            },
            {
              key: "metrics.total_business_contribution_margin",
              label: "Total Business Contribution Margin",
              type: "currency",
            },
            {
              key: "metrics.contribution_margin_percent",
              label: "Ecomm Contribution Margin %",
              type: "percentage",
            },
          ],
          showTotals: false,
          defaultExpanded: false,
        },
        revenueBreakdown: {
          title: "Baseline Revenue Breakdown",
          metrics: [], // Will be populated by getRevenueBreakdownMetrics function
          showTotals: false,
          defaultExpanded: true,
          isDynamic: true, // Flag to indicate this is dynamically populated
          getMetrics: (depVar: string, amazonEnabled: boolean) =>
            getRevenueBreakdownMetrics(depVar, amazonEnabled),
        },
        brandValue: {
          title: "Brand Value",
          metrics: [
            {
              key: "metrics.d30_brand_value",
              label: "30d Brand Value",
              type: "currency",
            },
            {
              key: "metrics.d180_brand_value",
              label: "180d Brand Value",
              type: "currency",
            },
          ],
          showTotals: false,
          defaultExpanded: true,
          marginalMetrics: ["metrics.d180_brand_value"], // Only 180d brand value for WoW marginal
        },
        brandValueBreakdown: {
          title: "Brand Value Breakdown",
          metrics: [
            {
              key: "company_platform_brand_value.ad_channel_group_brand_value",
              label: "Ad",
              type: "currency",
            },
            {
              key: "company_platform_brand_value.mentions_channel_group_brand_value",
              label: "Mentions",
              type: "currency",
            },
            {
              key: "company_platform_brand_value.organic_channel_group_brand_value",
              label: "Organic",
              type: "currency",
            },
            {
              key: "company_platform_brand_value.search_channel_group_brand_value",
              label: "Search",
              type: "currency",
            },
            {
              key: "company_platform_brand_value.subscription_channel_group_brand_value",
              label: "Subscription",
              type: "currency",
            },
            {
              key: "company_platform_brand_value.unattributed_channel_group_brand_value",
              label: "Unattributed",
              type: "currency",
            },
            {
              key: "company_platform_brand_value.total_brand_value",
              label: "Total Brand Value",
              type: "currency",
            },
          ],
          showTotals: false,
          defaultExpanded: true,
          marginalMetrics: ["company_platform_brand_value.total_brand_value"],
        },
        digitalEngagement: {
          title: "Digital Engagement",
          metrics: [
            {
              key: "metrics.total_likes",
              label: "Total Likes/Reactions",
              type: "number",
            },
            {
              key: "metrics.total_comments",
              label: "Total Comments",
              type: "number",
            },
            {
              key: "metrics.total_shares",
              label: "Total Shares",
              type: "number",
            },
            {
              key: "metrics.total_saves",
              label: "Total Saves",
              type: "number",
            },
            {
              key: "metrics.total_new_followers",
              label: "Total New Followers",
              type: "number",
            },
            {
              key: "metrics.total_new_subscribers",
              label: "Total Subscribers",
              type: "number",
            },
            {
              key: "metrics.total_followers_plus_subscribers",
              label: "Total New Followers + Subscribers",
              type: "number",
            },
            {
              key: "metrics.youtube_channel_views",
              label: "YouTube Views",
              type: "number",
            },
            {
              key: "metrics.youtube_ads_video_views",
              label: "YouTube Ad Views",
              type: "number",
            },
            {
              key: "metrics.total_platform_engagements",
              label: "Total Platform Engagements",
              type: "number",
            },
            {
              key: "metrics.total_digital_behavior",
              label: "Total Digital Behavior",
              type: "number",
            },
          ],
          showTotals: false,
          defaultExpanded: false,
        },
      },
      features: {
        synchronizedScrolling: true,
        collapsibleTables: true,
        dragDropReordering: true,
        weekOverWeekMarginal: true,
        localStoragePersistence: true,
      },
    },
    metadata: { defaultColSpan: 12, defaultRowSpan: 12 },
    requiredSources: [
      SOURCE_IDS.GOOGLE_ADS,
      SOURCE_IDS.FACEBOOK_ADS,
      SOURCE_IDS.TIKTOK_ADS,
      SOURCE_IDS.SHOPIFY,
      SOURCE_IDS.GOOGLE_ANALYTICS,
      SOURCE_IDS.AMAZON_ADS,
    ],
  },
  company_web_analytics_page_path_report_chart: {
    id: "company_web_analytics_page_path_report_chart",
    name: "Page Path Metrics Over Time",
    description:
      "Time series chart showing pageviews by page type, add to carts, and purchases",
    type: "ComposedChart",
    props: {
      title: "Page Path Metrics Over Time",
      comparisonEnabled: true,
      graphs: [
        {
          type: "area",
          label: "Pageviews",
          dataKey: "company_web_analytics_page_path_report.screen_page_views",
          color: "#10B981",
          unit: "view",
          pluralizeUnit: true,
        },
        {
          type: "area",
          label: "Add to Carts",
          dataKey: "company_web_analytics_page_path_report.add_to_carts",
          color: "#3B82F6",
          unit: "cart",
          pluralizeUnit: true,
        },
        {
          type: "area",
          label: "First Orders",
          dataKey: "company_web_analytics_page_path_report.first_orders",
          color: "#EF4444",
          unit: "order",
          pluralizeUnit: true,
        },
        {
          type: "area",
          label: "Repeat Orders",
          dataKey: "company_web_analytics_page_path_report.repeat_orders",
          color: "#EAB308",
          unit: "order",
          pluralizeUnit: true,
        },
      ],
      hideTotal: true,
      bottomLegend: true,
    },
    metadata: { defaultColSpan: 12, defaultRowSpan: 8 },
  },
  company_web_analytics_page_path_report_table: {
    id: "company_web_analytics_page_path_report_table",
    name: "Page Path Report",
    description: "Analytics data for page paths",
    type: "TableVisualization",
    props: {
      title: "Page Path Report",
      columnConfig: [
        // Default visible columns
        {
          key: "company_web_analytics_page_path_report.title",
          type: "title",
          header: "Title",
          width: 300,
        },
        {
          key: "company_web_analytics_page_path_report.handle",
          type: "text",
          header: "Handle",
          width: 300,
        },
        {
          key: "company_web_analytics_page_path_report.sessions",
          type: "number",
          header: "Sessions",
          width: 150,
        },
        {
          key: "company_web_analytics_page_path_report.add_to_carts",
          type: "number",
          header: "Adds to Cart",
          width: 150,
        },
        {
          key: "company_web_analytics_page_path_report.total_orders",
          type: "number",
          header: "Total Orders",
          width: 150,
        },
        {
          key: "company_web_analytics_page_path_report.conversion_rate",
          type: "percent",
          header: "Conversion Rate",
          width: 160,
        },
        {
          key: "company_web_analytics_page_path_report.first_orders",
          type: "number",
          header: "New Customer Orders",
          width: 180,
        },
        {
          key: "company_web_analytics_page_path_report.repeat_orders",
          type: "number",
          header: "Repeat Customer Orders",
          width: 200,
        },
        {
          key: "company_web_analytics_page_path_report.first_order_aov",
          type: "currency",
          header: "First Order AOV",
          width: 160,
        },
        {
          key: "company_web_analytics_page_path_report.avg_first_order_ltv_90_day",
          type: "currency",
          header: "First Order 90d LTV",
          width: 180,
        },
        {
          key: "company_web_analytics_page_path_report.first_order_ltv_90_day_pct_of_aov",
          type: "percent",
          header: "90d LTV as % of First Order Value",
          width: 250,
        },
        {
          key: "company_web_analytics_page_path_report.expected_new_customer_session_ltv",
          type: "currency",
          header: "Expected New Customer Session LTV",
          width: 270,
        },
        {
          key: "company_web_analytics_page_path_report.second_orders",
          type: "number",
          header: "Second Orders",
          width: 150,
        },
        {
          key: "company_web_analytics_page_path_report.second_order_aov",
          type: "currency",
          header: "Second Order AOV",
          width: 170,
        },
        {
          key: "company_web_analytics_page_path_report.avg_second_order_ltv_90_day",
          type: "currency",
          header: "Second Order 90d LTV",
          width: 190,
        },
        // Additional columns (hidden by default, available via column selector)
        // GA Web Analytics
        {
          key: "company_web_analytics_page_path_report.screen_page_views",
          type: "number",
          header: "Page Views",
          width: 150,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_path_report.scrolled_users",
          type: "number",
          header: "Scrolled Users",
          width: 150,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_path_report.bounces",
          type: "number",
          header: "Bounces",
          width: 150,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_path_report.checkouts",
          type: "number",
          header: "Checkouts",
          width: 150,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_path_report.key_events",
          type: "number",
          header: "Key Events",
          width: 150,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_path_report.total_revenue",
          type: "currency",
          header: "Total Revenue",
          width: 150,
          disabled: true,
        },
        // Calculated Rates
        {
          key: "company_web_analytics_page_path_report.bounce_rate",
          type: "percent",
          header: "Bounce Rate",
          width: 150,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_path_report.add_to_cart_rate",
          type: "percent",
          header: "Add to Cart Rate",
          width: 150,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_path_report.scroll_rate",
          type: "percent",
          header: "Scroll Rate",
          width: 120,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_path_report.checkout_rate",
          type: "percent",
          header: "Checkout Rate",
          width: 150,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_path_report.revenue_per_session",
          type: "currency",
          header: "Revenue per Session",
          width: 170,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_path_report.revenue_per_page_view",
          type: "currency",
          header: "Revenue per Page View",
          width: 180,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_path_report.page_views_per_session",
          type: "number",
          header: "Page Views per Session",
          width: 180,
          disabled: true,
        },
        // Order Counts
        {
          key: "company_web_analytics_page_path_report.third_orders",
          type: "number",
          header: "Third Orders",
          width: 140,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_path_report.fourth_orders",
          type: "number",
          header: "Fourth Orders",
          width: 140,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_path_report.fifth_plus_orders",
          type: "number",
          header: "5+ Orders",
          width: 130,
          disabled: true,
        },
        // Order Revenue
        {
          key: "company_web_analytics_page_path_report.sum_first_order_revs",
          type: "currency",
          header: "1st Order Revenue",
          width: 160,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_path_report.sum_second_order_revs",
          type: "currency",
          header: "2nd Order Revenue",
          width: 160,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_path_report.sum_third_order_revs",
          type: "currency",
          header: "3rd Order Revenue",
          width: 160,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_path_report.sum_fourth_order_revs",
          type: "currency",
          header: "4th Order Revenue",
          width: 160,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_path_report.sum_fifth_plus_order_revs",
          type: "currency",
          header: "5+ Order Revenue",
          width: 160,
          disabled: true,
        },
        // Order Line Revenue
        {
          key: "company_web_analytics_page_path_report.sum_first_order_line_revenue",
          type: "currency",
          header: "1st Order Line Revenue",
          width: 180,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_path_report.sum_second_order_line_revenue",
          type: "currency",
          header: "2nd Order Line Revenue",
          width: 180,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_path_report.sum_third_order_line_revenue",
          type: "currency",
          header: "3rd Order Line Revenue",
          width: 180,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_path_report.sum_fourth_order_line_revenue",
          type: "currency",
          header: "4th Order Line Revenue",
          width: 180,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_path_report.sum_fifth_plus_order_line_revenue",
          type: "currency",
          header: "5+ Order Line Revenue",
          width: 180,
          disabled: true,
        },
        // Sum LTV 90 Day
        {
          key: "company_web_analytics_page_path_report.sum_first_order_ltv_90_day",
          type: "currency",
          header: "Sum 1st Order LTV 90d",
          width: 190,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_path_report.sum_second_order_ltv_90_day",
          type: "currency",
          header: "Sum 2nd Order LTV 90d",
          width: 190,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_path_report.sum_third_order_ltv_90_day",
          type: "currency",
          header: "Sum 3rd Order LTV 90d",
          width: 190,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_path_report.sum_fourth_order_ltv_90_day",
          type: "currency",
          header: "Sum 4th Order LTV 90d",
          width: 190,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_path_report.sum_fifth_plus_order_ltv_90_day",
          type: "currency",
          header: "Sum 5+ Order LTV 90d",
          width: 190,
          disabled: true,
        },
        // Sum LTV 180 Day
        {
          key: "company_web_analytics_page_path_report.sum_first_order_ltv_180_day",
          type: "currency",
          header: "Sum 1st Order LTV 180d",
          width: 200,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_path_report.sum_second_order_ltv_180_day",
          type: "currency",
          header: "Sum 2nd Order LTV 180d",
          width: 200,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_path_report.sum_third_order_ltv_180_day",
          type: "currency",
          header: "Sum 3rd Order LTV 180d",
          width: 200,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_path_report.sum_fourth_order_ltv_180_day",
          type: "currency",
          header: "Sum 4th Order LTV 180d",
          width: 200,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_path_report.sum_fifth_plus_order_ltv_180_day",
          type: "currency",
          header: "Sum 5+ Order LTV 180d",
          width: 200,
          disabled: true,
        },
        // Sum LTV 365 Day
        {
          key: "company_web_analytics_page_path_report.sum_first_order_ltv_365_day",
          type: "currency",
          header: "Sum 1st Order LTV 365d",
          width: 200,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_path_report.sum_second_order_ltv_365_day",
          type: "currency",
          header: "Sum 2nd Order LTV 365d",
          width: 200,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_path_report.sum_third_order_ltv_365_day",
          type: "currency",
          header: "Sum 3rd Order LTV 365d",
          width: 200,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_path_report.sum_fourth_order_ltv_365_day",
          type: "currency",
          header: "Sum 4th Order LTV 365d",
          width: 200,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_path_report.sum_fifth_plus_order_ltv_365_day",
          type: "currency",
          header: "Sum 5+ Order LTV 365d",
          width: 200,
          disabled: true,
        },
        // Avg LTV 90 Day
        {
          key: "company_web_analytics_page_path_report.avg_third_order_ltv_90_day",
          type: "currency",
          header: "Avg 3rd Order LTV 90d",
          width: 190,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_path_report.avg_fourth_order_ltv_90_day",
          type: "currency",
          header: "Avg 4th Order LTV 90d",
          width: 190,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_path_report.avg_fifth_plus_order_ltv_90_day",
          type: "currency",
          header: "Avg 5+ Order LTV 90d",
          width: 190,
          disabled: true,
        },
        // Pre-Marketing Contribution
        {
          key: "company_web_analytics_page_path_report.sum_first_order_pre_mktg_contribution",
          type: "currency",
          header: "1st Order Pre-Mktg",
          width: 170,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_path_report.sum_second_order_pre_mktg_contribution",
          type: "currency",
          header: "2nd Order Pre-Mktg",
          width: 180,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_path_report.sum_third_order_pre_mktg_contribution",
          type: "currency",
          header: "3rd Order Pre-Mktg",
          width: 180,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_path_report.sum_fourth_order_pre_mktg_contribution",
          type: "currency",
          header: "4th Order Pre-Mktg",
          width: 180,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_path_report.sum_fifth_plus_order_pre_mktg_contribution",
          type: "currency",
          header: "5+ Order Pre-Mktg",
          width: 180,
          disabled: true,
        },
        // Pre-Marketing Contribution 90 Day
        {
          key: "company_web_analytics_page_path_report.sum_first_order_pre_mktg_contribution_90_day",
          type: "currency",
          header: "1st Order Pre-Mktg 90d",
          width: 200,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_path_report.sum_second_order_pre_mktg_contribution_90_day",
          type: "currency",
          header: "2nd Order Pre-Mktg 90d",
          width: 200,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_path_report.sum_third_order_pre_mktg_contribution_90_day",
          type: "currency",
          header: "3rd Order Pre-Mktg 90d",
          width: 200,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_path_report.sum_fourth_order_pre_mktg_contribution_90_day",
          type: "currency",
          header: "4th Order Pre-Mktg 90d",
          width: 200,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_path_report.sum_fifth_plus_order_pre_mktg_contribution_90_day",
          type: "currency",
          header: "5+ Order Pre-Mktg 90d",
          width: 200,
          disabled: true,
        },
        // Pre-Marketing Contribution 180 Day
        {
          key: "company_web_analytics_page_path_report.sum_first_order_pre_mktg_contribution_180_day",
          type: "currency",
          header: "1st Order Pre-Mktg 180d",
          width: 210,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_path_report.sum_second_order_pre_mktg_contribution_180_day",
          type: "currency",
          header: "2nd Order Pre-Mktg 180d",
          width: 210,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_path_report.sum_third_order_pre_mktg_contribution_180_day",
          type: "currency",
          header: "3rd Order Pre-Mktg 180d",
          width: 210,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_path_report.sum_fourth_order_pre_mktg_contribution_180_day",
          type: "currency",
          header: "4th Order Pre-Mktg 180d",
          width: 210,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_path_report.sum_fifth_plus_order_pre_mktg_contribution_180_day",
          type: "currency",
          header: "5+ Order Pre-Mktg 180d",
          width: 210,
          disabled: true,
        },
        // Pre-Marketing Contribution 365 Day
        {
          key: "company_web_analytics_page_path_report.sum_first_order_pre_mktg_contribution_365_day",
          type: "currency",
          header: "1st Order Pre-Mktg 365d",
          width: 210,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_path_report.sum_second_order_pre_mktg_contribution_365_day",
          type: "currency",
          header: "2nd Order Pre-Mktg 365d",
          width: 210,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_path_report.sum_third_order_pre_mktg_contribution_365_day",
          type: "currency",
          header: "3rd Order Pre-Mktg 365d",
          width: 210,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_path_report.sum_fourth_order_pre_mktg_contribution_365_day",
          type: "currency",
          header: "4th Order Pre-Mktg 365d",
          width: 210,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_path_report.sum_fifth_plus_order_pre_mktg_contribution_365_day",
          type: "currency",
          header: "5+ Order Pre-Mktg 365d",
          width: 210,
          disabled: true,
        },
      ],
      additionalFilters: [],
      totalLabelColumn: "handle",
      compareUniqueKey: "handle",
      enablePagination: true,
      disableGranularity: true,
      enableCSVExport: true,
      csvExportFilename: "page_path_report",
    },
    metadata: { defaultColSpan: 12, defaultRowSpan: 12 },
  },
  company_web_analytics_landing_page_report_chart: {
    id: "company_web_analytics_landing_page_report_chart",
    name: "Landing Page Sessions Over Time",
    description:
      "Time series chart showing sessions by landing page type, add to carts, and purchases",
    type: "ComposedChart",
    props: {
      title: "Landing Page Sessions Over Time",
      comparisonEnabled: true,
      graphs: [
        {
          type: "area",
          label: "Sessions",
          dataKey: "company_web_analytics_landing_page_report.sessions",
          color: "#10B981",
          unit: "session",
          pluralizeUnit: true,
        },
        {
          type: "area",
          label: "Add to Carts",
          dataKey: "company_web_analytics_landing_page_report.add_to_carts",
          color: "#3B82F6",
          unit: "cart",
          pluralizeUnit: true,
        },
        {
          type: "area",
          label: "First Orders",
          dataKey: "company_web_analytics_landing_page_report.first_orders",
          color: "#EF4444",
          unit: "order",
          pluralizeUnit: true,
        },
        {
          type: "area",
          label: "Repeat Orders",
          dataKey: "company_web_analytics_landing_page_report.repeat_orders",
          color: "#EAB308",
          unit: "order",
          pluralizeUnit: true,
        },
      ],
      hideTotal: true,
      bottomLegend: true,
    },
    metadata: { defaultColSpan: 12, defaultRowSpan: 8 },
  },
  company_web_analytics_landing_page_report_table: {
    id: "company_web_analytics_landing_page_report_table",
    name: "Landing Page Report",
    description: "Analytics data for landing pages",
    type: "TableVisualization",
    props: {
      title: "Landing Page Report",
      // Qualifies bare additionalFilters values (e.g. "session_medium") into
      // fully-qualified Cube members so the filter passes Cube's query
      // validation. Column filters are already fully-qualified via columnConfig
      // keys; additionalFilters are not, so they rely on this prefix.
      queryPrefix: "company_web_analytics_landing_page_report",
      additionalFilters: [
        {
          value: "session_medium",
          label: "Session Medium",
          type: "searchable-select",
        },
      ],
      columnConfig: [
        {
          key: "company_web_analytics_landing_page_report.handle",
          type: "title",
          header: "Handle",
          width: 400,
        },
        {
          key: "company_web_analytics_landing_page_report.sessions",
          type: "number",
          header: "Sessions",
          width: 130,
        },
        {
          key: "company_web_analytics_landing_page_report.key_events",
          type: "number",
          header: "Key Events",
          width: 130,
        },
        {
          key: "company_web_analytics_landing_page_report.checkouts",
          type: "number",
          header: "Checkouts",
          width: 130,
        },
        {
          key: "company_web_analytics_landing_page_report.add_to_carts",
          type: "number",
          header: "Add to Carts",
          width: 170,
        },
        {
          key: "company_web_analytics_landing_page_report.scrolled_users",
          type: "number",
          header: "Scrolled Users",
          width: 130,
        },
        {
          key: "company_web_analytics_landing_page_report.bounces",
          type: "number",
          header: "Bounces",
          width: 130,
        },
        {
          key: "company_web_analytics_landing_page_report.bounce_rate",
          type: "percent",
          header: "Bounce Rate",
          width: 120,
        },
        {
          key: "company_web_analytics_landing_page_report.conversion_rate",
          type: "percent",
          header: "Conversion Rate",
          width: 150,
        },
        {
          key: "company_web_analytics_landing_page_report.checkout_rate",
          type: "percent",
          header: "Checkout Rate",
          width: 140,
        },
        {
          key: "company_web_analytics_landing_page_report.add_to_cart_rate",
          type: "percent",
          header: "Add to Cart Rate",
          width: 150,
        },
        {
          key: "company_web_analytics_landing_page_report.scroll_rate",
          type: "percent",
          header: "Scroll Rate",
          width: 120,
        },
        {
          key: "company_web_analytics_landing_page_report.total_revenue",
          type: "currency",
          header: "Total Revenue",
          width: 140,
        },
        {
          key: "company_web_analytics_landing_page_report.revenue_per_session",
          type: "currency",
          header: "Revenue/Session",
          width: 150,
        },
        {
          key: "company_web_analytics_landing_page_report.first_orders",
          type: "number",
          header: "First Orders",
          width: 120,
          disabled: true,
        },
        {
          key: "company_web_analytics_landing_page_report.repeat_orders",
          type: "number",
          header: "Repeat Orders",
          width: 130,
          disabled: true,
        },
        {
          key: "company_web_analytics_landing_page_report.sum_first_order_revs",
          type: "currency",
          header: "1st Order Rev",
          width: 140,
          disabled: true,
        },
        {
          key: "company_web_analytics_landing_page_report.sum_second_order_revs",
          type: "currency",
          header: "2nd Order Rev",
          width: 140,
          disabled: true,
        },
        {
          key: "company_web_analytics_landing_page_report.sum_third_order_revs",
          type: "currency",
          header: "3rd Order Rev",
          width: 140,
          disabled: true,
        },
        {
          key: "company_web_analytics_landing_page_report.sum_fourth_order_revs",
          type: "currency",
          header: "4th Order Rev",
          width: 140,
          disabled: true,
        },
        {
          key: "company_web_analytics_landing_page_report.sum_fifth_plus_order_revs",
          type: "currency",
          header: "5+ Order Rev",
          width: 140,
          disabled: true,
        },
        {
          key: "company_web_analytics_landing_page_report.avg_first_order_ltv_90_day",
          type: "currency",
          header: "1st Order LTV 90d",
          width: 160,
          disabled: true,
        },
        {
          key: "company_web_analytics_landing_page_report.avg_second_order_ltv_90_day",
          type: "currency",
          header: "2nd Order LTV 90d",
          width: 160,
          disabled: true,
        },
        {
          key: "company_web_analytics_landing_page_report.avg_third_order_ltv_90_day",
          type: "currency",
          header: "3rd Order LTV 90d",
          width: 160,
          disabled: true,
        },
        {
          key: "company_web_analytics_landing_page_report.avg_fourth_order_ltv_90_day",
          type: "currency",
          header: "4th Order LTV 90d",
          width: 160,
          disabled: true,
        },
        {
          key: "company_web_analytics_landing_page_report.avg_fifth_plus_order_ltv_90_day",
          type: "currency",
          header: "5+ Order LTV 90d",
          width: 160,
          disabled: true,
        },
        {
          key: "company_web_analytics_landing_page_report.sum_first_order_pre_mktg_contribution",
          type: "currency",
          header: "1st Order Pre-Mktg",
          width: 180,
          disabled: true,
        },
        {
          key: "company_web_analytics_landing_page_report.sum_second_order_pre_mktg_contribution",
          type: "currency",
          header: "2nd Order Pre-Mktg",
          width: 180,
          disabled: true,
        },
        {
          key: "company_web_analytics_landing_page_report.sum_third_order_pre_mktg_contribution",
          type: "currency",
          header: "3rd Order Pre-Mktg",
          width: 180,
          disabled: true,
        },
        {
          key: "company_web_analytics_landing_page_report.sum_fourth_order_pre_mktg_contribution",
          type: "currency",
          header: "4th Order Pre-Mktg",
          width: 180,
          disabled: true,
        },
        {
          key: "company_web_analytics_landing_page_report.sum_fifth_plus_order_pre_mktg_contribution",
          type: "currency",
          header: "5+ Order Pre-Mktg",
          width: 180,
          disabled: true,
        },
      ],
      totalLabelColumn: "handle",
      compareUniqueKey: "handle",
      enablePagination: true,
      disableGranularity: true,
      enableCSVExport: true,
      csvExportFilename: "landing_page_report",
    },
    metadata: { defaultColSpan: 12, defaultRowSpan: 12 },
  },

  // Mentions Analysis Charts and Tables
  mentions_analysis_time_series_chart: {
    id: "mentions_analysis_time_series_chart",
    name: "Mentions Analysis Time Series",
    description:
      "Time series analysis of brand mentions showing post counts, sentiment, and engagement metrics over time.",
    type: "ComposedChart",
    props: {
      hideTotal: true,
      comparisonEnabled: true,
      isInverted: true,
      hideTooltipTitle: true,
      bottomLegend: true,
      groups: [
        {
          label: "Engagement",
          value: "engagement",
        },
        {
          label: "Sentiment",
          value: "sentiment",
        },
      ],
      graphs: [
        {
          type: "area",
          label: "Post Count",
          dataKey: "company_ai_mentions_metrics.post_count",
          group: "engagement",
          yAxisId: "eng",
        },
        {
          type: "area",
          label: "Avg Sentiment",
          dataKey: "company_ai_mentions_metrics.avg_sentiment",
          group: "sentiment",
          yAxisId: "sentiment",
        },
        {
          type: "area",
          label: "Total Likes",
          dataKey: "company_ai_mentions_metrics.total_likes",
          group: "engagement",
          yAxisId: "eng",
        },
        {
          type: "area",
          label: "Total Comments",
          dataKey: "company_ai_mentions_metrics.total_comments",
          group: "engagement",
          yAxisId: "eng",
        },
        {
          type: "area",
          label: "Total Views",
          dataKey: "company_ai_mentions_metrics.total_views",
          group: "engagement",
          yAxisId: "eng",
        },
        {
          type: "area",
          label: "Avg Likes",
          dataKey: "company_ai_mentions_metrics.avg_likes",
          group: "engagement",
          yAxisId: "eng",
        },
        {
          type: "area",
          label: "Avg Comments",
          dataKey: "company_ai_mentions_metrics.avg_comments",
          group: "engagement",
          yAxisId: "eng",
        },
        {
          type: "area",
          label: "Avg Views",
          dataKey: "company_ai_mentions_metrics.avg_views",
          group: "engagement",
          yAxisId: "eng",
        },
      ],
    },
    metadata: { defaultColSpan: 12, defaultRowSpan: 8 },
    requiredSources: AI_MENTIONS_SOURCES,
  },

  mentions_sentiment_by_theme_chart: {
    id: "mentions_sentiment_by_theme_chart",
    name: "Sentiment by Theme",
    description: "Average sentiment score broken down by mention theme.",
    type: "BarChart",
    props: {
      layout: "vertical",
      dataKey: "company_ai_mentions_metrics.dimension_value",
      valueKey: "company_ai_mentions_metrics.dim_avg_sentiment",
      labelKey: "company_ai_mentions_metrics.dimension_value",
    },
    metadata: { defaultColSpan: 4, defaultRowSpan: 4 },
    requiredSources: AI_MENTIONS_SOURCES,
  },

  mentions_post_count_by_theme_chart: {
    id: "mentions_post_count_by_theme_chart",
    name: "Post Count by Theme",
    description: "Number of posts broken down by mention theme.",
    type: "BarChart",
    props: {
      layout: "vertical",
      dataKey: "company_ai_mentions_metrics.dimension_value",
      valueKey: "company_ai_mentions_metrics.post_count",
      labelKey: "company_ai_mentions_metrics.dimension_value",
    },
    metadata: { defaultColSpan: 4, defaultRowSpan: 4 },
    requiredSources: AI_MENTIONS_SOURCES,
  },

  mentions_by_theme_table: {
    id: "mentions_by_theme_table",
    name: "Mentions by Theme",
    description: "Brand mentions aggregated by theme.",
    type: "TableVisualization",
    props: {
      comparisonEnabled: true,
      totalLabelColumn: "company_ai_mentions_metrics.dimension_value",
      queryPrefix: "company_ai_mentions_metrics",
      additionalFilters: [
        {
          value: "platform",
          label: "Platform",
        },
        {
          value: "sponsored",
          label: "Sponsored",
        },
        {
          value: "is_target_brand_mentioned",
          label: "Target Brand",
        },
      ],
      columnConfig: [
        {
          key: "company_ai_mentions_metrics.dimension_value",
          type: "title",
          width: 250,
          header: "Theme",
          blurInDemo: true,
        },
        {
          key: "company_ai_mentions_metrics.post_count",
          type: "number",
          header: "Post Count",
        },
        {
          key: "company_ai_mentions_metrics.dim_avg_sentiment",
          type: "decimal",
          header: "Avg Sentiment",
        },
        {
          key: "company_ai_mentions_metrics.dim_total_likes",
          type: "number",
          header: "Total Likes",
        },
        {
          key: "company_ai_mentions_metrics.dim_total_comments",
          type: "number",
          header: "Total Comments",
        },
        {
          key: "company_ai_mentions_metrics.dim_total_views",
          type: "number",
          header: "Total Views",
        },
        {
          key: "company_ai_mentions_metrics.dim_avg_likes",
          type: "decimal",
          header: "Avg Likes",
        },
        {
          key: "company_ai_mentions_metrics.dim_avg_comments",
          type: "decimal",
          header: "Avg Comments",
        },
        {
          key: "company_ai_mentions_metrics.dim_avg_views",
          type: "decimal",
          header: "Avg Views",
        },
      ],
      enablePagination: true,
      enableCSVExport: true,
      csvExportFilename: "mentions_by_theme",
    },
    metadata: { defaultColSpan: 12, defaultRowSpan: 8 },
    requiredSources: AI_MENTIONS_SOURCES,
  },

  mentions_by_metatheme_table: {
    id: "mentions_by_metatheme_table",
    name: "Mentions by Sub-theme",
    description: "Brand mentions aggregated by sub-theme (metatheme).",
    type: "TableVisualization",
    props: {
      comparisonEnabled: true,
      totalLabelColumn: "company_ai_mentions_metrics.dimension_value",
      queryPrefix: "company_ai_mentions_metrics",
      additionalFilters: [
        {
          value: "platform",
          label: "Platform",
        },
        {
          value: "sponsored",
          label: "Sponsored",
        },
        {
          value: "is_target_brand_mentioned",
          label: "Target Brand",
        },
      ],
      columnConfig: [
        {
          key: "company_ai_mentions_metrics.dimension_value",
          type: "title",
          width: 250,
          header: "Sub-theme",
          blurInDemo: true,
        },
        {
          key: "company_ai_mentions_metrics.post_count",
          type: "number",
          header: "Post Count",
        },
        {
          key: "company_ai_mentions_metrics.dim_avg_sentiment",
          type: "decimal",
          header: "Avg Sentiment",
        },
        {
          key: "company_ai_mentions_metrics.dim_total_likes",
          type: "number",
          header: "Total Likes",
        },
        {
          key: "company_ai_mentions_metrics.dim_total_comments",
          type: "number",
          header: "Total Comments",
        },
        {
          key: "company_ai_mentions_metrics.dim_total_views",
          type: "number",
          header: "Total Views",
        },
        {
          key: "company_ai_mentions_metrics.dim_avg_likes",
          type: "decimal",
          header: "Avg Likes",
        },
        {
          key: "company_ai_mentions_metrics.dim_avg_comments",
          type: "decimal",
          header: "Avg Comments",
        },
        {
          key: "company_ai_mentions_metrics.dim_avg_views",
          type: "decimal",
          header: "Avg Views",
        },
      ],
      enablePagination: true,
      enableCSVExport: true,
      csvExportFilename: "mentions_by_metatheme",
    },
    metadata: { defaultColSpan: 12, defaultRowSpan: 8 },
    requiredSources: AI_MENTIONS_SOURCES,
  },

  mentions_by_buying_occasion_table: {
    id: "mentions_by_buying_occasion_table",
    name: "Mentions by Buying Occasion",
    description: "Brand mentions aggregated by buying occasion.",
    type: "TableVisualization",
    props: {
      comparisonEnabled: true,
      totalLabelColumn: "company_ai_mentions_metrics.dimension_value",
      queryPrefix: "company_ai_mentions_metrics",
      additionalFilters: [
        {
          value: "platform",
          label: "Platform",
        },
        {
          value: "sponsored",
          label: "Sponsored",
        },
        {
          value: "is_target_brand_mentioned",
          label: "Target Brand",
        },
      ],
      columnConfig: [
        {
          key: "company_ai_mentions_metrics.dimension_value",
          type: "title",
          width: 250,
          header: "Buying Occasion",
          blurInDemo: true,
        },
        {
          key: "company_ai_mentions_metrics.post_count",
          type: "number",
          header: "Post Count",
        },
        {
          key: "company_ai_mentions_metrics.dim_avg_sentiment",
          type: "decimal",
          header: "Avg Sentiment",
        },
        {
          key: "company_ai_mentions_metrics.dim_total_likes",
          type: "number",
          header: "Total Likes",
        },
        {
          key: "company_ai_mentions_metrics.dim_total_comments",
          type: "number",
          header: "Total Comments",
        },
        {
          key: "company_ai_mentions_metrics.dim_total_views",
          type: "number",
          header: "Total Views",
        },
        {
          key: "company_ai_mentions_metrics.dim_avg_likes",
          type: "decimal",
          header: "Avg Likes",
        },
        {
          key: "company_ai_mentions_metrics.dim_avg_comments",
          type: "decimal",
          header: "Avg Comments",
        },
        {
          key: "company_ai_mentions_metrics.dim_avg_views",
          type: "decimal",
          header: "Avg Views",
        },
      ],
      enablePagination: true,
      enableCSVExport: true,
      csvExportFilename: "mentions_by_buying_occasion",
    },
    metadata: { defaultColSpan: 12, defaultRowSpan: 8 },
    requiredSources: AI_MENTIONS_SOURCES,
  },

  mentions_by_category_table: {
    id: "mentions_by_category_table",
    name: "Mentions by Category",
    description: "Brand mentions aggregated by associated category.",
    type: "TableVisualization",
    props: {
      comparisonEnabled: true,
      totalLabelColumn: "company_ai_mentions_metrics.dimension_value",
      queryPrefix: "company_ai_mentions_metrics",
      additionalFilters: [
        {
          value: "platform",
          label: "Platform",
        },
        {
          value: "sponsored",
          label: "Sponsored",
        },
        {
          value: "is_target_brand_mentioned",
          label: "Target Brand",
        },
      ],
      columnConfig: [
        {
          key: "company_ai_mentions_metrics.dimension_value",
          type: "title",
          width: 250,
          header: "Category",
          blurInDemo: true,
        },
        {
          key: "company_ai_mentions_metrics.post_count",
          type: "number",
          header: "Post Count",
        },
        {
          key: "company_ai_mentions_metrics.dim_avg_sentiment",
          type: "decimal",
          header: "Avg Sentiment",
        },
        {
          key: "company_ai_mentions_metrics.dim_total_likes",
          type: "number",
          header: "Total Likes",
        },
        {
          key: "company_ai_mentions_metrics.dim_total_comments",
          type: "number",
          header: "Total Comments",
        },
        {
          key: "company_ai_mentions_metrics.dim_total_views",
          type: "number",
          header: "Total Views",
        },
        {
          key: "company_ai_mentions_metrics.dim_avg_likes",
          type: "decimal",
          header: "Avg Likes",
        },
        {
          key: "company_ai_mentions_metrics.dim_avg_comments",
          type: "decimal",
          header: "Avg Comments",
        },
        {
          key: "company_ai_mentions_metrics.dim_avg_views",
          type: "decimal",
          header: "Avg Views",
        },
      ],
      enablePagination: true,
      enableCSVExport: true,
      csvExportFilename: "mentions_by_category",
    },
    metadata: { defaultColSpan: 12, defaultRowSpan: 8 },
    requiredSources: AI_MENTIONS_SOURCES,
  },
  product_page_funnel_table: {
    id: "product_page_funnel_table",
    name: "Product Page Funnel Table",
    description: "Product-level conversion funnel metrics",
    type: "TableVisualization",
    props: {
      queryPrefix: "company_web_analytics_product_page_funnel",
      sortBy: "product_viewers",
      compareUniqueKey: "product_handle",
      enablePagination: true,
      disableGranularity: true,
      columnConfig: [
        {
          key: "product_handle",
          type: "text",
          header: "Handle",
          disabled: true,
        },
        {
          key: "product_title",
          type: "text",
          header: "Product",
          width: 300,
          render: (row: TMetric) => {
            const title = row.product_title as string | undefined
            const handle = row.product_handle as string | undefined
            const hasValidTitle = title && title !== "Unnamed Product"
            return (
              <div className="max-w-xs">
                <div className="font-medium text-white truncate">
                  {hasValidTitle ? title : handle}
                </div>
                {hasValidTitle && (
                  <div className="text-xs text-white/50 truncate">{handle}</div>
                )}
              </div>
            )
          },
        },
        {
          key: "product_viewers",
          type: "number",
          header: "Product Views",
          width: 140,
          render: (row: TMetric) => (
            <div className="text-right font-medium">
              {formatNumber(Number(row.product_viewers ?? 0), { decimals: 0 })}
            </div>
          ),
        },
        {
          key: "product_to_cart_conversion",
          type: "number",
          header: "Add to Cart Rate (Abandon)",
          width: 200,
          disableSorting: true,
          render: (row: TMetric) => (
            <div className="text-right">
              <div className="text-green-bright font-semibold">
                {String(row.product_to_cart_conversion ?? 0)} %
              </div>
              <div className="text-error text-xs">
                ({String(row.product_to_cart_abandon ?? 0)} % abandon)
              </div>
            </div>
          ),
        },
        {
          key: "adders_to_cart",
          type: "number",
          header: "Add to Cart",
          width: 120,
          render: (row: TMetric) => (
            <div className="text-right font-medium">
              {formatNumber(Number(row.adders_to_cart ?? 0), { decimals: 0 })}
            </div>
          ),
        },
        {
          key: "cart_to_checkout_conversion",
          type: "number",
          header: "Checkout Rate (Abandon)",
          width: 200,
          disableSorting: true,
          render: (row: TMetric) => (
            <div className="text-right">
              <div className="text-green-bright font-semibold">
                {String(row.cart_to_checkout_conversion ?? 0)} %
              </div>
              <div className="text-error text-xs">
                ({String(row.cart_to_checkout_abandon ?? 0)} % abandon)
              </div>
            </div>
          ),
        },
        {
          key: "checkouts",
          type: "number",
          header: "Checkouts",
          width: 120,
          render: (row: TMetric) => (
            <div className="text-right font-medium">
              {formatNumber(Number(row.checkouts ?? 0), { decimals: 0 })}
            </div>
          ),
        },
        {
          key: "checkout_to_purchase_conversion",
          type: "number",
          header: "Purchase Rate (Abandon)",
          width: 200,
          disableSorting: true,
          render: (row: TMetric) => (
            <div className="text-right">
              <div className="text-green-bright font-semibold">
                {String(row.checkout_to_purchase_conversion ?? 0)} %
              </div>
              <div className="text-error text-xs">
                ({String(row.checkout_to_purchase_abandon ?? 0)} % abandon)
              </div>
            </div>
          ),
        },
        {
          key: "purchasers",
          type: "number",
          header: "Purchases",
          width: 120,
          render: (row: TMetric) => (
            <div className="text-right font-medium">
              {formatNumber(Number(row.purchasers ?? 0), { decimals: 0 })}
            </div>
          ),
        },
        {
          key: "product_to_purchase_conversion",
          type: "number",
          header: "Overall Conversion (Abandon)",
          width: 220,
          disableSorting: true,
          render: (row: TMetric) => (
            <div className="text-right">
              <div className="text-green-bright font-semibold">
                {String(row.product_to_purchase_conversion ?? 0)} %
              </div>
              <div className="text-error text-xs">
                ({String(row.product_to_purchase_abandon ?? 0)} % abandon)
              </div>
            </div>
          ),
        },
      ],
    },
    metadata: { defaultColSpan: 12, defaultRowSpan: 12 },
  },
  collection_funnel_table: {
    id: "collection_funnel_table",
    name: "Collection Funnel Table",
    description: "Collection-level conversion funnel metrics",
    type: "TableVisualization",
    props: {
      queryPrefix: "company_web_analytics_collection_funnel",
      sortBy: "collection_viewers",
      compareUniqueKey: "collection_handle",
      enablePagination: true,
      disableGranularity: true,
      columnConfig: [
        {
          key: "collection_handle",
          type: "text",
          header: "Handle",
          disabled: true,
        },
        {
          key: "collection_title",
          type: "text",
          header: "Collection",
          width: 300,
          render: (row: TMetric) => {
            const title = row.collection_title as string | undefined
            const handle = row.collection_handle as string | undefined
            const hasValidTitle = title && title !== "Unnamed Collection"
            return (
              <div className="max-w-xs">
                <div className="font-medium text-white truncate">
                  {hasValidTitle ? title : handle}
                </div>
                {hasValidTitle && (
                  <div className="text-xs text-white/50 truncate">{handle}</div>
                )}
              </div>
            )
          },
        },
        {
          key: "collection_viewers",
          type: "number",
          header: "Collection Views",
          width: 160,
          render: (row: TMetric) => (
            <div className="text-right font-medium">
              {formatNumber(Number(row.collection_viewers ?? 0), {
                decimals: 0,
              })}
            </div>
          ),
        },
        {
          key: "collection_to_cart_conversion",
          type: "number",
          header: "Add to Cart Conversion Rate (Abandon Rate)",
          width: 250,
          disableSorting: true,
          render: (row: TMetric) => (
            <div className="text-right">
              <div className="text-green-bright font-semibold">
                {String(row?.collection_to_cart_conversion ?? 0)} %
              </div>
              <div className="text-error text-xs">
                ({String(row?.collection_to_cart_abandon ?? 0)} % abandon)
              </div>
            </div>
          ),
        },
        {
          key: "adders_to_cart",
          type: "number",
          header: "Add to Cart",
          width: 160,
          render: (row: TMetric) => (
            <div className="text-right font-medium">
              {formatNumber(Number(row.adders_to_cart ?? 0), { decimals: 0 })}
            </div>
          ),
        },
        {
          key: "cart_to_purchase_conversion",
          type: "number",
          header: "Cart → Purchase Conversion Rate (Abandon Rate)",
          width: 250,
          disableSorting: true,
          render: (row: TMetric) => (
            <div className="text-right">
              <div className="text-green-bright font-semibold">
                {String(row.cart_to_purchase_conversion ?? 0)} %
              </div>
              <div className="text-error text-xs">
                ({String(row.cart_to_purchase_abandon ?? 0)} % abandon)
              </div>
            </div>
          ),
        },
        {
          key: "purchasers",
          type: "number",
          header: "Purchases",
          width: 160,
          render: (row: TMetric) => (
            <div className="text-right font-medium">
              {formatNumber(Number(row.purchasers ?? 0), { decimals: 0 })}
            </div>
          ),
        },
        {
          key: "collection_to_purchase_conversion",
          type: "number",
          header: "Collection → Purchase Conversion Rate (Abandon Rate)",
          width: 280,
          disableSorting: true,
          render: (row: TMetric) => (
            <div className="text-right">
              <div className="text-green-bright font-semibold">
                {String(row.collection_to_purchase_conversion ?? 0)} %
              </div>
              <div className="text-error text-xs">
                ({String(row.collection_to_purchase_abandon ?? 0)} % abandon)
              </div>
            </div>
          ),
        },
      ],
    },
    metadata: { defaultColSpan: 12, defaultRowSpan: 12 },
  },
  company_web_analytics_pre_atc_page_paths_table: {
    id: "company_web_analytics_pre_atc_page_paths_table",
    name: "Pre ATC Analysis - Page Paths",
    description: "Pre add-to-cart analysis by page path",
    type: "TableVisualization",
    props: {
      title: "Pre ATC Analysis - Page Paths",
      columnConfig: [
        {
          key: "company_web_analytics_page_value_pre_add_to_cart_page_paths.pre_add_to_cart_page_path",
          type: "text",
          header: "Page Path",
          width: 420,
          blurInDemo: true,
        },
        {
          key: "company_web_analytics_page_value_pre_add_to_cart_page_paths.session_count",
          type: "number",
          header: "Sessions",
          width: 130,
        },
        {
          key: "company_web_analytics_page_value_pre_add_to_cart_page_paths.user_count",
          type: "number",
          header: "Users",
          width: 120,
        },
        {
          key: "company_web_analytics_page_value_pre_add_to_cart_page_paths.avg_page_count",
          type: "number",
          header: "Avg Pages",
          width: 120,
        },
        {
          key: "company_web_analytics_page_value_pre_add_to_cart_page_paths.adds_to_cart",
          type: "number",
          header: "Adds to Cart",
          width: 130,
        },
        {
          key: "company_web_analytics_page_value_pre_add_to_cart_page_paths.checkout_starts",
          type: "number",
          header: "Checkout Starts",
          width: 140,
        },
        {
          key: "company_web_analytics_page_value_pre_add_to_cart_page_paths.checkout_completes",
          type: "number",
          header: "Checkouts",
          width: 130,
        },
        {
          key: "company_web_analytics_page_value_pre_add_to_cart_page_paths.purchases",
          type: "number",
          header: "Purchases",
          width: 120,
        },
        {
          key: "company_web_analytics_page_value_pre_add_to_cart_page_paths.purchase_value",
          type: "currency",
          header: "Purchase Value",
          width: 140,
        },
        {
          key: "company_web_analytics_page_value_pre_add_to_cart_page_paths.current_revenue",
          type: "currency",
          header: "Revenue",
          width: 130,
        },
        {
          key: "company_web_analytics_page_value_pre_add_to_cart_page_paths.gross_revenue",
          type: "currency",
          header: "Gross Revenue",
          width: 140,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_value_pre_add_to_cart_page_paths.gross_profit",
          type: "currency",
          header: "Gross Profit",
          width: 130,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_value_pre_add_to_cart_page_paths.new_customer_revenue",
          type: "currency",
          header: "New Customer Rev",
          width: 160,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_value_pre_add_to_cart_page_paths.repeat_customer_revenue",
          type: "currency",
          header: "Repeat Customer Rev",
          width: 170,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_value_pre_add_to_cart_page_paths.new_customer_gross_profit",
          type: "currency",
          header: "New Customer GP",
          width: 160,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_value_pre_add_to_cart_page_paths.repeat_customer_gross_profit",
          type: "currency",
          header: "Repeat Customer GP",
          width: 170,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_value_pre_add_to_cart_page_paths.prediction",
          type: "currency",
          header: "Predicted LTV",
          width: 140,
          disabled: true,
        },
      ],
      totalLabelColumn: "pre_add_to_cart_page_path",
      compareUniqueKey: "pre_add_to_cart_page_path",
      enablePagination: true,
      disableGranularity: true,
      enableCSVExport: true,
      csvExportFilename: "pre_atc_page_paths",
    },
    metadata: { defaultColSpan: 12, defaultRowSpan: 12 },
  },
  company_web_analytics_pre_atc_collections_table: {
    id: "company_web_analytics_pre_atc_collections_table",
    name: "Pre ATC Analysis - Collections",
    description: "Pre add-to-cart analysis by collection",
    type: "TableVisualization",
    props: {
      title: "Pre ATC Analysis - Collections",
      columnConfig: [
        {
          key: "company_web_analytics_page_value_pre_add_to_cart_collections.pre_add_to_cart_collection",
          type: "text",
          header: "Collection",
          width: 420,
          blurInDemo: true,
        },
        {
          key: "company_web_analytics_page_value_pre_add_to_cart_collections.session_count",
          type: "number",
          header: "Sessions",
          width: 130,
        },
        {
          key: "company_web_analytics_page_value_pre_add_to_cart_collections.user_count",
          type: "number",
          header: "Users",
          width: 120,
        },
        {
          key: "company_web_analytics_page_value_pre_add_to_cart_collections.avg_page_count",
          type: "number",
          header: "Avg Pages",
          width: 120,
        },
        {
          key: "company_web_analytics_page_value_pre_add_to_cart_collections.adds_to_cart",
          type: "number",
          header: "Adds to Cart",
          width: 130,
        },
        {
          key: "company_web_analytics_page_value_pre_add_to_cart_collections.checkout_starts",
          type: "number",
          header: "Checkout Starts",
          width: 140,
        },
        {
          key: "company_web_analytics_page_value_pre_add_to_cart_collections.checkout_completes",
          type: "number",
          header: "Checkouts",
          width: 130,
        },
        {
          key: "company_web_analytics_page_value_pre_add_to_cart_collections.purchases",
          type: "number",
          header: "Purchases",
          width: 120,
        },
        {
          key: "company_web_analytics_page_value_pre_add_to_cart_collections.purchase_value",
          type: "currency",
          header: "Purchase Value",
          width: 140,
        },
        {
          key: "company_web_analytics_page_value_pre_add_to_cart_collections.current_revenue",
          type: "currency",
          header: "Revenue",
          width: 130,
        },
        {
          key: "company_web_analytics_page_value_pre_add_to_cart_collections.gross_revenue",
          type: "currency",
          header: "Gross Revenue",
          width: 140,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_value_pre_add_to_cart_collections.gross_profit",
          type: "currency",
          header: "Gross Profit",
          width: 130,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_value_pre_add_to_cart_collections.new_customer_revenue",
          type: "currency",
          header: "New Customer Rev",
          width: 160,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_value_pre_add_to_cart_collections.repeat_customer_revenue",
          type: "currency",
          header: "Repeat Customer Rev",
          width: 170,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_value_pre_add_to_cart_collections.new_customer_gross_profit",
          type: "currency",
          header: "New Customer GP",
          width: 160,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_value_pre_add_to_cart_collections.repeat_customer_gross_profit",
          type: "currency",
          header: "Repeat Customer GP",
          width: 170,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_value_pre_add_to_cart_collections.prediction",
          type: "currency",
          header: "Predicted LTV",
          width: 140,
          disabled: true,
        },
      ],
      totalLabelColumn: "pre_add_to_cart_collection",
      compareUniqueKey: "pre_add_to_cart_collection",
      enablePagination: true,
      disableGranularity: true,
      enableCSVExport: true,
      csvExportFilename: "pre_atc_collections",
    },
    metadata: { defaultColSpan: 12, defaultRowSpan: 12 },
  },
  company_web_analytics_pre_atc_products_table: {
    id: "company_web_analytics_pre_atc_products_table",
    name: "Pre ATC Analysis - Products",
    description: "Pre add-to-cart analysis by product",
    type: "TableVisualization",
    props: {
      title: "Pre ATC Analysis - Products",
      columnConfig: [
        {
          key: "company_web_analytics_page_value_pre_add_to_cart_products.pre_add_to_cart_product",
          type: "text",
          header: "Product",
          width: 420,
          blurInDemo: true,
        },
        {
          key: "company_web_analytics_page_value_pre_add_to_cart_products.session_count",
          type: "number",
          header: "Sessions",
          width: 130,
        },
        {
          key: "company_web_analytics_page_value_pre_add_to_cart_products.user_count",
          type: "number",
          header: "Users",
          width: 120,
        },
        {
          key: "company_web_analytics_page_value_pre_add_to_cart_products.avg_page_count",
          type: "number",
          header: "Avg Pages",
          width: 120,
        },
        {
          key: "company_web_analytics_page_value_pre_add_to_cart_products.adds_to_cart",
          type: "number",
          header: "Adds to Cart",
          width: 130,
        },
        {
          key: "company_web_analytics_page_value_pre_add_to_cart_products.checkout_starts",
          type: "number",
          header: "Checkout Starts",
          width: 140,
        },
        {
          key: "company_web_analytics_page_value_pre_add_to_cart_products.checkout_completes",
          type: "number",
          header: "Checkouts",
          width: 130,
        },
        {
          key: "company_web_analytics_page_value_pre_add_to_cart_products.purchases",
          type: "number",
          header: "Purchases",
          width: 120,
        },
        {
          key: "company_web_analytics_page_value_pre_add_to_cart_products.purchase_value",
          type: "currency",
          header: "Purchase Value",
          width: 140,
        },
        {
          key: "company_web_analytics_page_value_pre_add_to_cart_products.current_revenue",
          type: "currency",
          header: "Revenue",
          width: 130,
        },
        {
          key: "company_web_analytics_page_value_pre_add_to_cart_products.gross_revenue",
          type: "currency",
          header: "Gross Revenue",
          width: 140,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_value_pre_add_to_cart_products.gross_profit",
          type: "currency",
          header: "Gross Profit",
          width: 130,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_value_pre_add_to_cart_products.new_customer_revenue",
          type: "currency",
          header: "New Customer Rev",
          width: 160,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_value_pre_add_to_cart_products.repeat_customer_revenue",
          type: "currency",
          header: "Repeat Customer Rev",
          width: 170,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_value_pre_add_to_cart_products.new_customer_gross_profit",
          type: "currency",
          header: "New Customer GP",
          width: 160,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_value_pre_add_to_cart_products.repeat_customer_gross_profit",
          type: "currency",
          header: "Repeat Customer GP",
          width: 170,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_value_pre_add_to_cart_products.prediction",
          type: "currency",
          header: "Predicted LTV",
          width: 140,
          disabled: true,
        },
      ],
      totalLabelColumn: "pre_add_to_cart_product",
      compareUniqueKey: "pre_add_to_cart_product",
      enablePagination: true,
      disableGranularity: true,
      enableCSVExport: true,
      csvExportFilename: "pre_atc_products",
    },
    metadata: { defaultColSpan: 12, defaultRowSpan: 12 },
  },
  company_web_analytics_page_depth_first_pageview_table: {
    id: "company_web_analytics_page_depth_first_pageview_table",
    name: "Page Depth - Landing Page",
    description: "Page depth analysis for first pageview (landing page)",
    type: "TableVisualization",
    props: {
      title: "Page Depth - Landing Page",
      columnConfig: [
        {
          key: "company_web_analytics_page_value_first_pageview.first_pageview_page_path",
          type: "text",
          header: "Landing Page",
          width: 420,
          blurInDemo: true,
        },
        {
          key: "company_web_analytics_page_value_first_pageview.session_count",
          type: "number",
          header: "Sessions",
          width: 130,
        },
        {
          key: "company_web_analytics_page_value_first_pageview.user_count",
          type: "number",
          header: "Users",
          width: 120,
        },
        {
          key: "company_web_analytics_page_value_first_pageview.avg_page_count",
          type: "number",
          header: "Avg Pages",
          width: 120,
        },
        {
          key: "company_web_analytics_page_value_first_pageview.adds_to_cart",
          type: "number",
          header: "Adds to Cart",
          width: 130,
        },
        {
          key: "company_web_analytics_page_value_first_pageview.checkout_starts",
          type: "number",
          header: "Checkout Starts",
          width: 140,
        },
        {
          key: "company_web_analytics_page_value_first_pageview.checkout_completes",
          type: "number",
          header: "Checkouts",
          width: 130,
        },
        {
          key: "company_web_analytics_page_value_first_pageview.purchases",
          type: "number",
          header: "Purchases",
          width: 120,
        },
        {
          key: "company_web_analytics_page_value_first_pageview.purchase_value",
          type: "currency",
          header: "Purchase Value",
          width: 140,
        },
        {
          key: "company_web_analytics_page_value_first_pageview.current_revenue",
          type: "currency",
          header: "Revenue",
          width: 130,
        },
        {
          key: "company_web_analytics_page_value_first_pageview.gross_revenue",
          type: "currency",
          header: "Gross Revenue",
          width: 140,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_value_first_pageview.gross_profit",
          type: "currency",
          header: "Gross Profit",
          width: 130,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_value_first_pageview.new_customer_revenue",
          type: "currency",
          header: "New Customer Rev",
          width: 160,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_value_first_pageview.repeat_customer_revenue",
          type: "currency",
          header: "Repeat Customer Rev",
          width: 170,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_value_first_pageview.new_customer_gross_profit",
          type: "currency",
          header: "New Customer GP",
          width: 160,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_value_first_pageview.repeat_customer_gross_profit",
          type: "currency",
          header: "Repeat Customer GP",
          width: 170,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_value_first_pageview.prediction",
          type: "currency",
          header: "Predicted LTV",
          width: 140,
          disabled: true,
        },
      ],
      totalLabelColumn: "first_pageview_page_path",
      compareUniqueKey: "first_pageview_page_path",
      enablePagination: true,
      disableGranularity: true,
      enableCSVExport: true,
      csvExportFilename: "page_depth_landing_page",
    },
    metadata: { defaultColSpan: 12, defaultRowSpan: 12 },
  },
  company_web_analytics_page_depth_second_pageview_table: {
    id: "company_web_analytics_page_depth_second_pageview_table",
    name: "Page Depth - 2nd Page",
    description: "Page depth analysis for second pageview",
    type: "TableVisualization",
    props: {
      title: "Page Depth - 2nd Page",
      columnConfig: [
        {
          key: "company_web_analytics_page_value_second_pageview.second_pageview_page_path",
          type: "text",
          header: "2nd Page",
          width: 420,
          blurInDemo: true,
        },
        {
          key: "company_web_analytics_page_value_second_pageview.session_count",
          type: "number",
          header: "Sessions",
          width: 130,
        },
        {
          key: "company_web_analytics_page_value_second_pageview.user_count",
          type: "number",
          header: "Users",
          width: 120,
        },
        {
          key: "company_web_analytics_page_value_second_pageview.avg_page_count",
          type: "number",
          header: "Avg Pages",
          width: 120,
        },
        {
          key: "company_web_analytics_page_value_second_pageview.adds_to_cart",
          type: "number",
          header: "Adds to Cart",
          width: 130,
        },
        {
          key: "company_web_analytics_page_value_second_pageview.checkout_starts",
          type: "number",
          header: "Checkout Starts",
          width: 140,
        },
        {
          key: "company_web_analytics_page_value_second_pageview.checkout_completes",
          type: "number",
          header: "Checkouts",
          width: 130,
        },
        {
          key: "company_web_analytics_page_value_second_pageview.purchases",
          type: "number",
          header: "Purchases",
          width: 120,
        },
        {
          key: "company_web_analytics_page_value_second_pageview.purchase_value",
          type: "currency",
          header: "Purchase Value",
          width: 140,
        },
        {
          key: "company_web_analytics_page_value_second_pageview.current_revenue",
          type: "currency",
          header: "Revenue",
          width: 130,
        },
        {
          key: "company_web_analytics_page_value_second_pageview.gross_revenue",
          type: "currency",
          header: "Gross Revenue",
          width: 140,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_value_second_pageview.gross_profit",
          type: "currency",
          header: "Gross Profit",
          width: 130,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_value_second_pageview.new_customer_revenue",
          type: "currency",
          header: "New Customer Rev",
          width: 160,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_value_second_pageview.repeat_customer_revenue",
          type: "currency",
          header: "Repeat Customer Rev",
          width: 170,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_value_second_pageview.new_customer_gross_profit",
          type: "currency",
          header: "New Customer GP",
          width: 160,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_value_second_pageview.repeat_customer_gross_profit",
          type: "currency",
          header: "Repeat Customer GP",
          width: 170,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_value_second_pageview.prediction",
          type: "currency",
          header: "Predicted LTV",
          width: 140,
          disabled: true,
        },
      ],
      totalLabelColumn: "second_pageview_page_path",
      compareUniqueKey: "second_pageview_page_path",
      enablePagination: true,
      disableGranularity: true,
      enableCSVExport: true,
      csvExportFilename: "page_depth_2nd_page",
    },
    metadata: { defaultColSpan: 12, defaultRowSpan: 12 },
  },
  company_web_analytics_page_depth_third_pageview_table: {
    id: "company_web_analytics_page_depth_third_pageview_table",
    name: "Page Depth - 3rd Page",
    description: "Page depth analysis for third pageview",
    type: "TableVisualization",
    props: {
      title: "Page Depth - 3rd Page",
      columnConfig: [
        {
          key: "company_web_analytics_page_value_third_pageview.third_pageview_page_path",
          type: "text",
          header: "3rd Page",
          width: 420,
          blurInDemo: true,
        },
        {
          key: "company_web_analytics_page_value_third_pageview.session_count",
          type: "number",
          header: "Sessions",
          width: 130,
        },
        {
          key: "company_web_analytics_page_value_third_pageview.user_count",
          type: "number",
          header: "Users",
          width: 120,
        },
        {
          key: "company_web_analytics_page_value_third_pageview.avg_page_count",
          type: "number",
          header: "Avg Pages",
          width: 120,
        },
        {
          key: "company_web_analytics_page_value_third_pageview.adds_to_cart",
          type: "number",
          header: "Adds to Cart",
          width: 130,
        },
        {
          key: "company_web_analytics_page_value_third_pageview.checkout_starts",
          type: "number",
          header: "Checkout Starts",
          width: 140,
        },
        {
          key: "company_web_analytics_page_value_third_pageview.checkout_completes",
          type: "number",
          header: "Checkouts",
          width: 130,
        },
        {
          key: "company_web_analytics_page_value_third_pageview.purchases",
          type: "number",
          header: "Purchases",
          width: 120,
        },
        {
          key: "company_web_analytics_page_value_third_pageview.purchase_value",
          type: "currency",
          header: "Purchase Value",
          width: 140,
        },
        {
          key: "company_web_analytics_page_value_third_pageview.current_revenue",
          type: "currency",
          header: "Revenue",
          width: 130,
        },
        {
          key: "company_web_analytics_page_value_third_pageview.gross_revenue",
          type: "currency",
          header: "Gross Revenue",
          width: 140,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_value_third_pageview.gross_profit",
          type: "currency",
          header: "Gross Profit",
          width: 130,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_value_third_pageview.new_customer_revenue",
          type: "currency",
          header: "New Customer Rev",
          width: 160,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_value_third_pageview.repeat_customer_revenue",
          type: "currency",
          header: "Repeat Customer Rev",
          width: 170,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_value_third_pageview.new_customer_gross_profit",
          type: "currency",
          header: "New Customer GP",
          width: 160,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_value_third_pageview.repeat_customer_gross_profit",
          type: "currency",
          header: "Repeat Customer GP",
          width: 170,
          disabled: true,
        },
        {
          key: "company_web_analytics_page_value_third_pageview.prediction",
          type: "currency",
          header: "Predicted LTV",
          width: 140,
          disabled: true,
        },
      ],
      totalLabelColumn: "third_pageview_page_path",
      compareUniqueKey: "third_pageview_page_path",
      enablePagination: true,
      disableGranularity: true,
      enableCSVExport: true,
      csvExportFilename: "page_depth_3rd_page",
    },
    metadata: { defaultColSpan: 12, defaultRowSpan: 12 },
  },
  company_shopify_inventory_sku_sellouts_table: {
    id: "company_shopify_inventory_sku_sellouts_table",
    name: "SKU Sell-Outs",
    description: "SKU-level sell-out and restock events with sales data",
    type: "TableVisualization",
    props: {
      title: "SKU Sell-Outs",
      columnConfig: [
        {
          key: "company_shopify_inventory_events.product_title",
          type: "text",
          header: "Product",
          width: 250,
        },
        {
          key: "company_shopify_inventory_events.sku",
          type: "text",
          header: "SKU",
          width: 160,
        },
        {
          key: "company_shopify_inventory_events.variant_title",
          type: "text",
          header: "Variant",
          width: 160,
        },
        {
          // Shopify inventory location id. Shown so the per-location row
          // repetition (same SKU at multiple locations) is self-explanatory.
          // "text" avoids compact number formatting (e.g. 62771167319 -> 62.8B).
          // Column filtering is disabled because a "contains" filter on the id
          // triggers a slow string scan; use the Primary Location dropdown
          // (exact-match equals filter) on the page instead.
          key: "company_shopify_inventory_events.location_id",
          type: "text",
          header: "Location ID",
          width: 130,
          disableFiltering: true,
        },
        {
          key: "company_shopify_inventory_events.event_type",
          type: "text",
          header: "Event Type",
          width: 120,
        },
        {
          key: "company_shopify_inventory_events.event_date",
          type: "date",
          header: "Event Date",
          width: 120,
        },
        {
          key: "company_shopify_inventory_events.is_currently_sold_out",
          type: "boolean",
          header: "Currently Sold Out",
          width: 150,
        },
        {
          key: "company_shopify_inventory_events.current_available",
          type: "number",
          header: "Current Qty",
          width: 120,
        },
        {
          key: "company_shopify_inventory_events.available_before_event",
          type: "number",
          header: "Qty Before",
          width: 110,
          disabled: true,
        },
        {
          key: "company_shopify_inventory_events.available_after_event",
          type: "number",
          header: "Qty After",
          width: 110,
          disabled: true,
        },
        {
          key: "company_shopify_variant_daily_sales.units_sold",
          type: "number",
          header: "Units Sold",
          width: 120,
        },
        {
          key: "company_shopify_variant_daily_sales.revenue",
          type: "currency",
          header: "Revenue",
          width: 130,
        },
        {
          key: "company_shopify_inventory_events.sellout_count",
          type: "number",
          header: "Sell-Outs",
          width: 110,
          disabled: true,
        },
        {
          key: "company_shopify_inventory_events.restock_count",
          type: "number",
          header: "Restocks",
          width: 110,
          disabled: true,
        },
        {
          key: "company_shopify_inventory_events.variant_id",
          type: "number",
          header: "Variant ID",
          width: 110,
          disabled: true,
        },
        {
          key: "company_shopify_inventory_events.product_id",
          type: "number",
          header: "Product ID",
          width: 110,
          disabled: true,
        },
      ],
      totalLabelColumn: "sku",
      compareUniqueKey: "sku",
      enablePagination: true,
      disableGranularity: true,
      enableCSVExport: true,
      csvExportFilename: "inv_sku_sellouts",
    },
    metadata: { defaultColSpan: 12, defaultRowSpan: 12 },
  },
  company_shopify_inventory_product_breakage_table: {
    id: "company_shopify_inventory_product_breakage_table",
    name: "Product Breakage",
    description: "Product-level inventory breakage analysis",
    type: "TableVisualization",
    props: {
      title: "Product Breakage",
      columnConfig: [
        {
          key: "company_shopify_product_variant_breakage.product_title",
          type: "text",
          header: "Product",
          width: 280,
        },
        {
          key: "company_shopify_product_variant_breakage.broken_variant_count",
          type: "number",
          header: "Broken Variants",
          width: 140,
        },
        {
          key: "company_shopify_product_variant_breakage.total_variants",
          type: "number",
          header: "Total Variants",
          width: 130,
        },
        {
          key: "company_shopify_product_variant_breakage.breakage_rate",
          type: "percent",
          header: "Breakage Rate",
          width: 130,
        },
        {
          key: "company_shopify_product_variant_breakage.inventory_health_status",
          type: "text",
          header: "Health Status",
          width: 140,
        },
        {
          key: "company_shopify_product_variant_breakage.has_unresolved_breaks",
          type: "boolean",
          header: "Unresolved Breaks",
          width: 150,
        },
        {
          key: "company_shopify_product_variant_breakage.latest_sellout_date",
          type: "date",
          header: "Latest Sell-Out",
          width: 130,
        },
        {
          key: "company_shopify_product_variant_breakage.latest_restock_date",
          type: "date",
          header: "Latest Restock",
          width: 130,
        },
        {
          key: "company_shopify_product_variant_breakage.total_sellout_events",
          type: "number",
          header: "Sell-Out Events",
          width: 130,
        },
        {
          key: "company_shopify_product_variant_breakage.total_restock_events",
          type: "number",
          header: "Restock Events",
          width: 130,
        },
        {
          key: "company_shopify_product_variant_breakage.tracked_variant_count",
          type: "number",
          header: "Tracked Variants",
          width: 140,
          disabled: true,
        },
        {
          key: "company_shopify_product_variant_breakage.product_id",
          type: "number",
          header: "Product ID",
          width: 110,
          disabled: true,
        },
      ],
      totalLabelColumn: "product_title",
      compareUniqueKey: "product_title",
      enablePagination: true,
      disableGranularity: true,
      enableCSVExport: true,
      csvExportFilename: "inv_product_breakage",
    },
    metadata: { defaultColSpan: 12, defaultRowSpan: 12 },
  },
  shopify_orders_economics_chart: {
    id: "shopify_orders_economics_chart",
    name: "Order Economics",
    description:
      "Order economics per order date. Revenue is the original (pre-refund) order value; Net Revenue is after realized refunds and order edits; Gross Profit is original Revenue less COGS, before refunds; Contribution is Gross Profit less realized refunds and assumed shipping and card-fee costs.",
    type: "ComposedChart",
    props: {
      title: "Order Economics",
      comparisonEnabled: true,
      totalLabelProps: {
        label: "Total Revenue",
        numberType: "currency",
      },
      graphs: [
        {
          type: "area",
          label: "Revenue",
          dataKey: "company_shopify_orders.revenue",
          numberType: "currency",
          labelAggType: "sum",
          pluralizeUnit: false,
          color: "#318936",
        },
        {
          type: "line",
          label: "Net Revenue",
          dataKey: "company_shopify_orders.net_revenue",
          numberType: "currency",
          labelAggType: "sum",
          pluralizeUnit: false,
          color: "#3366DD",
        },
        {
          type: "line",
          label: "Gross Profit",
          dataKey: "company_shopify_orders.gross_profit",
          numberType: "currency",
          labelAggType: "sum",
          pluralizeUnit: false,
          color: "#F4D148",
        },
        {
          type: "line",
          label: "Contribution",
          dataKey: "company_shopify_orders.net_pre_mktg_contribution",
          numberType: "currency",
          labelAggType: "sum",
          pluralizeUnit: false,
          color: "#EE8434",
        },
        {
          type: "line",
          label: "Discounts",
          dataKey: "company_shopify_orders.total_discounts",
          numberType: "currency",
          labelAggType: "sum",
          pluralizeUnit: false,
          color: "#E2323E",
        },
      ],
      graphsManualSort: true,
      interactive: false,
      compareNumberType: "currency",
      compareLabelAggType: "sum",
      filterableDataKey: true,
    },
    metadata: null,
    requiredSources: [SOURCE_IDS.SHOPIFY],
  },
  shopify_orders_by_channel_table: {
    id: "shopify_orders_by_channel_table",
    name: "Orders by Channel",
    description:
      "Order volume and unit economics broken down by Shopify sales channel over the selected period.",
    type: "TableVisualization",
    props: {
      title: "Orders by Channel",
      columnConfig: [
        {
          key: "company_shopify_orders.channel",
          type: "text",
          header: "Channel",
          width: 200,
        },
        {
          key: "company_shopify_orders.orders",
          type: "number",
          header: "Orders",
          width: 110,
        },
        {
          key: "company_shopify_orders.customers",
          type: "number",
          header: "Customers",
          width: 120,
        },
        {
          key: "company_shopify_orders.revenue",
          type: "currency",
          header: "Revenue",
          width: 130,
        },
        {
          key: "company_shopify_orders.net_revenue",
          type: "currency",
          header: "Net Revenue",
          width: 130,
        },
        {
          key: "company_shopify_orders.aov",
          type: "currency",
          header: "AOV",
          width: 110,
        },
        {
          key: "company_shopify_orders.total_discounts",
          type: "currency",
          header: "Discounts",
          width: 130,
        },
        {
          key: "company_shopify_orders.discount_rate",
          type: "percent",
          header: "Discount Rate",
          width: 130,
        },
        {
          key: "company_shopify_orders.gross_profit",
          type: "currency",
          header: "Gross Profit",
          width: 130,
        },
        {
          key: "company_shopify_orders.gross_margin_percent",
          type: "percent",
          header: "Gross Margin",
          width: 130,
        },
        {
          key: "company_shopify_orders.net_pre_mktg_contribution",
          type: "currency",
          header: "Contribution",
          width: 140,
        },
        {
          key: "company_shopify_orders.ltv_90_day_revenue",
          type: "currency",
          header: "90d Follow-on Net Revenue",
          width: 170,
          disabled: true,
        },
      ],
      totalLabelColumn: "company_shopify_orders.channel",
      compareUniqueKey: "company_shopify_orders.channel",
      enablePagination: false,
      disableGranularity: true,
      enableCSVExport: true,
      csvExportFilename: "shopify_orders_by_channel",
    },
    metadata: { defaultColSpan: 12, defaultRowSpan: 12 },
    requiredSources: [SOURCE_IDS.SHOPIFY],
  },
}
export type VISUALIZATIONS_MAP_KEY = keyof typeof VISUALIZATIONS_MAP
